/* eslint-disable */
import { useState, useCallback, useRef, useEffect } from 'react';
import Battle from './Battle';
import { AuthModal, UserMenu } from './Auth';
import { CardPhotoSearch } from './components/CardPhotoSearch';
import { supabase, signOut, getSession, onAuthStateChange, fetchDecks, saveDeck, deleteDeckRemote } from './supabaseClient';

// ─── THEME ────────────────────────────────────────────────────────────────────
function useTheme() {
  var [theme, setTheme] = useState(function() {
    return localStorage.getItem('pm_theme') || 'dark';
  });
  useEffect(function() {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('pm_theme', theme);
  }, [theme]);
  function toggle() { setTheme(function(t) { return t === 'dark' ? 'light' : 'dark'; }); }
  return [theme, toggle];
}

// ─── WINDOW WIDTH HOOK ───────────────────────────────────────────────────────
function useWindowWidth() {
  var [width, setWidth] = useState(window.innerWidth);
  useEffect(function() {
    function handler() { setWidth(window.innerWidth); }
    window.addEventListener('resize', handler);
    return function() { window.removeEventListener('resize', handler); };
  }, []);
  return width;
}

// ─── ASSET HELPERS ────────────────────────────────────────────────────────────
// Type SVG badge — points to /assets/ in public folder
function TypeBadge({ type, size }) {
  var v = TYPE_VAR[type] || 'normal';
  return (
    <img
      src={'/assets/type-' + v + '.svg'}
      alt={type}
      width={size || 16}
      height={size || 16}
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    />
  );
}

// Mascot blob for empty states
function MascotEmpty({ variant, title, body }) {
  var v = variant || 'a';
  return (
    <div style={{ textAlign: 'center', padding: 'var(--sp-8) var(--sp-5)' }}>
      <img src={'/assets/mascot-blob-' + v + '.svg'} alt="" style={{ width: 120, opacity: 0.9, margin: '0 auto var(--sp-4)' }} />
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-20)', color: 'var(--c-fg-1)', marginBottom: 6 }}>{title || 'Nothing here yet'}</div>
      {body && <div style={{ color: 'var(--c-fg-3)', fontSize: 'var(--fs-14)', maxWidth: 280, margin: '0 auto' }}>{body}</div>}
    </div>
  );
}

// Wordmark — force white so it reads on the dark frosted nav in both themes
function Wordmark() {
  return (
    <img
      src="/assets/wordmark.svg"
      alt="Pokemania"
      style={{ height: 18, display: 'block', filter: 'brightness(0) invert(1)', opacity: 0.92 }}
    />
  );
}

// Monogram — use the light (white-on-dark) variant so it pops on the nav
function Monogram({ size }) {
  return (
    <img
      src="/assets/monogram-light.svg"
      alt="P"
      style={{ width: size || 28, height: size || 28, display: 'block' }}
    />
  );
}

// Dot grid background element
function DotGrid() {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
      backgroundImage: 'url(/assets/bg-dotgrid.svg)',
      backgroundRepeat: 'repeat', opacity: 0.04,
    }} />
  );
}

// ─── DESIGN SYSTEM HELPERS ────────────────────────────────────────────────────
// Maps TCG card types to Pokemania CSS variables
var TYPE_VAR = {
  Fire:'fire', Water:'water', Grass:'grass', Lightning:'electric',
  Psychic:'psychic', Fighting:'fighting', Darkness:'dark', Metal:'steel',
  Dragon:'dragon', Fairy:'fairy', Colorless:'colorless', Normal:'normal',
  Ice:'ice', Poison:'poison', Ground:'ground', Flying:'flying',
  Bug:'bug', Rock:'rock', Ghost:'ghost',
};
function typeColor(type) {
  var v = TYPE_VAR[type] || 'normal';
  return 'var(--c-'+v+')';
}
function typeBg(type) {
  var v = TYPE_VAR[type] || 'normal';
  return 'var(--c-'+v+'-bg)';
}
function typeInk(type) {
  var v = TYPE_VAR[type] || 'normal';
  return 'var(--c-'+v+'-ink)';
}

// ─── FORMAT LEGALITY ─────────────────────────────────────────────────────────
// Regulation marks per set (source: Bulbapedia + Pokemon.com official rotation)
// Standard 2026: H, I, J legal (G rotated out April 10 2026)
// Expanded: Black & White onward (set era: bw, dp, hgss, pl, ex, base = NOT expanded legal)
var SET_REG_MARKS = {
  // Mega Evolution era (J mark)
  'me4':'J','me3':'J','me2':'J',
  // Scarlet & Violet — I mark (sv7 onward = Stellar Crown+)
  'sv9':'I','sv8pt5':'I','sv8':'I','sv7':'I',
  // Scarlet & Violet — H mark (sv6pt5 = Shrouded Fable through sv5 = Temporal Forces)
  'sv6pt5':'H','sv6':'H','sv5':'H','sv4pt5':'H','sv4':'H','sv3pt5':'H','sv3':'H',
  // Scarlet & Violet — G mark (sv1–sv2, rotated out April 10 2026)
  'sv2':'G','sv1':'G',
  // Sword & Shield — F mark (all rotated out of Standard, still Expanded legal)
  'swsh12pt5':'F','swsh12':'F','swsh11':'F','swsh9':'F','swsh8':'F',
  'swsh7':'F','swsh6':'F','swsh5':'F','swsh4':'F','swsh3':'F','swsh2':'F','swsh1':'F',
  // Sun & Moon — E mark
  'sm12':'E','sm11':'E','sm10':'E','sm9':'E','sm8':'E','sm6':'E',
  'sm5':'E','sm4':'E','sm3':'E','sm2':'E','sm1':'E',
  // XY — D mark
  'xy12':'D','xy11':'D','xy9':'D','xy8':'D','xy6':'D','xy5':'D','xy4':'D','xy2':'D','xy1':'D',
  // BW — C mark
  'bw11':'C','bw10':'C','bw9':'C','bw8':'C','bw7':'C','bw6':'C',
  'bw5':'C','bw4':'C','bw3':'C','bw2':'C','bw1':'C',
  // HGSS, PL, DP, EX, Base — no regulation marks (pre-system, Unlimited only)
};

// Which era IDs are Expanded legal (BW onward)
var EXPANDED_ERAS = new Set(['bw','xy','sm','swsh','sv','me']);

// Format definitions
var FORMATS = {
  unlimited: {
    label:'Unlimited', color:'var(--c-normal)',
    desc:'All cards from every era are legal.',
    isLegal: function(setId, era) { return true; },
  },
  expanded: {
    label:'Expanded', color:'var(--c-dragon)',
    desc:'Black & White onward. No set rotation.',
    isLegal: function(setId, era) { return EXPANDED_ERAS.has(era); },
  },
  standard: {
    label:'Standard', color:'var(--c-grass)',
    desc:'H, I, J regulation marks only. Rotated April 10, 2026.',
    isLegal: function(setId, era) {
      var mark = SET_REG_MARKS[setId];
      return mark === 'H' || mark === 'I' || mark === 'J';
    },
  },
};

function getCardFormat(card) {
  // Determine legality from card's setName or setId
  var setId = card.set && card.set.id;
  var era = card.set && card.set.era;
  // Try to find era from our SETS array by set name
  if (!setId && card.setName) {
    var found = null;
    // Will be resolved at runtime via SET lookup
    return { standard: false, expanded: false, unlimited: true };
  }
  var mark = SET_REG_MARKS[setId] || null;
  var isExpanded = era ? EXPANDED_ERAS.has(era) : false;
  // Basic Energy is always legal
  if (card.supertype === 'Energy' && card.subtypes && card.subtypes.includes('Basic')) {
    return { standard: true, expanded: true, unlimited: true };
  }
  return {
    standard: mark === 'H' || mark === 'I' || mark === 'J',
    expanded: isExpanded || (mark && 'CDEFGHIJ'.includes(mark)),
    unlimited: true,
  };
}

// ─── WORLD CHAMPIONSHIP DECKS ─────────────────────────────────────────────────
var WORLDS_DECKS = [
  {
    id:'worlds-2004-queendom',era:'wotc',year:2004,name:"Queendom (2004)",player:'Jermy Magram',
    placement:'2004 US Nationals Runner-Up',archetype:'Vileplume / Nidoqueen',
    description:'Classic stall deck using Vileplume to lock Trainers and Nidoqueen for damage.',
    cards:[
      {name:'Nidoran ♀',qty:4,set:'Expedition Base Set'},{name:'Nidorina',qty:3,set:'Expedition Base Set'},
      {name:'Nidoqueen',qty:3,set:'Expedition Base Set'},{name:'Oddish',qty:4,set:'Aquapolis'},
      {name:'Gloom',qty:3,set:'Aquapolis'},{name:'Vileplume',qty:3,set:'Aquapolis'},
      {name:'Cleffa',qty:2,set:'Neo Genesis'},{name:'Professor Elm',qty:4,set:'Neo Genesis'},
      {name:'Pokemon Nurse',qty:3,set:'Expedition Base Set'},{name:'Gold Berry',qty:4,set:'Neo Genesis'},
      {name:'Switch',qty:3,set:'Base Set'},{name:'Pokemon Reversal',qty:2,set:'Expedition Base Set'},
      {name:'Grass Energy',qty:14,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2003-blaziken',era:'wotc',year:2003,name:"Blaziken ex Beatdown (2003)",player:'Jason Klaczynski',
    placement:'2003 US Nationals Champion',archetype:'Blaziken ex',
    description:"Aggressive fire deck built around Blaziken ex's massive Blaze attack.",
    cards:[
      {name:'Torchic',qty:4,set:'EX Ruby & Sapphire'},{name:'Combusken',qty:2,set:'EX Ruby & Sapphire'},
      {name:'Blaziken',qty:3,set:'EX Ruby & Sapphire'},{name:'Blaziken ex',qty:2,set:'EX Ruby & Sapphire'},
      {name:'Dunsparce',qty:4,set:'EX Sandstorm'},{name:'Skitty',qty:3,set:'EX Sandstorm'},
      {name:'Delcatty',qty:2,set:'EX Sandstorm'},{name:'Rare Candy',qty:4,set:'EX Sandstorm'},
      {name:'Professor Birch',qty:4,set:'EX Ruby & Sapphire'},{name:'TV Reporter',qty:3,set:'EX Dragon'},
      {name:'Switch',qty:3,set:'Base Set'},{name:'Fire Energy',qty:12,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2002-haymaker',era:'wotc',year:2002,name:"Haymaker Classic (2002)",player:'Multiple finalists',
    placement:'Classic WotC Era Archetype',archetype:'Electabuzz / Hitmonchan / Scyther',
    description:'The original dominant deck — fast basics with high damage and energy efficiency.',
    cards:[
      {name:'Electabuzz',qty:4,set:'Base Set'},{name:'Hitmonchan',qty:4,set:'Base Set'},
      {name:'Scyther',qty:4,set:'Jungle'},{name:'Professor Oak',qty:4,set:'Base Set'},
      {name:'Bill',qty:4,set:'Base Set'},{name:'Computer Search',qty:3,set:'Base Set'},
      {name:'Gust of Wind',qty:3,set:'Base Set'},{name:'Energy Removal',qty:3,set:'Base Set'},
      {name:'PlusPower',qty:3,set:'Base Set'},{name:'Switch',qty:2,set:'Base Set'},
      {name:'Lightning Energy',qty:8,set:'Base Set'},{name:'Fighting Energy',qty:6,set:'Base Set'},
      {name:'Double Colorless Energy',qty:4,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2004-team-magma',era:'wotc',year:2004,name:"Team Magma's Rush (2004)",player:'World Championship Deck',
    placement:'2004 World Championship Release',archetype:"Team Magma's Groudon",
    description:"Team Magma deck using energy acceleration to power up Groudon ex quickly.",
    cards:[
      {name:"Team Magma's Groudon",qty:3,set:'EX Team Magma vs Team Aqua'},
      {name:"Team Magma's Camerupt",qty:3,set:'EX Team Magma vs Team Aqua'},
      {name:"Team Magma's Claydol",qty:2,set:'EX Team Magma vs Team Aqua'},
      {name:"Team Magma's Zangoose",qty:2,set:'EX Team Magma vs Team Aqua'},
      {name:"Team Magma's Aron",qty:3,set:'EX Team Magma vs Team Aqua'},
      {name:"Team Magma's Lairon",qty:2,set:'EX Team Magma vs Team Aqua'},
      {name:'Dunsparce',qty:3,set:'EX Sandstorm'},
      {name:'Team Magma Ball',qty:4,set:'EX Team Magma vs Team Aqua'},
      {name:'Team Magma Conspirator',qty:3,set:'EX Team Magma vs Team Aqua'},
      {name:'Rare Candy',qty:3,set:'EX Sandstorm'},{name:'TV Reporter',qty:3,set:'EX Dragon'},
      {name:'Fighting Energy',qty:8,set:'Base Set'},{name:'Fire Energy',qty:6,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2006-metanite',era:'ex',year:2006,name:"Metanite (2006 Worlds)",player:'Dylan Lefavour',
    placement:'2006 World Champion',archetype:'Metagross DX / Dragonite ex',
    description:'Dominant 2006 deck combining Metagross Delta Species with Dragonite ex for energy acceleration.',
    cards:[
      {name:'Beldum',qty:4,set:'EX Delta Species'},{name:'Metang',qty:2,set:'EX Delta Species'},
      {name:'Metagross',qty:3,set:'EX Delta Species'},{name:'Dragonite ex',qty:3,set:'EX Delta Species'},
      {name:'Dratini',qty:3,set:'EX Delta Species'},{name:'Eevee',qty:2,set:'EX Unseen Forces'},
      {name:'Castaway',qty:4,set:'EX Crystal Guardians'},{name:'Rare Candy',qty:4,set:'EX Sandstorm'},
      {name:'Holon Transceiver',qty:4,set:'EX Holon Phantoms'},{name:'Holon Mentor',qty:3,set:'EX Holon Phantoms'},
      {name:'Metal Energy',qty:4,set:'Base Set'},{name:'Lightning Energy',qty:4,set:'Base Set'},
      {name:'Water Energy',qty:3,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2007-mario',era:'ex',year:2007,name:"MARIO (2007 Worlds)",player:'Jason Klaczynski',
    placement:'2007 World Champion',archetype:'Lucario / Machamp',
    description:"Fast fighting aggro with Lucario's disruption and Machamp's massive damage.",
    cards:[
      {name:'Riolu',qty:4,set:'EX Power Keepers'},{name:'Lucario',qty:3,set:'EX Power Keepers'},
      {name:'Machop',qty:3,set:'EX Power Keepers'},{name:'Machamp',qty:3,set:'EX Power Keepers'},
      {name:'Rare Candy',qty:4,set:'EX Sandstorm'},{name:'Celio\'s Network',qty:3,set:'EX FireRed & LeafGreen'},
      {name:'Professor Rowan',qty:3,set:'Diamond & Pearl'},{name:'Castaway',qty:2,set:'EX Crystal Guardians'},
      {name:'Strength Charm',qty:2,set:'EX FireRed & LeafGreen'},{name:'Fighting Energy',qty:12,set:'Base Set'},
      {name:'Double Rainbow Energy',qty:3,set:'EX Team Rocket Returns'},
    ]
  },
  {
    id:'worlds-2008-plox',era:'dp',year:2008,name:"PLOX (2008 Worlds)",player:'Jason Klaczynski',
    placement:'2008 World Champion',archetype:'Gardevoir SW / Gallade',
    description:"The legendary PLOX deck — Gardevoir's Psy Shadow locks Poke-Powers while Gallade sweeps.",
    cards:[
      {name:'Ralts',qty:4,set:'EX Sandstorm'},{name:'Gardevoir',qty:2,set:'Secret Wonders'},
      {name:'Gallade',qty:2,set:'Secret Wonders'},{name:'Duskull',qty:2,set:'Diamond & Pearl'},
      {name:'Dusknoir',qty:2,set:'Diamond & Pearl'},{name:'Pachirisu',qty:2,set:'Great Encounters'},
      {name:'Rare Candy',qty:4,set:'EX Sandstorm'},{name:'Roseanne\'s Research',qty:3,set:'Secret Wonders'},
      {name:'Team Galactic\'s Wager',qty:3,set:'Mysterious Treasures'},
      {name:'Lake Boundary',qty:2,set:'Mysterious Treasures'},{name:'Psychic Energy',qty:6,set:'Base Set'},
      {name:'Fighting Energy',qty:4,set:'Base Set'},{name:'Scramble Energy',qty:2,set:'EX Deoxys'},
    ]
  },
  {
    id:'worlds-2010-luxchomp',era:'hgss',year:2010,name:"LuxChomp (2010 Worlds)",player:'Jason Klaczynski',
    placement:'2010 World Champion',archetype:'Luxray GL / Garchomp C',
    description:"The most dominant deck ever built. Luxray GL's Bright Look + Garchomp C's Dragon Rush.",
    cards:[
      {name:'Luxray GL',qty:2,set:'Rising Rivals'},{name:'Luxray GL Lv.X',qty:2,set:'Rising Rivals'},
      {name:'Garchomp C',qty:2,set:'Supreme Victors'},{name:'Garchomp C Lv.X',qty:1,set:'Supreme Victors'},
      {name:'Crobat G',qty:2,set:'Platinum'},{name:'Uxie',qty:2,set:'Legends Awakened'},
      {name:'Cyrus\'s Conspiracy',qty:4,set:'Platinum'},{name:'Poke Turn',qty:4,set:'Platinum'},
      {name:'Power Spray',qty:3,set:'Platinum'},{name:'Energy Gain',qty:4,set:'Platinum'},
      {name:'Pokemon Collector',qty:3,set:'HeartGold SoulSilver'},
      {name:'Lightning Energy',qty:4,set:'Base Set'},{name:'Psychic Energy',qty:3,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2012-darkrai',era:'bw',year:2012,name:"Darkrai EX / Smeargle (2012)",player:'Multiple Champions',
    placement:'2012 Worlds Dominant Archetype',archetype:'Darkrai EX / Mewtwo EX',
    description:'Night Spear snipes the bench while Smeargle copies supporter draws for insane speed.',
    cards:[
      {name:'Darkrai EX',qty:3,set:'Dark Explorers'},{name:'Mewtwo EX',qty:2,set:'Next Destinies'},
      {name:'Smeargle',qty:3,set:'Undaunted'},{name:'Professor Juniper',qty:4,set:'Black & White'},
      {name:'N',qty:4,set:'Noble Victories'},{name:'Dark Patch',qty:4,set:'Dark Explorers'},
      {name:'Pokemon Catcher',qty:4,set:'Emerging Powers'},{name:'Junk Arm',qty:4,set:'Triumphant'},
      {name:'Ultra Ball',qty:4,set:'Dark Explorers'},{name:'Darkness Energy',qty:8,set:'Base Set'},
      {name:'Double Colorless Energy',qty:4,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2013-virizion',era:'bw',year:2013,name:"Virizion EX / Genesect EX (2013)",player:'Jason Klaczynski',
    placement:'2013 World Champion',archetype:'Virizion EX / Genesect EX',
    description:'Verdant Wind blocks Special Conditions. Red Signal gusts with Megalo Cannon.',
    cards:[
      {name:'Virizion EX',qty:3,set:'Plasma Blast'},{name:'Genesect EX',qty:3,set:'Plasma Blast'},
      {name:'Professor Juniper',qty:4,set:'Black & White'},{name:'N',qty:3,set:'Noble Victories'},
      {name:'Colress',qty:2,set:'Plasma Storm'},{name:'Pokemon Catcher',qty:4,set:'Emerging Powers'},
      {name:'Ultra Ball',qty:4,set:'Dark Explorers'},{name:'Colress Machine',qty:4,set:'Plasma Storm'},
      {name:'Team Plasma Ball',qty:4,set:'Plasma Freeze'},{name:'Plasma Energy',qty:4,set:'Plasma Storm'},
      {name:'Grass Energy',qty:7,set:'Base Set'},{name:'Double Colorless Energy',qty:4,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2015-night-march',era:'xy',year:2015,name:"Night March (2015 Worlds)",player:'Enrique Avila',
    placement:'2015 Worlds Finalist',archetype:'Joltik / Pumpkaboo / Lampent',
    description:'Discard Night Marchers to deal 20 damage each — up to 200+ with a full discard.',
    cards:[
      {name:'Joltik',qty:4,set:'Phantom Forces'},{name:'Pumpkaboo',qty:4,set:'Phantom Forces'},
      {name:'Lampent',qty:4,set:'Phantom Forces'},{name:'Shaymin EX',qty:2,set:'Roaring Skies'},
      {name:'Professor Juniper',qty:4,set:'Black & White'},{name:'N',qty:2,set:'Noble Victories'},
      {name:'Lysandre',qty:2,set:'Flashfire'},{name:'Battle Compressor',qty:4,set:'Phantom Forces'},
      {name:'VS Seeker',qty:4,set:'Phantom Forces'},{name:'Ultra Ball',qty:4,set:'Dark Explorers'},
      {name:'Acro Bike',qty:4,set:'Primal Clash'},{name:'Double Colorless Energy',qty:4,set:'XY'},
    ]
  },
  {
    id:'worlds-2016-greninja',era:'xy',year:2016,name:"Greninja BREAK (2016 Worlds)",player:'Shintaro Ito',
    placement:'2016 World Champion',archetype:'Greninja BREAK',
    description:'Shadow Stitching locks Abilities while Giant Water Shuriken snipes the bench.',
    cards:[
      {name:'Froakie',qty:4,set:'BREAKpoint'},{name:'Frogadier',qty:4,set:'BREAKpoint'},
      {name:'Greninja',qty:3,set:'BREAKpoint'},{name:'Greninja BREAK',qty:2,set:'BREAKpoint'},
      {name:'Talonflame',qty:2,set:'Steam Siege'},{name:'Professor Sycamore',qty:4,set:'XY'},
      {name:'N',qty:3,set:'Noble Victories'},{name:'Lysandre',qty:2,set:'Flashfire'},
      {name:'VS Seeker',qty:4,set:'Phantom Forces'},{name:'Rare Candy',qty:4,set:'EX Sandstorm'},
      {name:'Ultra Ball',qty:4,set:'Dark Explorers'},{name:'Dive Ball',qty:4,set:'Primal Clash'},
      {name:'Water Energy',qty:12,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2018-buzzwole',era:'sm',year:2018,name:"Buzzwole GX / Lycanroc GX (2018)",player:'Robin Schulz',
    placement:'2018 World Champion',archetype:'Buzzwole GX / Lycanroc GX',
    description:"Bloodthirsty Eyes gusts while Buzzwole GX hits for massive damage with few energy.",
    cards:[
      {name:'Buzzwole GX',qty:3,set:'Crimson Invasion'},{name:'Lycanroc GX',qty:2,set:'Guardians Rising'},
      {name:'Rockruff',qty:3,set:'Guardians Rising'},{name:'Diancie Prism Star',qty:1,set:'Forbidden Light'},
      {name:'Professor Sycamore',qty:4,set:'XY'},{name:'N',qty:3,set:'Noble Victories'},
      {name:'Guzma',qty:3,set:'Burning Shadows'},{name:'Cynthia',qty:2,set:'Ultra Prism'},
      {name:'Ultra Ball',qty:4,set:'Dark Explorers'},{name:'Max Elixir',qty:4,set:'BREAKthrough'},
      {name:'Choice Band',qty:3,set:'Guardians Rising'},{name:'Strong Energy',qty:4,set:'Fates Collide'},
      {name:'Fighting Energy',qty:8,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2019-pikarom',era:'sm',year:2019,name:"PikaRom (2019 Worlds)",player:'Henry Brand',
    placement:'2019 Worlds Top 8',archetype:'Pikachu & Zekrom GX',
    description:'Full Blitz attaches energy to bench while Tag Bolt GX OHKOs anything.',
    cards:[
      {name:'Pikachu & Zekrom GX',qty:3,set:'Team Up'},{name:'Zeraora GX',qty:2,set:'Lost Thunder'},
      {name:'Dedenne GX',qty:2,set:'Unbroken Bonds'},{name:'Tapu Koko Prism Star',qty:1,set:'Team Up'},
      {name:'Volkner',qty:4,set:'Ultra Prism'},{name:'Cynthia',qty:3,set:'Ultra Prism'},
      {name:'Guzma',qty:2,set:'Burning Shadows'},{name:'Electromagnetic Radar',qty:4,set:'Unbroken Bonds'},
      {name:'Electropower',qty:4,set:'Lost Thunder'},{name:'Choice Band',qty:3,set:'Guardians Rising'},
      {name:'Thunder Mountain Prism',qty:2,set:'Lost Thunder'},{name:'Lightning Energy',qty:11,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2022-mew-vmax',era:'swsh',year:2022,name:"Mew VMAX (2022 Worlds)",player:'Andre Chiasson',
    placement:'2022 Worlds Top 4',archetype:'Mew VMAX / Genesect V',
    description:'Genesect V draws cards. Mew VMAX copies attacks for consistent massive damage.',
    cards:[
      {name:'Mew VMAX',qty:3,set:'Fusion Strike'},{name:'Mew V',qty:4,set:'Fusion Strike'},
      {name:'Genesect V',qty:4,set:'Fusion Strike'},{name:'Meloetta',qty:4,set:'Fusion Strike'},
      {name:'Boss\'s Orders',qty:3,set:'Rebel Clash'},{name:'Professor\'s Research',qty:4,set:'Sword & Shield'},
      {name:'Marnie',qty:2,set:'Sword & Shield'},{name:'Power Tablet',qty:4,set:'Fusion Strike'},
      {name:'Battle VIP Pass',qty:4,set:'Fusion Strike'},{name:'Ultra Ball',qty:4,set:'Dark Explorers'},
      {name:'Elesa\'s Sparkle',qty:4,set:'Fusion Strike'},{name:'Psychic Energy',qty:8,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2022-arceus',era:'swsh',year:2022,name:"Arceus VSTAR / Palkia VSTAR (2022)",player:'Ondrej Skubal',
    placement:'2022 World Champion',archetype:'Arceus VSTAR / Palkia VSTAR',
    description:"Arceus VSTAR's Trinity Nova charges Palkia VSTAR for Subspace Swell.",
    cards:[
      {name:'Arceus VSTAR',qty:2,set:'Brilliant Stars'},{name:'Arceus V',qty:3,set:'Brilliant Stars'},
      {name:'Palkia VSTAR',qty:2,set:'Astral Radiance'},{name:'Palkia V',qty:2,set:'Astral Radiance'},
      {name:'Inteleon',qty:2,set:'Chilling Reign'},{name:'Sobble',qty:3,set:'Chilling Reign'},
      {name:'Professor\'s Research',qty:4,set:'Sword & Shield'},{name:'Marnie',qty:3,set:'Sword & Shield'},
      {name:'Boss\'s Orders',qty:3,set:'Rebel Clash'},{name:'Melony',qty:3,set:'Chilling Reign'},
      {name:'Ultra Ball',qty:4,set:'Dark Explorers'},{name:'Water Energy',qty:10,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2024-charizard-ex',era:'sv',year:2024,name:"Charizard ex (2024 Worlds)",player:'Multiple Finalists',
    placement:'2024 Worlds Dominant Archetype',archetype:'Charizard ex',
    description:'Infernal Reign attaches energy, Burning Darkness OHKOs anything.',
    cards:[
      {name:'Charizard ex',qty:3,set:'Obsidian Flames'},{name:'Charmeleon',qty:1,set:'Obsidian Flames'},
      {name:'Charmander',qty:4,set:'Obsidian Flames'},{name:'Pidgeot ex',qty:2,set:'Obsidian Flames'},
      {name:'Pidgey',qty:2,set:'Obsidian Flames'},{name:'Professor\'s Research',qty:3,set:'Sword & Shield'},
      {name:'Iono',qty:4,set:'Paldea Evolved'},{name:'Boss\'s Orders',qty:3,set:'Rebel Clash'},
      {name:'Arven',qty:4,set:'Scarlet & Violet'},{name:'Rare Candy',qty:4,set:'EX Sandstorm'},
      {name:'Ultra Ball',qty:4,set:'Dark Explorers'},{name:'Nest Ball',qty:4,set:'Sun & Moon'},
      {name:'Fire Energy',qty:8,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2024-roaring-moon',era:'sv',year:2024,name:"Roaring Moon ex (2024 Worlds)",player:'Sakuya Ota',
    placement:'2024 World Champion',archetype:'Roaring Moon ex',
    description:'Frenzied Gouging discards all energy but hits for 220. Brutal aggro.',
    cards:[
      {name:'Roaring Moon ex',qty:3,set:'Paradox Rift'},{name:'Squawkabilly ex',qty:2,set:'Paldea Evolved'},
      {name:'Radiant Greninja',qty:1,set:'Astral Radiance'},{name:'Professor\'s Research',qty:3,set:'Sword & Shield'},
      {name:'Iono',qty:4,set:'Paldea Evolved'},{name:'Boss\'s Orders',qty:3,set:'Rebel Clash'},
      {name:'Arven',qty:3,set:'Scarlet & Violet'},{name:'Dark Patch',qty:4,set:'Dark Explorers'},
      {name:'Ultra Ball',qty:4,set:'Dark Explorers'},{name:'Earthen Vessel',qty:4,set:'Paradox Rift'},
      {name:'Jet Energy',qty:4,set:'Paldea Evolved'},{name:'Darkness Energy',qty:8,set:'Base Set'},
    ]
  },
  {
    id:'worlds-2023-gardevoir',era:'sv',year:2023,name:"Gardevoir ex (2023 Worlds)",player:'Tord Reklev',
    placement:'2023 Worlds Top 4',archetype:'Gardevoir ex',
    description:'Psychic Embrace attaches Psychic energy from discard. Scales with energy in play.',
    cards:[
      {name:'Gardevoir ex',qty:3,set:'Scarlet & Violet'},{name:'Kirlia',qty:3,set:'Scarlet & Violet'},
      {name:'Ralts',qty:4,set:'Scarlet & Violet'},{name:'Professor\'s Research',qty:3,set:'Sword & Shield'},
      {name:'Iono',qty:4,set:'Paldea Evolved'},{name:'Boss\'s Orders',qty:3,set:'Rebel Clash'},
      {name:'Arven',qty:3,set:'Scarlet & Violet'},{name:'Rare Candy',qty:4,set:'EX Sandstorm'},
      {name:'Ultra Ball',qty:4,set:'Dark Explorers'},{name:'Nest Ball',qty:3,set:'Sun & Moon'},
      {name:'Psychic Energy',qty:11,set:'Base Set'},
    ]
  },
];

var WORLDS_ERAS = [
  {id:'all', label:'All eras',color:'var(--c-electric)'},
  {id:'wotc',label:'WotC',    color:'var(--c-ground)'},
  {id:'ex',  label:'EX era',  color:'var(--c-fire)'},
  {id:'dp',  label:'D&P',     color:'var(--c-water)'},
  {id:'hgss',label:'HGSS',    color:'var(--c-grass)'},
  {id:'bw',  label:'B&W',     color:'var(--c-dragon)'},
  {id:'xy',  label:'XY',      color:'var(--c-fighting)'},
  {id:'sm',  label:'S&M',     color:'var(--c-psychic)'},
  {id:'swsh',label:'SwSh',    color:'var(--c-ice)'},
  {id:'sv',  label:'S&V',     color:'var(--c-electric)'},
];

var SETS = [
  {id:'me4',name:'Mega Evolution — Chaos Rising',  era:'me',year:2026,total:131},
  {id:'me3',name:'Mega Evolution — Perfect Order', era:'me',year:2026,total:124},
  {id:'me2',name:'Mega Evolution — Ascended Heroes',era:'me',year:2026,total:293},
  {id:'sv9',name:'Journey Together',era:'sv',year:2025,total:190},
  {id:'sv8pt5',name:'Prismatic Evolutions',era:'sv',year:2025,total:131},
  {id:'sv8',name:'Surging Sparks',era:'sv',year:2024,total:191},
  {id:'sv7',name:'Stellar Crown',era:'sv',year:2024,total:175},
  {id:'sv6pt5',name:'Shrouded Fable',era:'sv',year:2024,total:99},
  {id:'sv6',name:'Twilight Masquerade',era:'sv',year:2024,total:167},
  {id:'sv5',name:'Temporal Forces',era:'sv',year:2024,total:218},
  {id:'sv4pt5',name:'Paldean Fates',era:'sv',year:2024,total:245},
  {id:'sv4',name:'Paradox Rift',era:'sv',year:2023,total:266},
  {id:'sv3',name:'Obsidian Flames',era:'sv',year:2023,total:230},
  {id:'sv3pt5',name:'151',era:'sv',year:2023,total:207},
  {id:'sv2',name:'Paldea Evolved',era:'sv',year:2023,total:279},
  {id:'sv1',name:'Scarlet & Violet',era:'sv',year:2023,total:258},
  {id:'swsh12pt5',name:'Crown Zenith',era:'swsh',year:2023,total:230},
  {id:'swsh12',name:'Silver Tempest',era:'swsh',year:2022,total:245},
  {id:'swsh11',name:'Lost Origin',era:'swsh',year:2022,total:217},
  {id:'swsh9',name:'Brilliant Stars',era:'swsh',year:2022,total:186},
  {id:'swsh8',name:'Fusion Strike',era:'swsh',year:2021,total:284},
  {id:'swsh7',name:'Evolving Skies',era:'swsh',year:2021,total:237},
  {id:'swsh6',name:'Chilling Reign',era:'swsh',year:2021,total:233},
  {id:'swsh5',name:'Battle Styles',era:'swsh',year:2021,total:183},
  {id:'swsh4',name:'Vivid Voltage',era:'swsh',year:2020,total:203},
  {id:'swsh3',name:'Darkness Ablaze',era:'swsh',year:2020,total:201},
  {id:'swsh2',name:'Rebel Clash',era:'swsh',year:2020,total:209},
  {id:'swsh1',name:'Sword & Shield',era:'swsh',year:2020,total:216},
  {id:'sm12',name:'Cosmic Eclipse',era:'sm',year:2019,total:272},
  {id:'sm11',name:'Unified Minds',era:'sm',year:2019,total:260},
  {id:'sm10',name:'Unbroken Bonds',era:'sm',year:2019,total:234},
  {id:'sm9',name:'Team Up',era:'sm',year:2019,total:196},
  {id:'sm8',name:'Lost Thunder',era:'sm',year:2018,total:238},
  {id:'sm6',name:'Forbidden Light',era:'sm',year:2018,total:146},
  {id:'sm5',name:'Ultra Prism',era:'sm',year:2018,total:173},
  {id:'sm4',name:'Crimson Invasion',era:'sm',year:2017,total:124},
  {id:'sm3',name:'Burning Shadows',era:'sm',year:2017,total:169},
  {id:'sm2',name:'Guardians Rising',era:'sm',year:2017,total:169},
  {id:'sm1',name:'Sun & Moon',era:'sm',year:2017,total:149},
  {id:'xy12',name:'Evolutions',era:'xy',year:2016,total:113},
  {id:'xy11',name:'Steam Siege',era:'xy',year:2016,total:116},
  {id:'xy9',name:'BREAKpoint',era:'xy',year:2016,total:123},
  {id:'xy8',name:'BREAKthrough',era:'xy',year:2015,total:162},
  {id:'xy6',name:'Roaring Skies',era:'xy',year:2015,total:108},
  {id:'xy5',name:'Primal Clash',era:'xy',year:2015,total:160},
  {id:'xy4',name:'Phantom Forces',era:'xy',year:2014,total:122},
  {id:'xy2',name:'Flashfire',era:'xy',year:2014,total:109},
  {id:'xy1',name:'XY',era:'xy',year:2014,total:146},
  {id:'bw11',name:'Legendary Treasures',era:'bw',year:2013,total:140},
  {id:'bw10',name:'Plasma Blast',era:'bw',year:2013,total:105},
  {id:'bw9',name:'Plasma Freeze',era:'bw',year:2013,total:122},
  {id:'bw8',name:'Plasma Storm',era:'bw',year:2013,total:138},
  {id:'bw7',name:'Boundaries Crossed',era:'bw',year:2012,total:153},
  {id:'bw6',name:'Dragons Exalted',era:'bw',year:2012,total:128},
  {id:'bw5',name:'Dark Explorers',era:'bw',year:2012,total:111},
  {id:'bw4',name:'Next Destinies',era:'bw',year:2012,total:103},
  {id:'bw3',name:'Noble Victories',era:'bw',year:2011,total:102},
  {id:'bw2',name:'Emerging Powers',era:'bw',year:2011,total:98},
  {id:'bw1',name:'Black & White',era:'bw',year:2011,total:115},
  {id:'hgss4',name:'Triumphant',era:'hgss',year:2010,total:103},
  {id:'hgss3',name:'Undaunted',era:'hgss',year:2010,total:91},
  {id:'hgss2',name:'Unleashed',era:'hgss',year:2010,total:96},
  {id:'hgss1',name:'HeartGold SoulSilver',era:'hgss',year:2010,total:124},
  {id:'pl4',name:'Arceus',era:'pl',year:2009,total:111},
  {id:'pl3',name:'Supreme Victors',era:'pl',year:2009,total:175},
  {id:'pl2',name:'Rising Rivals',era:'pl',year:2009,total:120},
  {id:'pl1',name:'Platinum',era:'pl',year:2009,total:133},
  {id:'dp7',name:'Stormfront',era:'dp',year:2008,total:106},
  {id:'dp6',name:'Legends Awakened',era:'dp',year:2008,total:146},
  {id:'dp5',name:'Majestic Dawn',era:'dp',year:2008,total:100},
  {id:'dp4',name:'Great Encounters',era:'dp',year:2008,total:106},
  {id:'dp3',name:'Secret Wonders',era:'dp',year:2007,total:132},
  {id:'dp2',name:'Mysterious Treasures',era:'dp',year:2007,total:124},
  {id:'dp1',name:'Diamond & Pearl',era:'dp',year:2007,total:130},
  {id:'ex16',name:'Power Keepers',era:'ex',year:2007,total:108},
  {id:'ex15',name:'Dragon Frontiers',era:'ex',year:2006,total:101},
  {id:'ex14',name:'Crystal Guardians',era:'ex',year:2006,total:100},
  {id:'ex13',name:'Holon Phantoms',era:'ex',year:2006,total:111},
  {id:'ex11',name:'Delta Species',era:'ex',year:2005,total:114},
  {id:'ex10',name:'Unseen Forces',era:'ex',year:2005,total:145},
  {id:'ex8',name:'Deoxys',era:'ex',year:2005,total:108},
  {id:'ex7',name:'Team Rocket Returns',era:'ex',year:2004,total:111},
  {id:'ex6',name:'FireRed & LeafGreen',era:'ex',year:2004,total:116},
  {id:'ex4',name:'Team Magma vs Team Aqua',era:'ex',year:2004,total:97},
  {id:'ex3',name:'Dragon',era:'ex',year:2003,total:100},
  {id:'ex2',name:'Sandstorm',era:'ex',year:2003,total:100},
  {id:'ex1',name:'Ruby & Sapphire',era:'ex',year:2003,total:109},
  {id:'ecard3',name:'Skyridge',era:'base',year:2003,total:182},
  {id:'ecard2',name:'Aquapolis',era:'base',year:2003,total:186},
  {id:'ecard1',name:'Expedition Base Set',era:'base',year:2002,total:165},
  {id:'neo4',name:'Neo Destiny',era:'base',year:2002,total:113},
  {id:'neo3',name:'Neo Revelation',era:'base',year:2001,total:66},
  {id:'neo2',name:'Neo Discovery',era:'base',year:2001,total:75},
  {id:'neo1',name:'Neo Genesis',era:'base',year:2000,total:111},
  {id:'gym2',name:'Gym Challenge',era:'base',year:2000,total:132},
  {id:'gym1',name:'Gym Heroes',era:'base',year:2000,total:132},
  {id:'base5',name:'Team Rocket',era:'base',year:2000,total:83},
  {id:'base4',name:'Base Set 2',era:'base',year:2000,total:130},
  {id:'base3',name:'Fossil',era:'base',year:1999,total:62},
  {id:'base2',name:'Jungle',era:'base',year:1999,total:64},
  {id:'base1',name:'Base Set',era:'base',year:1999,total:102},
];

var ERAS = [
  {id:'all', label:'All eras',           color:'var(--c-electric)'},
  {id:'me',  label:'Mega Evolution',     color:'var(--c-dragon)'},
  {id:'base',label:'WotC (1999–2003)',   color:'var(--c-ground)'},
  {id:'ex',  label:'EX era (2003–2007)', color:'var(--c-fire)'},
  {id:'dp',  label:'Diamond & Pearl',    color:'var(--c-water)'},
  {id:'pl',  label:'Platinum',           color:'var(--c-ghost)'},
  {id:'hgss',label:'HeartGold SoulSilver',color:'var(--c-grass)'},
  {id:'bw',  label:'Black & White',      color:'var(--c-dragon)'},
  {id:'xy',  label:'XY',                color:'var(--c-fighting)'},
  {id:'sm',  label:'Sun & Moon',         color:'var(--c-psychic)'},
  {id:'swsh',label:'Sword & Shield',     color:'var(--c-ice)'},
  {id:'sv',  label:'Scarlet & Violet',   color:'var(--c-electric)'},
];

// ─── DECK UTILS ───────────────────────────────────────────────────────────────
function loadDecks(){try{var r=localStorage.getItem('pm_decks');return r?JSON.parse(r):[];}catch(e){return[];}}
function saveDecks(d){localStorage.setItem('pm_decks',JSON.stringify(d));}
function deckCount(deck){return deck.cards.reduce(function(s,e){return s+e.qty;},0);}
function countInDeck(deck,cardId){var e=deck.cards.find(function(e){return e.card.id===cardId;});return e?e.qty:0;}
function maxCopies(card){if(card.supertype==='Energy'&&card.subtypes&&card.subtypes.indexOf('Basic')!==-1)return 99;return 4;}
function addCardToDeck(deck,card){
  var total=deckCount(deck);if(total>=60)return{ok:false,msg:'Deck is full (60 cards max)'};
  var cur=countInDeck(deck,card.id),max=maxCopies(card);
  if(cur>=max)return{ok:false,msg:'Max '+max+' copies of '+card.name};
  var found=false;
  var cards=deck.cards.map(function(e){if(e.card.id===card.id){found=true;return{card:e.card,qty:e.qty+1};}return e;});
  if(!found)cards=cards.concat([{card:card,qty:1}]);
  return{ok:true,cards:cards};
}
function removeCard(deck,id){return deck.cards.map(function(e){if(e.card.id===id)return{card:e.card,qty:e.qty-1};return e;}).filter(function(e){return e.qty>0;});}
function validateDeck(deck, fmt){
  var t=deckCount(deck),issues=[];
  if(t<60)issues.push('Need '+(60-t)+' more cards');
  if(!deck.cards.some(function(e){return e.card.supertype==='Pokemon'||e.card.supertype==='Pokémon';}))issues.push('No Pokémon in deck');
  // Format legality check
  if(fmt && fmt !== 'unlimited') {
    var formatDef = FORMATS[fmt];
    var illegal = deck.cards.filter(function(e){
      var card = e.card;
      // Basic Energy always legal
      if(card.supertype==='Energy' && card.subtypes && card.subtypes.indexOf('Basic')!==-1) return false;
      // Find set in our SETS array by name
      var setEntry = SETS.find(function(s){return s.name===card.setName;});
      if(!setEntry) return false; // unknown set, assume legal
      return !formatDef.isLegal(setEntry.id, setEntry.era);
    });
    if(illegal.length>0){
      var names = illegal.slice(0,2).map(function(e){return e.card.name;}).join(', ');
      issues.push((fmt==='standard'?'Not Standard legal: ':'Not Expanded legal: ')+names+(illegal.length>2?' +more':''));
    }
  }
  return issues;
}

function worldsDeckToPlayable(wdeck){
  return{
    name:wdeck.name, isWorldsDeck:true,
    cards:wdeck.cards.map(function(c){
      return{
        qty:c.qty, setName:c.set,
        card:{
          id:wdeck.id+'-'+c.name.replace(/\s+/g,'-'), name:c.name, setName:c.set,
          supertype:'Pokemon', subtypes:['Basic'],
          types:[], hp:null, attacks:[], abilities:[],
          weaknesses:[], resistances:[], retreatCost:[], rules:[], images:{},
        }
      };
    })
  };
}

// ─── TYPE CHIP ────────────────────────────────────────────────────────────────
function TypeChip({type, solid}){
  if(!type) return null;
  return(
    <span style={{
      display:'inline-flex',alignItems:'center',gap:4,
      padding:'2px 8px 2px 4px',borderRadius:'var(--r-full)',
      fontSize:'var(--fs-11)',fontWeight:600,textTransform:'capitalize',
      background:solid?typeColor(type):typeBg(type),
      color:solid?'#fff':typeInk(type),
    }}>
      <TypeBadge type={type} size={14}/>
      {type}
    </span>
  );
}

// ─── TOAST ────────────────────────────────────────────────────────────────────
function Toast({data}){
  if(!data) return null;
  var dotColor = data.color==='danger'?'var(--c-fire)':data.color==='warning'?'var(--c-electric)':data.color==='info'?'var(--c-water)':'var(--c-grass)';
  return(
    <div className="pm-toast">
      <span style={{width:8,height:8,borderRadius:'50%',background:dotColor,flexShrink:0}}/>
      {data.msg}
    </div>
  );
}

// ─── FORMAT BADGE ────────────────────────────────────────────────────────────
function FormatBadge({card, fmt}) {
  if(!fmt || fmt === 'unlimited') return null;
  if(!card || !card.setName) return null;
  // Basic Energy always legal
  if(card.supertype==='Energy' && card.subtypes && card.subtypes.indexOf('Basic')!==-1) return null;
  var setEntry = SETS.find(function(s){return s.name===card.setName;});
  if(!setEntry) return null;
  var formatDef = FORMATS[fmt];
  var legal = formatDef.isLegal(setEntry.id, setEntry.era);
  var mark = SET_REG_MARKS[setEntry.id];
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:3,
      padding:'1px 6px', borderRadius:'var(--r-sm)',
      fontSize:'var(--fs-10)', fontWeight:700,
      background: legal ? 'var(--c-grass-bg)' : 'var(--c-fire-bg)',
      color: legal ? 'var(--c-grass-ink)' : 'var(--c-fire-ink)',
      border: '1px solid ' + (legal ? 'var(--c-grass)' : 'var(--c-fire)'),
      letterSpacing:'var(--tr-wide)',
    }}>
      {mark && <span style={{fontFamily:'var(--font-mono)'}}>{mark}</span>}
      {legal ? '✓' : '✗'}
    </span>
  );
}

// ─── PRICE PANEL ─────────────────────────────────────────────────────────────
function PricePanel({card}) {
  var tcg = card.tcgplayer;
  var hasPrice = tcg && tcg.prices && Object.keys(tcg.prices).length > 0;

  function fmt(n){ return n!=null ? '$'+Number(n).toFixed(2) : '—'; }

  // Build all price variants (normal, holofoil, reverseHolofoil, 1stEdition, etc)
  var variants = hasPrice ? Object.entries(tcg.prices) : [];

  // Best label map
  var VARIANT_LABELS = {
    normal:             'Normal',
    holofoil:           'Holofoil',
    reverseHolofoil:    'Reverse Holo',
    unlimited:          'Unlimited',
    '1stEditionHolofoil': '1st Ed. Holo',
    '1stEditionNormal': '1st Ed. Normal',
    unlimitedHolofoil:  'Unlimited Holo',
  };

  // Pick the most relevant variant for the headline price
  var primary = null;
  var preferOrder = ['holofoil','normal','reverseHolofoil','unlimitedHolofoil','1stEditionHolofoil','unlimited','1stEditionNormal'];
  for(var i=0;i<preferOrder.length;i++){
    if(tcg && tcg.prices && tcg.prices[preferOrder[i]]){ primary={key:preferOrder[i],data:tcg.prices[preferOrder[i]]};break; }
  }
  if(!primary && variants.length>0){ primary={key:variants[0][0],data:variants[0][1]}; }

  var tcgUrl = tcg && tcg.url ? tcg.url : ('https://www.tcgplayer.com/search/pokemon/product?q='+encodeURIComponent(card.name)+'&view=grid');

  return(
    <div style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)',overflow:'hidden',flexShrink:0,width:'100%'}}>
      {/* Header band */}
      <div style={{background:'var(--c-electric-bg)',borderBottom:'1px solid var(--c-electric)',padding:'var(--sp-3) var(--sp-4)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <p className="eyebrow" style={{color:'var(--c-electric-ink)',marginBottom:2}}>Market price</p>
          <div style={{fontFamily:'var(--font-display)',fontWeight:900,fontSize:'var(--fs-30)',color:'var(--c-electric-ink)',lineHeight:1}}>
            {primary ? fmt(primary.data.market||primary.data.mid) : '—'}
          </div>
          {primary&&<div style={{fontSize:'var(--fs-11)',color:'var(--c-electric-ink)',opacity:0.7,marginTop:2}}>{VARIANT_LABELS[primary.key]||primary.key}</div>}
        </div>
        <a href={tcgUrl} target="_blank" rel="noreferrer"
          style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'10px 16px',borderRadius:'var(--r-md)',background:'var(--c-electric)',color:'#000',textDecoration:'none',fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-14)',gap:2,flexShrink:0}}>
          <span>Buy now</span>
          <span style={{fontSize:'var(--fs-10)',fontWeight:600,opacity:0.75}}>TCGPlayer →</span>
        </a>
      </div>

      {/* Price breakdown */}
      {hasPrice ? (
        <div style={{padding:'var(--sp-3) var(--sp-4)'}}>
          {variants.map(function(v){
            var vKey=v[0], vData=v[1];
            var label=VARIANT_LABELS[vKey]||vKey;
            return(
              <div key={vKey} style={{marginBottom:'var(--sp-3)'}}>
                {variants.length>1&&<div style={{fontFamily:'var(--font-ui)',fontSize:'var(--fs-11)',fontWeight:700,color:'var(--c-fg-3)',letterSpacing:'var(--tr-wide)',textTransform:'uppercase',marginBottom:'var(--sp-2)'}}>{label}</div>}
                {(function(){
                  var market = vData.market || vData.mid || 0;
                  var rawHigh = vData.high;
                  // Cap high at 10x market to filter out obvious outlier listings
                  // e.g. someone listing a $0.16 card for $19.99
                  var cappedHigh = (rawHigh && market > 0 && rawHigh > market * 10)
                    ? null // show as outlier
                    : rawHigh;
                  var highIsOutlier = rawHigh && market > 0 && rawHigh > market * 10;
                  return(
                    <>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'var(--sp-2)'}}>
                        {[
                          {label:'Low',    value:vData.low,    color:'var(--c-grass-ink)'},
                          {label:'Market', value:market,       color:'var(--c-electric-ink)'},
                          {label:'High',   value:cappedHigh,   color:'var(--c-fire-ink)'},
                        ].map(function(p){return(
                          <div key={p.label} style={{background:'var(--c-mist)',borderRadius:'var(--r-md)',padding:'var(--sp-2) var(--sp-3)',textAlign:'center'}}>
                            <div style={{fontFamily:'var(--font-mono)',fontWeight:700,fontSize:'var(--fs-16)',color:p.value!=null?p.color:'var(--c-fg-3)',fontVariantNumeric:'tabular-nums'}}>{p.value!=null?fmt(p.value):'—'}</div>
                            <div style={{fontSize:'var(--fs-10)',fontWeight:700,color:'var(--c-fg-3)',letterSpacing:'var(--tr-wide)',textTransform:'uppercase',marginTop:2}}>{p.label}</div>
                          </div>
                        );})}
                      </div>
                      {highIsOutlier&&<div style={{fontSize:'var(--fs-11)',color:'var(--c-fg-3)',marginTop:4,textAlign:'right'}}>High listing: {fmt(rawHigh)} (outlier hidden)</div>}
                      {vData.directLow!=null&&<div style={{fontSize:'var(--fs-11)',color:'var(--c-fg-3)',marginTop:4,textAlign:'right'}}>Direct low: {fmt(vData.directLow)}</div>}
                    </>
                  );
                })()}
              </div>
            );
          })}
          {tcg.updatedAt&&<div style={{fontSize:'var(--fs-11)',color:'var(--c-fg-3)',textAlign:'right',marginTop:'var(--sp-2)',fontFamily:'var(--font-mono)'}}>Updated {tcg.updatedAt}</div>}
        </div>
      ):(
        <div style={{padding:'var(--sp-4)',textAlign:'center'}}>
          <div style={{fontSize:28,marginBottom:8}}>💸</div>
          <div style={{fontSize:'var(--fs-13)',color:'var(--c-fg-3)',marginBottom:'var(--sp-3)'}}>No pricing data available for this card.</div>
          <a href={tcgUrl} target="_blank" rel="noreferrer"
            style={{display:'inline-block',padding:'8px 20px',borderRadius:'var(--r-md)',background:'var(--c-electric)',color:'#000',textDecoration:'none',fontFamily:'var(--font-ui)',fontWeight:700,fontSize:'var(--fs-13)'}}>
            Search on TCGPlayer →
          </a>
        </div>
      )}
    </div>
  );
}

// ─── FORMAT SELECTOR ─────────────────────────────────────────────────────────
function FormatSelector({format, onChange}) {
  return (
    <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
      <span className="eyebrow" style={{marginRight:4}}>Format</span>
      {Object.entries(FORMATS).map(function([id, def]) {
        var active = format === id;
        return (
          <button key={id} onClick={function(){onChange(id);}}
            style={{
              padding:'5px 12px', borderRadius:'var(--r-full)',
              fontSize:'var(--fs-12)', fontWeight:700, cursor:'pointer',
              border:'1px solid '+(active?def.color:'var(--c-line)'),
              background:active?'color-mix(in srgb, '+def.color+' 18%, transparent)':'var(--c-mist)',
              color:active?def.color:'var(--c-fg-2)',
              fontFamily:'var(--font-ui)',
              transition:'all var(--dur-1) var(--ease-smooth)',
            }}>
            {def.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App(){
  var [theme, toggleTheme] = useTheme();
  // Navigate to wishlist tab if URL has shared wishlist param
  useEffect(function(){
    var params=new URLSearchParams(window.location.search);
    if(params.get('wl')){
      setScreen('wishlist');
    }
  },[]);
  var [session, setSession]       = useState(null);
  var [profile, setProfile]       = useState(null);
  var [showAuth, setShowAuth]     = useState(false);
  var [authLoading, setAuthLoading] = useState(true);
  var winWidth = useWindowWidth();

  // ── Supabase auth ─────────────────────────────────────────────────────────
  useEffect(function() {
    // Check existing session on mount
    getSession().then(function(s) {
      setSession(s);
      setAuthLoading(false);
    });
    // Listen for auth changes
    var sub = onAuthStateChange(function(s) {
      setSession(s);
      if (!s) setProfile(null);
    });
    return function() { sub.data.subscription.unsubscribe(); };
  }, []);

  // Load profile when session changes
  useEffect(function() {
    if (!session) return;
    supabase.from('profiles').select('*').eq('id', session.user.id).single()
      .then(function(r) { if (r.data) setProfile(r.data); });
  }, [session]);

  // Sync decks to Supabase when logged in
  useEffect(function() {
    if (!session) return;
    fetchDecks(session.user.id).then(function(remoteDecks) {
      if (remoteDecks.length > 0) {
        // Convert Supabase deck format back to app format
        var converted = remoteDecks.map(function(d) {
          return { id: d.id, name: d.name, cards: d.cards || [], format: d.format || 'unlimited', created: d.created_at };
        });
        setDecks(converted);
        saveDecks(converted);
      }
    }).catch(console.error);
  }, [session]);

  async function handleSaveDeck(deck) {
    if (!session) { showToast('Sign in to save decks to the cloud', 'warning'); return; }
    try {
      var saved = await saveDeck(session.user.id, deck);
      showToast('Deck saved to cloud ☁️');
      return saved;
    } catch(e) { showToast('Save failed: ' + e.message, 'danger'); }
  }

  async function handleDeleteDeck(idx) {
    var deck = decks[idx];
    if (session && deck.id) {
      try { await deleteDeckRemote(session.user.id, deck.id); } catch(e) {}
    }
    deleteDeck(idx);
  }

  // Cache of set preview cards so we don't re-fetch on every hover
  var previewCardCache = useRef({});

  // Fetch a card from hovered set for drawer hero background
  useEffect(function(){
    var ps=hoveredSet||activeSet;
    if(!ps){return;} // don't clear — keep last card visible while loading next
    // Return cached card immediately
    if(previewCardCache.current[ps.id]){
      setPreviewCard(previewCardCache.current[ps.id]);
      return;
    }
    var cancelled=false;
    fetch('https://api.pokemontcg.io/v2/cards?q=set.id:'+ps.id+'&pageSize=10&orderBy=number')
      .then(function(r){return r.json();})
      .then(function(j){
        if(cancelled||!j.data||!j.data.length)return;
        var candidates=j.data.filter(function(c){return c.images&&c.images.large;});
        if(!candidates.length){
          // Try small image fallback
          var anyImg=j.data.find(function(c){return c.images&&(c.images.large||c.images.small);});
          if(anyImg){
            var imgUrl=anyImg.images.large||anyImg.images.small;
            previewCardCache.current[ps.id]={imageUrl:imgUrl};
            setPreviewCard({imageUrl:imgUrl});
          }
          return;
        }
        // Pick a card ~60% through — usually more interesting art than #1
        var pick=candidates[Math.min(Math.floor(candidates.length*0.6),candidates.length-1)];
        var imgUrl=pick.images.large||pick.images.small;
        previewCardCache.current[ps.id]={imageUrl:imgUrl};
        setPreviewCard({imageUrl:imgUrl});
      }).catch(function(){});
    return function(){cancelled=true;};
  },[hoveredSet,activeSet]);

  var isMobile = winWidth < 640;
  var [menuOpen, setMenuOpen] = useState(false);
  var [screen,setScreen]           = useState('sets');
  var [era,setEra]                 = useState('all');
  var [setSearch,setSetSearch]     = useState('');
  var [activeSet,setActiveSet]     = useState(null);
  var [cards,setCards]             = useState([]);
  var [total,setTotal]             = useState(0);
  var [loading,setLoading]         = useState(false);
  var [detail,setDetail]           = useState(null);
  var [navStack,setNavStack]       = useState([]);
  var [collections,setCollections] = useState(function(){try{var r=localStorage.getItem('pm_collections');return r?JSON.parse(r):[];}catch(e){return[];}});
  var [activeCollection,setActiveCollection] = useState(null); // id of open collection
  var [showCollectionModal,setShowCollectionModal] = useState(false); // create/edit modal
  var [editingCollection,setEditingCollection] = useState(null); // null=new, obj=editing
  var [collectionForm,setCollectionForm] = useState({name:'',description:'',tags:'',color:'var(--c-water)'});
  var [cardNoteModal,setCardNoteModal] = useState(null); // {collectionId, cardId, note}
  var [photoSearchModal,setPhotoSearchModal] = useState(null); // {target: 'collection'|'catalog', collectionId?: id}
  var [photoSearchResults,setPhotoSearchResults] = useState(null); // {cardName, fullText, results}
  var [wishlist,setWishlist]       = useState(function(){try{var r=localStorage.getItem('pm_wishlist');return r?JSON.parse(r):[];}catch(e){return[];}});
  // Check if viewing a shared wishlist from URL
  var [sharedWishlist,setSharedWishlist] = useState(function(){
    try{
      var params=new URLSearchParams(window.location.search);
      var encoded=params.get('wl');
      if(encoded){
        var decoded=JSON.parse(atob(encoded));
        return decoded;
      }
    }catch(e){}
    return null;
  });
  var [shareToast,setShareToast] = useState(false);
  var [decks,setDecks]             = useState(loadDecks);
  var [activeDeckIdx,setActiveDeckIdx] = useState(null);
  var [toast,setToast]             = useState(null);
  var [newDeckName,setNewDeckName] = useState('');
  var [globalQ,setGlobalQ]         = useState('');
  var [globalCards,setGlobalCards] = useState([]);
  var [globalLoading,setGlobalLoading] = useState(false);
  var [lastQ,setLastQ]             = useState('');
  var [battleDeck,setBattleDeck]   = useState(null);
  var [worldsEra,setWorldsEra]     = useState('all');
  var [hoveredSet,setHoveredSet]   = useState(null);
  var [previewCard,setPreviewCard] = useState(null); // card image for drawer hero
  var [format,setFormat]           = useState('unlimited');
  var [evtFilter,setEvtFilter]     = useState('all');
  var [showPast,setShowPast]       = useState(false);
  var [deckTab,setDeckTab]         = useState('meta');
  var debRef = useRef(null);

  var activeDeck = activeDeckIdx!==null ? decks[activeDeckIdx] : null;
  var visibleSets = SETS.filter(function(s){
    if(era!=='all'&&s.era!==era) return false;
    if(setSearch&&!s.name.toLowerCase().includes(setSearch.toLowerCase())) return false;
    // Format filter — hide sets that have no legal cards in the selected format
    if(format==='standard'){
      var mark=SET_REG_MARKS[s.id];
      if(!mark||!('HIJ'.includes(mark))) return false;
    }
    if(format==='expanded'){
      if(!EXPANDED_ERAS.has(s.era)) return false;
    }
    return true;
  });
  var visibleWorlds = WORLDS_DECKS.filter(function(d){return worldsEra==='all'||d.era===worldsEra;});

  function showToast(msg,color){setToast({msg:msg,color:color||'success'});setTimeout(function(){setToast(null);},2600);}

  // ── Wishlist helpers ─────────────────────────────────────────────────────────
  function saveWishlist(wl){ localStorage.setItem('pm_wishlist', JSON.stringify(wl)); }

  var WISHLIST_VARIANT_LABELS = {
    normal:'Normal', holofoil:'Holofoil', reverseHolofoil:'Reverse Holo',
    unlimited:'Unlimited', '1stEditionHolofoil':'1st Ed. Holo',
    '1stEditionNormal':'1st Ed. Normal', unlimitedHolofoil:'Unlimited Holo',
    shadowless:'Shadowless', shadowlessHolofoil:'Shadowless Holo',
  };

  function addToWishlist(card, variant){
    // variant = specific key like 'holofoil', or null for auto-pick
    var entryId = card.id + (variant?'--'+variant:'');
    if(wishlist.some(function(w){return w.id===entryId;})){
      showToast(card.name+' already in wishlist','warning'); return;
    }
    var prices = card.tcgplayer&&card.tcgplayer.prices?card.tcgplayer.prices:{};
    // Pick market price for selected variant
    var marketPrice = null;
    if(variant&&prices[variant]){
      marketPrice = prices[variant].market||prices[variant].mid||null;
    } else {
      var order=['holofoil','normal','reverseHolofoil','unlimitedHolofoil','1stEditionHolofoil'];
      for(var i=0;i<order.length;i++){if(prices[order[i]]&&prices[order[i]].market){marketPrice=prices[order[i]].market;break;}}
      if(!marketPrice){var first=Object.values(prices)[0];marketPrice=first?first.market||first.mid:null;}
    }
    var variantLabel = variant?(WISHLIST_VARIANT_LABELS[variant]||variant):null;
    var entry={
      id:entryId,
      cardId:card.id,
      name:card.name,
      variant:variant||null,
      variantLabel:variantLabel,
      image:card.images&&card.images.small?card.images.small:null,
      setName:card.set&&card.set.name?card.set.name:'',
      number:card.number||'',
      rarity:card.rarity||'',
      types:card.types||[],
      supertype:card.supertype||'',
      tcgUrl:(card.tcgplayer&&card.tcgplayer.url)||('https://www.tcgplayer.com/search/pokemon/product?q='+encodeURIComponent(card.name)+'&view=grid'),
      marketPrice:marketPrice,
      addedAt:Date.now(),
      purchased:false,
    };
    var wl=wishlist.concat([entry]);
    setWishlist(wl);saveWishlist(wl);
    showToast('Added '+(variantLabel?variantLabel+' ':'')+card.name+' to wishlist ⭐');
  }

  function removeFromWishlist(id){
    var wl=wishlist.filter(function(w){return w.id!==id;});
    setWishlist(wl);saveWishlist(wl);
  }

  function togglePurchased(id){
    var wl=wishlist.map(function(w){return w.id===id?Object.assign({},w,{purchased:!w.purchased}):w;});
    setWishlist(wl);saveWishlist(wl);
  }

  function isInWishlist(cardId){ return wishlist.some(function(w){return w.id===cardId||w.cardId===cardId||w.id===cardId;}); }
  function isVariantInWishlist(cardId,variant){ return wishlist.some(function(w){return w.id===cardId+'--'+variant;}); }

  function shareWishlist(){
    var data = remaining.slice(0,50).map(function(w){
      return {id:w.id,name:w.name,image:w.image,setName:w.setName,number:w.number,rarity:w.rarity,marketPrice:w.marketPrice,tcgUrl:w.tcgUrl};
    });
    try{
      var encoded = btoa(JSON.stringify(data));
      var url = window.location.origin + window.location.pathname + '?wl=' + encoded + '#wishlist';
      if(navigator.share){
        navigator.share({title:'My Pokémon TCG Wishlist',text:'Check out my Pokémon TCG wishlist on Pallet Town!',url:url});
      } else {
        navigator.clipboard.writeText(url).then(function(){
          setShareToast(true);
          setTimeout(function(){setShareToast(false);},3000);
        });
      }
    }catch(e){ showToast('Could not generate share link','danger'); }
  }

  // ── Collections helpers ─────────────────────────────────────────────────────
  function saveCollections(c){ localStorage.setItem('pm_collections', JSON.stringify(c)); }

  var COLLECTION_COLORS = [
    'var(--c-water)','var(--c-fire)','var(--c-grass)','var(--c-electric)',
    'var(--c-psychic)','var(--c-dragon)','var(--c-ghost)','var(--c-fighting)',
    'var(--c-dark)','var(--c-steel)','var(--c-fairy)','var(--c-ground)',
  ];

  var COLLECTION_TAGS = ['Competitive','Casual','Vintage','Modern','Holo Hunt','Complete Set','Favourites','Trade Binder','For Sale','Graded'];

  function createCollection(form){
    var col = {
      id: 'col-'+Date.now(),
      name: form.name.trim(),
      description: form.description.trim(),
      tags: form.tags ? form.tags.split(',').map(function(t){return t.trim();}).filter(Boolean) : [],
      color: form.color || 'var(--c-water)',
      cards: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    var c = collections.concat([col]);
    setCollections(c); saveCollections(c);
    setCollectionForm({name:'',description:'',tags:'',color:'var(--c-water)'});
    setShowCollectionModal(false);
    showToast('Collection "'+col.name+'" created 📦');
    return col.id;
  }

  function updateCollection(id, form){
    var c = collections.map(function(col){
      if(col.id!==id) return col;
      return Object.assign({},col,{
        name:form.name.trim(),
        description:form.description.trim(),
        tags:form.tags?form.tags.split(',').map(function(t){return t.trim();}).filter(Boolean):[],
        color:form.color||col.color,
        updatedAt:Date.now(),
      });
    });
    setCollections(c); saveCollections(c);
    setShowCollectionModal(false);
    setEditingCollection(null);
    showToast('Collection updated');
  }

  function deleteCollection(id){
    var c = collections.filter(function(col){return col.id!==id;});
    setCollections(c); saveCollections(c);
    if(activeCollection===id) setActiveCollection(null);
    showToast('Collection deleted','danger');
  }

  var VARIANT_LABELS_COL = {
    normal:'Normal', holofoil:'Holofoil', reverseHolofoil:'Reverse Holo',
    unlimited:'Unlimited', '1stEditionHolofoil':'1st Ed. Holo',
    '1stEditionNormal':'1st Ed. Normal', unlimitedHolofoil:'Unlimited Holo',
    shadowless:'Shadowless', shadowlessHolofoil:'Shadowless Holo',
  };

  function addCardToCollection(collectionId, card, note, variant){
    // variant = key like 'holofoil', 'normal', etc. or null for base
    var entryId = card.id + (variant ? '--' + variant : '');
    var variantLabel = variant ? (VARIANT_LABELS_COL[variant]||variant) : null;
    var marketPrice = null;
    if(card.tcgplayer && card.tcgplayer.prices) {
      var vKey = variant || Object.keys(card.tcgplayer.prices)[0];
      var vData = card.tcgplayer.prices[vKey];
      if(vData) marketPrice = vData.market || vData.mid || null;
    }
    var c = collections.map(function(col){
      if(col.id!==collectionId) return col;
      if(col.cards.some(function(e){return e.id===entryId;})){
        showToast(card.name+(variantLabel?' ('+variantLabel+')':'')+' already in this collection','warning');
        return col;
      }
      var entry = {
        id:entryId,
        cardId:card.id,
        name:card.name,
        variant:variant||null,
        variantLabel:variantLabel,
        image:card.images&&card.images.small?card.images.small:null,
        setName:card.set&&card.set.name?card.set.name:'',
        number:card.number||'',
        rarity:card.rarity||'',
        types:card.types||[],
        supertype:card.supertype||'',
        marketPrice:marketPrice,
        note:note||'',
        condition:'NM', // Near Mint default
        addedAt:Date.now(),
      };
      return Object.assign({},col,{cards:col.cards.concat([entry]),updatedAt:Date.now()});
    });
    setCollections(c); saveCollections(c);
    showToast('Added '+(variantLabel||card.name)+' to collection 📦');
  }

  function removeCardFromCollection(collectionId, cardId){
    var c = collections.map(function(col){
      if(col.id!==collectionId) return col;
      return Object.assign({},col,{cards:col.cards.filter(function(e){return e.id!==cardId;}),updatedAt:Date.now()});
    });
    setCollections(c); saveCollections(c);
  }

  function updateCardNote(collectionId, cardId, note, condition){
    var c = collections.map(function(col){
      if(col.id!==collectionId) return col;
      return Object.assign({},col,{
        cards:col.cards.map(function(e){return e.id===cardId?Object.assign({},e,{note:note,condition:condition||e.condition||'NM'}):e;}),
        updatedAt:Date.now(),
      });
    });
    setCollections(c); saveCollections(c);
    setCardNoteModal(null);
    showToast('Note saved');
  }

  function isInCollection(collectionId, cardId, variant){
    var col = collections.find(function(c){return c.id===collectionId;});
    if(!col) return false;
    var entryId = cardId + (variant ? '--' + variant : '');
    // Check for this specific variant, OR any variant of the card
    return col.cards.some(function(e){return e.id===entryId||e.cardId===cardId||e.id===cardId;});
  }

  function isVariantInCollection(collectionId, cardId, variant){
    var col = collections.find(function(c){return c.id===collectionId;});
    if(!col) return false;
    var entryId = cardId + (variant ? '--' + variant : '');
    return col.cards.some(function(e){return e.id===entryId;});
  }

  function getCardCollections(cardId){
    return collections.filter(function(col){return col.cards.some(function(e){return e.id===cardId;});});
  }

  // ── Navigation stack (Spotify-style back) ─────────────────────────────────
  function pushNav(newDetail){
    // Save current state onto stack before navigating
    setNavStack(function(stack){
      return stack.concat([{screen:screen,detail:detail,activeSet:activeSet,scrollY:window.scrollY}]);
    });
    setDetail(newDetail);
    window.scrollTo(0,0);
  }

  function popNav(){
    setNavStack(function(stack){
      if(stack.length===0) return stack;
      var prev=stack[stack.length-1];
      // Restore previous state
      setScreen(prev.screen);
      setDetail(prev.detail);
      setActiveSet(prev.activeSet);
      setTimeout(function(){window.scrollTo(0,prev.scrollY||0);},50);
      return stack.slice(0,-1);
    });
  }

  function goHome(){
    setNavStack([]);
    setDetail(null);
    setScreen('sets');
    setActiveSet(null);
    window.scrollTo(0,0);
  }

  var loadCards = useCallback(async function(setId){
    setLoading(true);setCards([]);
    try{
      // Fetch all cards in set across multiple pages
      var all=[];var page=1;var pageSize=250;
      while(true){
        var r=await fetch('https://api.pokemontcg.io/v2/cards?q=set.id:'+setId+'&page='+page+'&pageSize='+pageSize+'&orderBy=number');
        var j=await r.json();
        var batch=j.data||[];
        all=all.concat(batch);
        if(all.length>=j.totalCount||batch.length<pageSize)break;
        page++;
      }
      setTotal(all.length);
      // Group by type
      setCards(all);
    }catch(e){}
    setLoading(false);
  },[]);

  var searchCards = useCallback(async function(q){
    if(!q.trim()) return;
    setGlobalLoading(true);setGlobalCards([]);setLastQ(q.trim());
    try{
      var r=await fetch('https://api.pokemontcg.io/v2/cards?q='+encodeURIComponent('name:"'+q.trim()+'*"')+'&pageSize=250&orderBy=set.releaseDate');
      var j=await r.json();setGlobalCards(j.data||[]);
    }catch(e){}
    setGlobalLoading(false);
  },[]);

  function handleSearch(e){
    e.preventDefault();if(!globalQ.trim())return;
    searchCards(globalQ);setScreen('search');
  }
  function handleSearchInput(val){
    setGlobalQ(val);clearTimeout(debRef.current);
    if(!val.trim()){setGlobalCards([]);return;}
    debRef.current=setTimeout(function(){searchCards(val);setScreen('search');},500);
  }
  function openSet(s){
    setNavStack(function(stack){
      return stack.concat([{screen:screen,detail:detail,activeSet:activeSet,scrollY:window.scrollY}]);
    });
    setActiveSet(s);setDetail(null);setScreen('cards');loadCards(s.id);window.scrollTo(0,0);
  }

  function addToDeck(card){
    if(activeDeckIdx===null){showToast('Select a deck first','warning');return;}
    var r=addCardToDeck(decks[activeDeckIdx],card);
    if(!r.ok){showToast(r.msg,'danger');return;}
    var nd=Object.assign({},decks[activeDeckIdx],{cards:r.cards});
    var nds=decks.map(function(d,i){return i===activeDeckIdx?nd:d;});
    setDecks(nds);saveDecks(nds);
    showToast('Added '+card.name+' ('+countInDeck(nd,card.id)+'/'+maxCopies(card)+')');
  }
  function createDeck(){
    if(!newDeckName.trim()) return;
    var d={name:newDeckName.trim(),cards:[],created:Date.now()};
    var nds=decks.concat([d]);setDecks(nds);saveDecks(nds);
    setActiveDeckIdx(nds.length-1);setNewDeckName('');showToast('Deck "'+d.name+'" created');
  }
  function deleteDeck(idx){
    var nds=decks.filter(function(_,i){return i!==idx;});setDecks(nds);saveDecks(nds);
    if(activeDeckIdx===idx)setActiveDeckIdx(null);
    else if(activeDeckIdx>idx)setActiveDeckIdx(activeDeckIdx-1);
  }
  function removeFromDeck(cardId){
    if(activeDeckIdx===null) return;
    var nd=Object.assign({},decks[activeDeckIdx],{cards:removeCard(decks[activeDeckIdx],cardId)});
    var nds=decks.map(function(d,i){return i===activeDeckIdx?nd:d;});
    setDecks(nds);saveDecks(nds);
  }

  if(battleDeck) return <Battle deck={battleDeck} onExit={function(){setBattleDeck(null);setScreen('decklist');}}/>;

  // ── GLOBAL AUTH MODAL — rendered on top of every screen ──────────────────
  var GlobalAuthModal = showAuth
    ? <AuthModal onClose={function(){setShowAuth(false);}} onSuccess={function(s){setSession(s);setShowAuth(false);}}/>
    : null;;

  // ── NAV ──────────────────────────────────────────────────────────────────────
  var NAV_TABS = [
    {id:'sets',        label:'Cards',       icon:'🃏'},
    {id:'deckbuilder', label:'Build',       icon:'🔨'},
    {id:'decklist',    label:'Decks',       icon:'📋'},
    {id:'wishlist',    label:'Wishlist',    icon:'⭐'},
    {id:'collections', label:'Collection',  icon:'📦'},
    {id:'events',      label:'Events',      icon:'📅'},
  ];
  function navTabActive(id){ return screen===id||(id==='sets'&&(screen==='cards'||screen==='search')); }
  function goTab(id){ setNavStack([]);setScreen(id);setDetail(null);setActiveSet(null);setMenuOpen(false); }

  var navBaseStyle = {
    position:'sticky',top:isMobile?6:12,zIndex:100,
    margin:isMobile?'0 8px':'0 var(--sp-4)',
    background:'rgba(28,28,34,0.96)',backdropFilter:'blur(12px)',
    border:'1px solid var(--c-line)',
    borderRadius:isMobile?'var(--r-xl)':'var(--r-full)',
    boxShadow:'var(--sh-2)',
  };

  var nav = isMobile ? (
    /* ── MOBILE NAV ── */
    <nav style={navBaseStyle}>
      {/* Top row: brand + search + menu */}
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px'}}>
        <button onClick={goHome}
          style={{fontFamily:"'Archivo',sans-serif",fontWeight:900,fontSize:'var(--fs-16)',color:'var(--c-ink-pure)',letterSpacing:'-0.02em',flexShrink:0,background:'none',border:'none',cursor:'pointer',padding:0,userSelect:'none'}}>
          Pokemania
        </button>
        <form onSubmit={handleSearch} style={{flex:1,position:'relative',display:'flex'}}>
          <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--c-fg-3)',fontSize:13,pointerEvents:'none'}}>⌕</span>
          <input value={globalQ} onChange={function(e){handleSearchInput(e.target.value);}}
            placeholder="Search cards…"
            style={{width:'100%',background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-full)',padding:'7px 12px 7px 30px',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',color:'var(--c-fg-1)',outline:'none'}}
            onFocus={function(e){e.target.style.borderColor='var(--c-water)';}}
            onBlur={function(e){e.target.style.borderColor='var(--c-line)';}}
          />
        </form>
        <button onClick={function(){setMenuOpen(function(o){return !o;});}}
          style={{width:34,height:34,borderRadius:'var(--r-full)',border:'1px solid var(--c-line)',background:'var(--c-mist)',color:'var(--c-fg-2)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>
          {menuOpen?'✕':'☰'}
        </button>
      </div>
      {/* Tab row — always visible on mobile */}
      <div style={{display:'flex',borderTop:'1px solid var(--c-line)',padding:'4px 8px',gap:4}}>
        {NAV_TABS.map(function(t){
          var active=navTabActive(t.id);
          return(
            <button key={t.id} onClick={function(){goTab(t.id);}}
              style={{flex:1,padding:'7px 4px',borderRadius:'var(--r-lg)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-12)',fontWeight:700,border:'none',cursor:'pointer',color:active?'var(--c-paper)':'var(--c-fg-3)',background:active?'var(--c-ink)':'transparent',display:'flex',flexDirection:'column',alignItems:'center',gap:2,transition:'all var(--dur-1) var(--ease-smooth)'}}>
              <span style={{fontSize:14}}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>
      {/* Expandable menu for format + theme */}
      {menuOpen&&(
        <div style={{borderTop:'1px solid var(--c-line)',padding:'10px 12px',display:'flex',flexDirection:'column',gap:10}}>

          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span style={{fontSize:'var(--fs-13)',color:'var(--c-fg-2)'}}>Theme</span>
            <button onClick={function(){toggleTheme();setMenuOpen(false);}}
              style={{padding:'6px 16px',borderRadius:'var(--r-full)',border:'1px solid var(--c-line)',background:'var(--c-mist)',color:'var(--c-fg-2)',cursor:'pointer',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600}}>
              {theme==='dark'?'☀️ Light':'🌙 Dark'}
            </button>
          </div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span style={{fontSize:'var(--fs-13)',color:'var(--c-fg-2)'}}>Format</span>
            <div style={{display:'flex',gap:4}}>
              {Object.entries(FORMATS).map(function([id,def]){
                var active=format===id;
                return(
                  <button key={id} onClick={function(){setFormat(id);setMenuOpen(false);}}
                    style={{padding:'4px 10px',borderRadius:'var(--r-full)',fontSize:'var(--fs-11)',fontWeight:700,cursor:'pointer',border:'1px solid '+(active?def.color:'var(--c-line)'),background:active?'color-mix(in srgb, '+def.color+' 18%, transparent)':'transparent',color:active?def.color:'var(--c-fg-3)',fontFamily:'var(--font-ui)'}}>
                    {def.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span style={{fontSize:'var(--fs-13)',color:'var(--c-fg-2)'}}>Account</span>
            {session
              ? <button onClick={function(){signOut();setMenuOpen(false);}} style={{padding:'6px 16px',borderRadius:'var(--r-full)',border:'1px solid var(--c-line)',background:'var(--c-mist)',color:'var(--c-fg-2)',cursor:'pointer',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600}}>Sign out</button>
              : <button onClick={function(){setShowAuth(true);setMenuOpen(false);}} style={{padding:'6px 16px',borderRadius:'var(--r-full)',border:'1px solid var(--c-water)',background:'var(--c-water-bg)',color:'var(--c-water-ink)',cursor:'pointer',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:700}}>Sign in</button>
            }
          </div>
          {activeDeck&&(
            <div style={{background:'var(--c-grass-bg)',border:'1px solid var(--c-grass)',borderRadius:'var(--r-md)',padding:'6px 10px',fontSize:'var(--fs-12)',fontWeight:700,color:'var(--c-grass-ink)'}}>
              Editing: {activeDeck.name} · {deckCount(activeDeck)}/60
            </div>
          )}
        </div>
      )}
    </nav>
  ) : (
    /* ── DESKTOP NAV ── */
    <nav style={Object.assign({},navBaseStyle,{display:'flex',alignItems:'center',padding:'6px 6px 6px var(--sp-5)',gap:'var(--sp-2)',height:56})}>
      <button onClick={goHome}
        style={{fontFamily:"'Archivo', sans-serif",fontWeight:900,fontSize:'var(--fs-20)',color:'var(--c-ink-pure)',letterSpacing:'-0.03em',flexShrink:0,marginRight:'var(--sp-2)',userSelect:'none',background:'none',border:'none',cursor:'pointer',padding:0,transition:'opacity var(--dur-1)'}}>
        Pokemania
      </button>
      <form onSubmit={handleSearch} style={{flex:1,minWidth:160,maxWidth:360,display:'flex',position:'relative'}}>
        <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--c-fg-3)',fontSize:14,pointerEvents:'none'}}>⌕</span>
        <input value={globalQ} onChange={function(e){handleSearchInput(e.target.value);}}
          placeholder="Search all cards…"
          style={{width:'100%',background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-full)',padding:'7px 14px 7px 34px',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',color:'var(--c-fg-1)',outline:'none',transition:'border-color var(--dur-1)'}}
          onFocus={function(e){e.target.style.borderColor='var(--c-water)';}}
          onBlur={function(e){e.target.style.borderColor='var(--c-line)';}}
        />
      </form>
      <div style={{display:'flex',gap:'var(--sp-1)',marginLeft:'auto'}}>
        {NAV_TABS.map(function(t){
          var active=navTabActive(t.id);
          return(
            <button key={t.id} onClick={function(){goTab(t.id);}}
              style={{fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600,padding:'7px 14px',borderRadius:'var(--r-full)',color:active?'var(--c-paper)':'var(--c-fg-2)',background:active?'var(--c-ink)':'transparent',border:'none',cursor:'pointer',transition:'all var(--dur-1) var(--ease-smooth)',whiteSpace:'nowrap'}}>
              {t.label}
            </button>
          );
        })}
      </div>

      <button onClick={toggleTheme}
        title={theme==='dark'?'Switch to light mode':'Switch to dark mode'}
        style={{width:34,height:34,borderRadius:'var(--r-full)',border:'1px solid var(--c-line)',background:'var(--c-mist)',color:'var(--c-fg-2)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0,transition:'all var(--dur-1) var(--ease-smooth)'}}>
        {theme==='dark'?'☀️':'🌙'}
      </button>
      {/* Auth */}
      {!authLoading&&(session
        ? <UserMenu session={session} profile={profile} onSignOut={function(){signOut();}} onProfileUpdate={function(u){setProfile(function(p){return Object.assign({},p,u);});}}/>
        : <button onClick={function(e){e.stopPropagation();setShowAuth(true);}}
            style={{padding:'6px 16px',borderRadius:'var(--r-full)',fontSize:'var(--fs-13)',fontWeight:700,cursor:'pointer',border:'1px solid var(--c-water)',background:'var(--c-water-bg)',color:'var(--c-water-ink)',fontFamily:'var(--font-ui)',flexShrink:0,whiteSpace:'nowrap',position:'relative',zIndex:101}}>
            Sign in
          </button>
      )}
      {activeDeck&&(
        <div style={{background:'var(--c-grass-bg)',border:'1px solid var(--c-grass)',borderRadius:'var(--r-full)',padding:'4px 12px',fontSize:'var(--fs-11)',fontWeight:700,color:'var(--c-grass-ink)',flexShrink:0,whiteSpace:'nowrap'}}>
          {activeDeck.name} · {deckCount(activeDeck)}/60
        </div>
      )}
    </nav>
  );

  // Type order for grouping
  var TYPE_ORDER = ['Grass','Fire','Water','Lightning','Psychic','Fighting','Darkness','Metal','Dragon','Fairy','Colorless','Free'];
  var TRAINER_GROUPS = ['Supporter','Item','Stadium','Trainer'];

  function CardTile({card}){
    var t=card.types&&card.types[0];
    var inDk=activeDeck?countInDeck(activeDeck,card.id):0;
    return(
      <div style={{position:'relative'}}>
        <button onClick={function(){pushNav(card);}}
          style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:4,cursor:'pointer',width:'100%',transition:'all var(--dur-1) var(--ease-pop)',display:'block'}}
          onMouseEnter={function(e){e.currentTarget.style.borderColor=t?typeColor(t):'var(--c-ink-2)';e.currentTarget.style.transform='translateY(-2px)';}}
          onMouseLeave={function(e){e.currentTarget.style.borderColor='var(--c-line)';e.currentTarget.style.transform='none';}}>
          {card.images&&card.images.small
            ?<img src={card.images.small} alt={card.name} loading="lazy" style={{width:'100%',borderRadius:'var(--r-md)',display:'block'}}/>
            :<div style={{paddingBottom:'140%',background:'var(--c-mist)',borderRadius:'var(--r-md)',position:'relative'}}><span style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,color:'var(--c-fg-3)',padding:4,textAlign:'center'}}>{card.name}</span></div>
          }
          <div style={{fontSize:'var(--fs-11)',color:'var(--c-fg-3)',marginTop:4,textAlign:'center',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontFamily:'var(--font-ui)'}}>{card.name}</div>
          {format!=='unlimited'&&<div style={{textAlign:'center',marginTop:3}}><FormatBadge card={card} fmt={format}/></div>}
        </button>
        {inDk>0&&<span style={{position:'absolute',top:6,right:6,background:'var(--c-grass)',color:'#fff',borderRadius:'var(--r-full)',fontSize:'var(--fs-10)',fontWeight:800,padding:'1px 6px',pointerEvents:'none'}}>{inDk}</span>}
      </div>
    );
  }

  function TypeSection({type, cards, color}){
    if(!cards||!cards.length) return null;
    return(
      <div style={{marginBottom:'var(--sp-7)'}}>
        {/* Section header */}
        <div style={{display:'flex',alignItems:'center',gap:'var(--sp-3)',marginBottom:'var(--sp-4)',paddingBottom:'var(--sp-3)',borderBottom:'2px solid '+color+'44'}}>
          <TypeBadge type={type} size={20}/>
          <span style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-20)',color:color||'var(--c-fg-1)'}}>{type}</span>
          <span style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginLeft:'auto'}}>{cards.length} cards</span>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:'var(--sp-3)'}}>
          {cards.map(function(card){return <CardTile key={card.id} card={card}/>;}) }
        </div>
      </div>
    );
  }

  function CardGrid({list}){
    // Separate Pokemon, Trainers, Energy
    var pokemon = list.filter(function(c){return c.supertype==='Pokémon'||c.supertype==='Pokemon';});
    var trainers = list.filter(function(c){return c.supertype==='Trainer';});
    var energy   = list.filter(function(c){return c.supertype==='Energy';});

    // Group Pokemon by first type
    var pokeByType = {};
    pokemon.forEach(function(c){
      var t = (c.types&&c.types[0])||'Colorless';
      if(!pokeByType[t]) pokeByType[t]=[];
      pokeByType[t].push(c);
    });

    // Group Trainers by subtype
    var trainerByType = {};
    trainers.forEach(function(c){
      var sub = (c.subtypes&&c.subtypes[0])||'Trainer';
      if(!trainerByType[sub]) trainerByType[sub]=[];
      trainerByType[sub].push(c);
    });

    var typeOrder = ['Grass','Fire','Water','Lightning','Psychic','Fighting','Darkness','Metal','Dragon','Fairy','Colorless','Free'];

    return(
      <div>
        {/* Pokemon sections by type */}
        {Object.keys(pokeByType).length>0&&(
          <div style={{marginBottom:'var(--sp-6)'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:'var(--sp-5)',paddingBottom:'var(--sp-3)',borderBottom:'1px solid var(--c-line)'}}>
              <span style={{fontFamily:'var(--font-display)',fontWeight:900,fontSize:'var(--fs-24)',color:'var(--c-fg-1)'}}>Pokémon</span>
              <span style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-13)',color:'var(--c-fg-3)'}}>{pokemon.length}</span>
            </div>
            {typeOrder.map(function(type){
              var group = pokeByType[type];
              if(!group||!group.length) return null;
              return <TypeSection key={type} type={type} cards={group} color={typeColor(type)}/>;
            })}
            {/* Any types not in our order */}
            {Object.keys(pokeByType).filter(function(t){return typeOrder.indexOf(t)===-1;}).map(function(type){
              return <TypeSection key={type} type={type} cards={pokeByType[type]} color={typeColor(type)}/>;
            })}
          </div>
        )}

        {/* Trainer sections by subtype */}
        {trainers.length>0&&(
          <div style={{marginBottom:'var(--sp-6)'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:'var(--sp-5)',paddingBottom:'var(--sp-3)',borderBottom:'1px solid var(--c-line)'}}>
              <span style={{fontFamily:'var(--font-display)',fontWeight:900,fontSize:'var(--fs-24)',color:'var(--c-fg-1)'}}>Trainers</span>
              <span style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-13)',color:'var(--c-fg-3)'}}>{trainers.length}</span>
            </div>
            {['Supporter','Item','Stadium','Tool','Ace Spec'].map(function(sub){
              var group=trainerByType[sub];
              if(!group||!group.length) return null;
              var subColor = sub==='Supporter'?'var(--c-psychic)':sub==='Stadium'?'var(--c-grass)':sub==='Tool'?'var(--c-ground)':'var(--c-water)';
              return(
                <div key={sub} style={{marginBottom:'var(--sp-6)'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'var(--sp-3)',marginBottom:'var(--sp-3)',paddingBottom:'var(--sp-2)',borderBottom:'2px solid '+subColor+'44'}}>
                    <span style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-18)',color:subColor}}>{sub}</span>
                    <span style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginLeft:'auto'}}>{group.length} cards</span>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:'var(--sp-3)'}}>
                    {group.map(function(card){return <CardTile key={card.id} card={card}/>;}) }
                  </div>
                </div>
              );
            })}
            {/* Any other trainer subtypes */}
            {Object.keys(trainerByType).filter(function(s){return['Supporter','Item','Stadium','Tool','Ace Spec'].indexOf(s)===-1;}).map(function(sub){
              return(
                <div key={sub} style={{marginBottom:'var(--sp-5)'}}>
                  <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-18)',color:'var(--c-water)',marginBottom:'var(--sp-3)'}}>{sub} <span style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-12)',color:'var(--c-fg-3)'}}>{trainerByType[sub].length}</span></div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:'var(--sp-3)'}}>
                    {trainerByType[sub].map(function(card){return <CardTile key={card.id} card={card}/>;}) }
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Energy */}
        {energy.length>0&&(
          <div style={{marginBottom:'var(--sp-6)'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:'var(--sp-5)',paddingBottom:'var(--sp-3)',borderBottom:'1px solid var(--c-line)'}}>
              <span style={{fontFamily:'var(--font-display)',fontWeight:900,fontSize:'var(--fs-24)',color:'var(--c-fg-1)'}}>Energy</span>
              <span style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-13)',color:'var(--c-fg-3)'}}>{energy.length}</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:'var(--sp-3)'}}>
              {energy.map(function(card){return <CardTile key={card.id} card={card}/>;}) }
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── DETAIL OVERLAY — slides over any screen ─────────────────────────────
  if(detail){
    var t0=detail.types&&detail.types[0];
    var accent=t0?typeColor(t0):'var(--c-water)';
    var accentBg=t0?typeBg(t0):'var(--c-water-bg)';
    var accentInk=t0?typeInk(t0):'var(--c-water-ink)';
    var inDk2=activeDeck?countInDeck(activeDeck,detail.id):0;
    var full=activeDeck?deckCount(activeDeck)>=60:false;
    var EE={Fire:'🔥',Water:'💧',Grass:'🌿',Lightning:'⚡',Psychic:'🔮',Fighting:'👊',Darkness:'🌑',Metal:'⚙️',Dragon:'🐲',Fairy:'✨',Colorless:'⬜',Free:'⬜'};
    return(
      <div style={{background:'var(--c-paper)',minHeight:'100vh',fontFamily:'var(--font-ui)'}}>
        {nav}
        <div style={{maxWidth:isMobile?560:1000,margin:'0 auto',padding:isMobile?'var(--sp-3)':'var(--sp-6) var(--sp-4)'}}>
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 320px',gap:'var(--sp-6)',alignItems:'start'}}>
        <div> {/* Left column */}

          {/* Breadcrumb nav */}
          <div style={{display:'flex',gap:8,marginBottom:'var(--sp-5)',alignItems:'center',flexWrap:'wrap'}}>
            <button onClick={popNav}
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:'var(--r-full)',background:'var(--c-mist)',border:'1px solid var(--c-line)',color:'var(--c-fg-2)',cursor:'pointer',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600}}>
              ← Back
            </button>
            <button onClick={goHome}
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:'var(--r-full)',background:'var(--c-mist)',border:'1px solid var(--c-line)',color:'var(--c-fg-2)',cursor:'pointer',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600}}>
              🏠 Home
            </button>
            {navStack.length>0&&(
              <div style={{display:'flex',alignItems:'center',gap:4,fontSize:'var(--fs-12)',color:'var(--c-fg-3)',flexWrap:'wrap'}}>
                {navStack.map(function(s,i){
                  var label=s.detail?s.detail.name:s.activeSet?s.activeSet.name:s.screen==='search'?('Search: '+lastQ):s.screen;
                  return(
                    <span key={i} style={{display:'inline-flex',alignItems:'center',gap:4}}>
                      {i>0&&<span>›</span>}
                      <button onClick={function(){
                        // Pop back to this specific stack position
                        setNavStack(navStack.slice(0,i));
                        setScreen(s.screen);setDetail(s.detail);setActiveSet(s.activeSet);
                        setTimeout(function(){window.scrollTo(0,s.scrollY||0);},50);
                      }} style={{background:'none',border:'none',color:'var(--c-fg-3)',cursor:'pointer',fontFamily:'var(--font-mono)',fontSize:'var(--fs-11)',padding:0,textDecoration:'underline'}}>
                        {label}
                      </button>
                    </span>
                  );
                })}
                <span>›</span>
                <span style={{color:'var(--c-fg-1)',fontWeight:600,fontFamily:'var(--font-mono)',fontSize:'var(--fs-11)'}}>{detail.name}</span>
              </div>
            )}
          </div>

          {/* Card image */}
          <div style={{textAlign:'center',marginBottom:'var(--sp-5)'}}>
            {detail.images&&detail.images.large&&(
              <img src={detail.images.large} alt={detail.name} style={{maxWidth:isMobile?'90%':'72%',borderRadius:'var(--r-xl)',boxShadow:'0 0 40px '+accent+'44'}}/>
            )}
          </div>

          {/* Add to deck bar */}
          <div style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',marginBottom:'var(--sp-4)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
            <div style={{fontSize:'var(--fs-13)',color:'var(--c-fg-2)'}}>
              {activeDeck
                ?<span><span style={{color:'var(--c-grass-ink)',fontWeight:700}}>{activeDeck.name}</span> · {deckCount(activeDeck)}/60 · {inDk2}/{maxCopies(detail)} copies</span>
                :<span style={{color:'var(--c-fire-ink)'}}>Select a deck to add cards</span>
              }
            </div>
            <div style={{display:'flex',gap:6,flexShrink:0}}>
              <button onClick={function(){addToDeck(detail);}}
                disabled={!activeDeck||inDk2>=maxCopies(detail)||full}
                style={{padding:'8px 18px',borderRadius:'var(--r-md)',fontSize:'var(--fs-13)',fontWeight:600,cursor:(!activeDeck||inDk2>=maxCopies(detail)||full)?'default':'pointer',border:'1px solid var(--c-grass)',background:(!activeDeck||inDk2>=maxCopies(detail)||full)?'transparent':'var(--c-grass-bg)',color:(!activeDeck||inDk2>=maxCopies(detail)||full)?'var(--c-fg-3)':'var(--c-grass-ink)',fontFamily:'var(--font-ui)'}}>
                {inDk2>=maxCopies(detail)?'At max copies':full?'Deck full':'Add to deck'}
              </button>
              {(function(){
                var wlP2=detail.tcgplayer&&detail.tcgplayer.prices?detail.tcgplayer.prices:{};
                var wlV2=Object.keys(wlP2);
                // On mobile show a select dropdown for variants
                if(wlV2.length>1){
                  return(
                    <select onChange={function(e){if(e.target.value)addToWishlist(detail,e.target.value);e.target.value='';}}
                      defaultValue=""
                      style={{padding:'7px 10px',borderRadius:'var(--r-md)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer',border:'1px solid var(--c-electric)',background:'var(--c-electric-bg)',color:'var(--c-electric-ink)',fontFamily:'var(--font-ui)',flexShrink:0}}>
                      <option value="" disabled>☆ Add variant</option>
                      {wlV2.map(function(vKey){
                        var vData=wlP2[vKey];
                        var vLabel=WISHLIST_VARIANT_LABELS[vKey]||vKey;
                        var market=vData&&(vData.market||vData.mid);
                        var inWl=isVariantInWishlist(detail.id,vKey);
                        return <option key={vKey} value={vKey}>{inWl?'⭐ ':'☆ '}{vLabel}{market?' — $'+market.toFixed(2):''}</option>;
                      })}
                    </select>
                  );
                }
                return(
                  <button onClick={function(){isInWishlist(detail.id)?removeFromWishlist(detail.id):addToWishlist(detail,wlV2[0]||null);}}
                    title={isInWishlist(detail.id)?'Remove from wishlist':'Add to wishlist'}
                    style={{padding:'8px 12px',borderRadius:'var(--r-md)',fontSize:18,cursor:'pointer',border:'1px solid '+(isInWishlist(detail.id)?'var(--c-electric)':'var(--c-line)'),background:isInWishlist(detail.id)?'var(--c-electric-bg)':'var(--c-mist)',color:isInWishlist(detail.id)?'var(--c-electric-ink)':'var(--c-fg-3)',flexShrink:0}}>
                    {isInWishlist(detail.id)?'⭐':'☆'}
                  </button>
                );
              })()}
            </div>
          </div>

          {/* Card header */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'var(--sp-4)'}}>
            <div>
              <h2 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-30)',color:'var(--c-fg-1)',marginBottom:8,letterSpacing:'var(--tr-tight)'}}>{detail.name}</h2>
              <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                {(detail.types||[]).map(function(t){return<TypeChip key={t} type={t}/>;} )}
                <span style={{padding:'2px 8px',borderRadius:'var(--r-full)',fontSize:'var(--fs-11)',fontWeight:600,background:'var(--c-mist)',border:'1px solid var(--c-line)',color:'var(--c-fg-2)'}}>{detail.supertype}{detail.subtypes&&detail.subtypes.length?' · '+detail.subtypes.join(', '):''}</span>
                <FormatBadge card={detail} fmt={format}/>
                {SET_REG_MARKS[(detail.set&&detail.set.id)]&&<span style={{padding:'2px 7px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-10)',fontWeight:700,background:'var(--c-dragon-bg)',color:'var(--c-dragon-ink)',border:'1px solid var(--c-dragon)',fontFamily:'var(--font-mono)'}}>Reg {SET_REG_MARKS[detail.set.id]}</span>}
              </div>
            </div>
            {detail.hp&&<div style={{textAlign:'right',flexShrink:0,marginLeft:16}}><div style={{fontFamily:'var(--font-display)',fontSize:'var(--fs-38)',fontWeight:900,color:accent,lineHeight:1}}>{detail.hp}</div><div style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-11)',color:'var(--c-fg-3)',letterSpacing:'var(--tr-mega)'}}>HP</div></div>}
          </div>

          {/* Abilities */}
          {(detail.abilities||[]).map(function(ab,i){return(
            <div key={i} style={{background:'var(--c-ghost-bg)',border:'1px solid var(--c-ghost)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',marginBottom:8}}>
              <div style={{fontFamily:'var(--font-ui)',fontSize:'var(--fs-10)',fontWeight:700,color:'var(--c-ghost-ink)',letterSpacing:'var(--tr-mega)',textTransform:'uppercase',marginBottom:4}}>{ab.type||'Ability'}</div>
              <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-16)',marginBottom:4}}>{ab.name}</div>
              {ab.text&&<div style={{fontSize:'var(--fs-14)',color:'var(--c-fg-2)',lineHeight:'var(--lh-normal)'}}>{ab.text}</div>}
            </div>
          );})}

          {/* Attacks */}
          {(detail.attacks||[]).map(function(atk,i){return(
            <div key={i} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',marginBottom:8}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{display:'flex',gap:3}}>{(atk.cost||[]).map(function(c,j){return<span key={j} style={{fontSize:16}}>{EE[c]||'⬜'}</span>;})}</div>
                  <span style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-16)'}}>{atk.name}</span>
                </div>
                {atk.damage&&<span style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-20)',color:accent}}>{atk.damage}</span>}
              </div>
              {atk.text&&<div style={{fontSize:'var(--fs-13)',color:'var(--c-fg-2)',lineHeight:'var(--lh-normal)'}}>{atk.text}</div>}
            </div>
          );})}

          {/* Rules */}
          {(detail.rules||[]).map(function(r,i){return(
            <div key={i} style={{background:accentBg,border:'1px solid '+accent+'44',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',marginBottom:8}}>
              <div style={{fontSize:'var(--fs-13)',color:accentInk,lineHeight:'var(--lh-loose)'}}>{r}</div>
            </div>
          );})}

          {/* Weakness / Resistance / Retreat */}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:8,marginBottom:'var(--sp-5)'}}>
            {(detail.weaknesses||[]).map(function(w,i){return<div key={i} style={{padding:'4px 12px',borderRadius:'var(--r-md)',fontSize:'var(--fs-13)',background:'var(--c-fire-bg)',border:'1px solid var(--c-fire)'}}><span style={{color:'var(--c-fire-ink)',fontSize:'var(--fs-11)',fontWeight:700}}>Weak </span>{w.type} {w.value}</div>;})}
            {(detail.resistances||[]).map(function(r,i){return<div key={i} style={{padding:'4px 12px',borderRadius:'var(--r-md)',fontSize:'var(--fs-13)',background:'var(--c-grass-bg)',border:'1px solid var(--c-grass)'}}><span style={{color:'var(--c-grass-ink)',fontSize:'var(--fs-11)',fontWeight:700}}>Resist </span>{r.type} {r.value}</div>;})}
            {detail.retreatCost&&detail.retreatCost.length>0&&<div style={{padding:'4px 12px',borderRadius:'var(--r-md)',fontSize:'var(--fs-13)',background:'var(--c-mist)',border:'1px solid var(--c-line)',color:'var(--c-fg-2)'}}>Retreat ×{detail.retreatCost.length}</div>}
          </div>

          {/* Footer */}
          <div style={{paddingTop:'var(--sp-4)',borderTop:'1px solid var(--c-line)',display:'flex',justifyContent:'space-between',fontSize:'var(--fs-12)',color:'var(--c-fg-3)',fontFamily:'var(--font-mono)'}}>
            <span>{detail.set&&detail.set.name} {detail.set&&detail.set.releaseDate&&'('+detail.set.releaseDate.slice(0,4)+')'}</span>
            <span>#{detail.number}{detail.rarity&&' · '+detail.rarity}</span>
          </div>
          {detail.artist&&<div style={{textAlign:'center',fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginTop:4,fontFamily:'var(--font-ui)'}}>Art by {detail.artist}</div>}
          {detail.flavorText&&<div style={{fontStyle:'italic',fontSize:'var(--fs-13)',color:'var(--c-fg-3)',textAlign:'center',marginTop:'var(--sp-3)',lineHeight:'var(--lh-loose)'}}>{detail.flavorText}</div>}
        </div> {/* end left column */}

        {/* Right column — price panel */}
        <div style={{display:'flex',flexDirection:'column',gap:'var(--sp-4)'}}>
          <PricePanel card={detail}/>

          {/* Add to deck — also in right column on desktop */}
          {!isMobile&&(
            <div style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-4)'}}>
              <p className="eyebrow" style={{marginBottom:'var(--sp-2)',color:'var(--c-water-ink)'}}>Add to deck</p>
              <div style={{fontSize:'var(--fs-13)',color:'var(--c-fg-2)',marginBottom:'var(--sp-3)'}}>
                {activeDeck
                  ?<span><span style={{color:'var(--c-grass-ink)',fontWeight:700}}>{activeDeck.name}</span><br/>{deckCount(activeDeck)}/60 · {countInDeck(activeDeck,detail.id)}/{maxCopies(detail)} copies</span>
                  :<span style={{color:'var(--c-fg-3)'}}>Select a deck in the Build tab to add cards.</span>
                }
              </div>
              <button onClick={function(){addToDeck(detail);}}
                disabled={!activeDeck||countInDeck(activeDeck,detail.id)>=maxCopies(detail)||deckCount(activeDeck)>=60}
                style={{width:'100%',padding:'10px',borderRadius:'var(--r-md)',fontSize:'var(--fs-14)',fontWeight:700,cursor:(!activeDeck||countInDeck(activeDeck,detail.id)>=maxCopies(detail)||deckCount(activeDeck)>=60)?'default':'pointer',border:'1px solid var(--c-grass)',background:(!activeDeck||countInDeck(activeDeck,detail.id)>=maxCopies(detail)||deckCount(activeDeck)>=60)?'transparent':'var(--c-grass-bg)',color:(!activeDeck||countInDeck(activeDeck,detail.id)>=maxCopies(detail)||deckCount(activeDeck)>=60)?'var(--c-fg-3)':'var(--c-grass-ink)',fontFamily:'var(--font-ui)'}}>
                {!activeDeck?'No deck selected':countInDeck(activeDeck,detail.id)>=maxCopies(detail)?'At max copies':deckCount(activeDeck)>=60?'Deck full':'+ Add to deck'}
              </button>
            </div>
          )}

          {/* Collection buttons — with variant picker */}
          {collections.length>0&&(function(){
            var tcgPrices = detail.tcgplayer && detail.tcgplayer.prices ? detail.tcgplayer.prices : {};
            var variants = Object.keys(tcgPrices);
            // If no TCGPlayer variants, offer one generic entry
            var showVariants = variants.length > 1;
            return(
              <div style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)'}}>
                <p className="eyebrow" style={{marginBottom:'var(--sp-2)',color:'var(--c-dragon-ink)'}}>Add to collection</p>
                {/* Variant selector */}
                {showVariants&&(
                  <div style={{marginBottom:'var(--sp-3)'}}>
                    <div style={{fontSize:'var(--fs-11)',fontWeight:600,color:'var(--c-fg-3)',marginBottom:6,letterSpacing:'var(--tr-wide)',textTransform:'uppercase'}}>Select variant</div>
                    <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                      {variants.map(function(vKey){
                        var vData=tcgPrices[vKey];
                        var vLabel=VARIANT_LABELS_COL[vKey]||vKey;
                        var market=vData&&(vData.market||vData.mid);
                        return(
                          <div key={vKey} style={{border:'1px solid var(--c-line)',borderRadius:'var(--r-md)',padding:'var(--sp-2) var(--sp-3)',background:'var(--c-mist)',marginBottom:4}}>
                            <div style={{fontWeight:700,fontSize:'var(--fs-12)',color:'var(--c-fg-1)',marginBottom:2}}>{vLabel}</div>
                            {market&&<div style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-11)',color:'var(--c-electric-ink)',marginBottom:6}}>${market.toFixed(2)}</div>}
                            <div style={{display:'flex',flexDirection:'column',gap:4}}>
                              {collections.map(function(col){
                                var inCol=isVariantInCollection(col.id,detail.id,vKey);
                                return(
                                  <button key={col.id} onClick={function(){inCol?removeCardFromCollection(col.id,detail.id+'--'+vKey):addCardToCollection(col.id,detail,null,vKey);}}
                                    style={{display:'flex',alignItems:'center',gap:6,padding:'4px 8px',borderRadius:'var(--r-sm)',border:'1px solid '+(inCol?col.color:'var(--c-line)'),background:inCol?'color-mix(in srgb, '+col.color+' 12%, transparent)':'transparent',color:inCol?col.color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-11)',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
                                    <span style={{width:7,height:7,borderRadius:'50%',background:col.color,flexShrink:0}}/>
                                    <span style={{overflow:'hidden',textOverflow:'ellipsis',maxWidth:80,whiteSpace:'nowrap'}}>{col.name}</span>
                                    <span style={{marginLeft:'auto',opacity:0.7}}>{inCol?'✓':'+' }</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Single variant — just show collection list */}
                {!showVariants&&(
                  <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:'var(--sp-2)'}}>
                    {collections.map(function(col){
                      var inCol=isInCollection(col.id,detail.id);
                      return(
                        <button key={col.id} onClick={function(){inCol?removeCardFromCollection(col.id,detail.id):addCardToCollection(col.id,detail,null,variants[0]||null);}}
                          style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',borderRadius:'var(--r-md)',border:'1px solid '+(inCol?col.color:'var(--c-line)'),background:inCol?'color-mix(in srgb, '+col.color+' 12%, transparent)':'transparent',color:inCol?col.color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer',textAlign:'left',width:'100%'}}>
                          <span style={{width:10,height:10,borderRadius:'50%',background:col.color,flexShrink:0}}/>
                          <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{col.name}</span>
                          <span style={{fontSize:12,opacity:0.7}}>{inCol?'✓':'+' }</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {/* Add note */}
                {getCardCollections(detail.id).length>0&&(
                  <button onClick={function(){var col=getCardCollections(detail.id)[0];var cardEntry=col.cards.find(function(e){return e.cardId===detail.id||e.id===detail.id;});setCardNoteModal({collectionId:col.id,cardId:(cardEntry&&cardEntry.id)||detail.id,note:(cardEntry&&cardEntry.note)||'',cardName:detail.name});}}
                    style={{marginTop:6,width:'100%',padding:'6px',borderRadius:'var(--r-md)',border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-3)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-12)',fontWeight:600,cursor:'pointer'}}>
                    📝 Add note
                  </button>
                )}
              </div>
            );
          })()}
          {collections.length===0&&(
            <button onClick={function(){setScreen('collections');setShowCollectionModal(true);}}
              style={{width:'100%',padding:'9px',borderRadius:'var(--r-md)',border:'1px solid var(--c-line)',background:'var(--c-mist)',color:'var(--c-fg-3)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer'}}>
              📦 Create a collection
            </button>
          )}

          {/* Wishlist — variant picker */}
          {(function(){
            var wlPrices = detail.tcgplayer&&detail.tcgplayer.prices?detail.tcgplayer.prices:{};
            var wlVariants = Object.keys(wlPrices);
            var hasVariants = wlVariants.length>1;
            return(
              <div style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)'}}>
                <p className="eyebrow" style={{marginBottom:'var(--sp-2)',color:'var(--c-electric-ink)'}}>Add to wishlist</p>
                {hasVariants?(
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {wlVariants.map(function(vKey){
                      var vData=wlPrices[vKey];
                      var vLabel=WISHLIST_VARIANT_LABELS[vKey]||vKey;
                      var market=vData&&(vData.market||vData.mid);
                      var inWl=isVariantInWishlist(detail.id,vKey);
                      return(
                        <button key={vKey}
                          onClick={function(){inWl?removeFromWishlist(detail.id+'--'+vKey):addToWishlist(detail,vKey);}}
                          style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 12px',borderRadius:'var(--r-md)',border:'1px solid '+(inWl?'var(--c-electric)':'var(--c-line)'),background:inWl?'var(--c-electric-bg)':'var(--c-mist)',color:inWl?'var(--c-electric-ink)':'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer',transition:'all var(--dur-1) var(--ease-smooth)',width:'100%'}}>
                          <span style={{display:'flex',alignItems:'center',gap:8}}>
                            <span>{inWl?'⭐':'☆'}</span>
                            <span>{vLabel}</span>
                          </span>
                          {market&&<span style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-12)',color:inWl?'var(--c-electric-ink)':'var(--c-fg-3)'}}>${market.toFixed(2)}</span>}
                        </button>
                      );
                    })}
                  </div>
                ):(
                  <button onClick={function(){isInWishlist(detail.id)?removeFromWishlist(detail.id):addToWishlist(detail,wlVariants[0]||null);}}
                    style={{width:'100%',padding:'10px',borderRadius:'var(--r-md)',fontSize:'var(--fs-14)',fontWeight:700,cursor:'pointer',border:'1px solid '+(isInWishlist(detail.id)?'var(--c-electric)':'var(--c-line)'),background:isInWishlist(detail.id)?'var(--c-electric-bg)':'var(--c-mist)',color:isInWishlist(detail.id)?'var(--c-electric-ink)':'var(--c-fg-2)',fontFamily:'var(--font-ui)',transition:'all var(--dur-1) var(--ease-smooth)'}}>
                    {isInWishlist(detail.id)?'⭐ In wishlist — remove':'☆ Add to wishlist'}
                  </button>
                )}
              </div>
            );
          })()}
        </div>
        </div> {/* end grid */}
        </div>
        <Toast data={toast}/>
      </div>
    );
  }

  // ── SEARCH SCREEN ────────────────────────────────────────────────────────
  if(screen==='search') return(
    <div style={{background:'var(--c-paper)',minHeight:'100vh',fontFamily:'var(--font-ui)'}}>
      {nav}
      <div style={{maxWidth:1100,margin:'0 auto',padding:'var(--sp-6) var(--sp-4)'}}>
        <div style={{marginBottom:'var(--sp-5)'}}>
          <p className="eyebrow" style={{marginBottom:4}}>Search results</p>
          <h2 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-30)',color:'var(--c-fg-1)',letterSpacing:'var(--tr-tight)'}}>{lastQ}</h2>
          {!globalLoading&&globalCards.length>0&&<p style={{color:'var(--c-fg-3)',fontSize:'var(--fs-13)',marginTop:4}}>{globalCards.length} printings found</p>}
        </div>
        {globalLoading&&<div style={{textAlign:'center',padding:80,color:'var(--c-fg-3)'}}>Loading…</div>}
        {!globalLoading&&globalCards.length===0&&lastQ&&<MascotEmpty variant="c" title={'No results for "'+lastQ+'"'} body="Try a different name or check the spelling."/>}
        {!globalLoading&&globalCards.length>0&&<CardGrid list={globalCards}/>}
      </div>
      <Toast data={toast}/>
      {GlobalAuthModal}
    </div>
  );

  // ── DECK BUILDER ─────────────────────────────────────────────────────────
  if(screen==='deckbuilder') return(
    <div style={{background:'var(--c-paper)',minHeight:'100vh',fontFamily:'var(--font-ui)'}}>
      {nav}
      <div style={{maxWidth:1100,margin:'0 auto',padding:isMobile?'var(--sp-4) var(--sp-3)':'var(--sp-6) var(--sp-4)',display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 340px',gap:'var(--sp-5)'}}>
        <div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'var(--sp-5)',flexWrap:'wrap',gap:'var(--sp-3)'}}>
            <div>
              <p className="eyebrow" style={{marginBottom:4}}>Deck builder</p>
              <h2 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-30)',letterSpacing:'var(--tr-tight)',color:'var(--c-fg-1)'}}>Your decks</h2>
            </div>
            <div style={{paddingTop:4}}>
              <FormatSelector format={format} onChange={setFormat}/>
              <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginTop:6,maxWidth:280}}>{FORMATS[format].desc}</div>
            </div>
          </div>

          {/* Create deck */}
          <div style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-4)',marginBottom:'var(--sp-5)'}}>
            <p className="eyebrow" style={{marginBottom:'var(--sp-3)',color:'var(--c-water-ink)'}}>New deck</p>
            <div style={{display:'flex',gap:8}}>
              <input value={newDeckName} onChange={function(e){setNewDeckName(e.target.value);}} onKeyDown={function(e){if(e.key==='Enter')createDeck();}}
                placeholder="Deck name…"
                style={{flex:1,background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-md)',padding:'8px 12px',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',color:'var(--c-fg-1)',outline:'none'}}/>
              <button onClick={createDeck} style={{padding:'8px 18px',borderRadius:'var(--r-md)',background:'var(--c-water)',color:'#fff',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600,border:'none',cursor:'pointer'}}>Create</button>
            </div>
          </div>

          {decks.length===0&&<MascotEmpty variant="a" title="No decks yet" body="Create one above, then browse cards to add them."/>}
          {decks.map(function(deck,idx){
            var cnt=deckCount(deck),isActive=activeDeckIdx===idx,issues=validateDeck(deck,format);
            return(
              <div key={idx} style={{background:isActive?'var(--c-water-bg)':'var(--c-bg-elevated)',border:'1px solid '+(isActive?'var(--c-water)':'var(--c-line)'),borderRadius:'var(--r-lg)',padding:'var(--sp-4)',marginBottom:'var(--sp-3)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div>
                    <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-18)',color:'var(--c-fg-1)'}}>{deck.name}</div>
                    <div style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginTop:2}}>{cnt}/60 cards</div>
                  </div>
                  <div style={{display:'flex',gap:6}}>
                    <button onClick={function(){setActiveDeckIdx(idx);showToast('Editing: '+deck.name,'info');}}
                      style={{padding:'6px 12px',borderRadius:'var(--r-md)',fontSize:'var(--fs-12)',fontWeight:600,cursor:'pointer',border:'1px solid '+(isActive?'var(--c-grass)':'var(--c-line)'),background:isActive?'var(--c-grass-bg)':'var(--c-mist)',color:isActive?'var(--c-grass-ink)':'var(--c-fg-2)',fontFamily:'var(--font-ui)'}}>
                      {isActive?'Active':'Select'}
                    </button>
                    <button onClick={function(){deleteDeck(idx);}}
                      style={{padding:'6px 10px',borderRadius:'var(--r-md)',fontSize:'var(--fs-12)',cursor:'pointer',border:'1px solid var(--c-fire)',background:'var(--c-fire-bg)',color:'var(--c-fire-ink)',fontFamily:'var(--font-ui)'}}>
                      Delete
                    </button>
                  </div>
                </div>
                <div style={{background:'var(--c-mist-2)',borderRadius:'var(--r-full)',height:6,marginBottom:8,overflow:'hidden'}}>
                  <div style={{background:cnt===60?'var(--c-grass)':'var(--c-water)',width:Math.min(100,(cnt/60)*100)+'%',height:'100%',borderRadius:'var(--r-full)',transition:'width .3s var(--ease-smooth)'}}/>
                </div>
                {issues.length>0?<div style={{fontSize:'var(--fs-12)',color:'var(--c-fire-ink)'}}>{issues[0]}</div>:<div style={{fontSize:'var(--fs-12)',color:'var(--c-grass-ink)'}}>{cnt===60?'Legal deck — ready to play!':''}</div>}
                {deck.cards.length>0&&<div style={{marginTop:8,display:'flex',flexWrap:'wrap',gap:4}}>{deck.cards.slice(0,10).map(function(e){return<span key={e.card.id} style={{fontSize:'var(--fs-11)',background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-sm)',padding:'1px 6px',color:'var(--c-fg-3)'}}>{e.qty}× {e.card.name}</span>;})}{deck.cards.length>10&&<span style={{fontSize:'var(--fs-11)',color:'var(--c-fg-3)',padding:'1px 6px'}}>+{deck.cards.length-10} more</span>}</div>}
              </div>
            );
          })}
        </div>

        {/* Deck sidebar */}
        {activeDeck&&(
          <div style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-4)',height:'fit-content',position:isMobile?'static':'sticky',top:70}}>
            <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-18)',color:'var(--c-fg-1)',marginBottom:4}}>{activeDeck.name}</div>
            <div style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginBottom:'var(--sp-4)'}}>{deckCount(activeDeck)}/60 cards</div>
            {activeDeck.cards.length===0&&<div style={{color:'var(--c-fg-3)',fontSize:'var(--fs-13)',textAlign:'center',padding:'var(--sp-5)'}}>Browse cards to add them</div>}
            {['Pokemon','Pokémon','Trainer','Energy'].map(function(st){
              var group=activeDeck.cards.filter(function(e){return e.card.supertype===st;});
              if(!group.length) return null;
              var gc=group.reduce(function(s,e){return s+e.qty;},0);
              var label=st==='Pokémon'?'Pokémon':st;
              return(
                <div key={st} style={{marginBottom:'var(--sp-4)'}}>
                  <p className="eyebrow" style={{marginBottom:8}}>{label} ({gc})</p>
                  {group.map(function(entry){return(
                    <div key={entry.card.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'4px 0',borderBottom:'1px solid var(--c-line)'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <span style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-12)',color:'var(--c-electric-ink)',fontWeight:700,width:20}}>{entry.qty}×</span>
                        {entry.card.images&&entry.card.images.small&&<img src={entry.card.images.small} alt={entry.card.name} style={{height:28,borderRadius:'var(--r-sm)'}}/>}
                        <span style={{fontSize:'var(--fs-13)',color:'var(--c-fg-1)'}}>{entry.card.name}</span>
                      </div>
                      <button onClick={function(){removeFromDeck(entry.card.id);}}
                        style={{background:'transparent',border:'none',color:'var(--c-fire-ink)',cursor:'pointer',fontSize:18,padding:'0 4px',lineHeight:1}}>−</button>
                    </div>
                  );})}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Toast data={toast}/>
      {GlobalAuthModal}
    </div>
  );

  // ── MY DECKS + WORLDS ─────────────────────────────────────────────────────
  if(screen==='decklist') return(
    <div style={{background:'var(--c-paper)',minHeight:'100vh',fontFamily:'var(--font-ui)'}}>
      {nav}
      <div style={{maxWidth:900,margin:'0 auto',padding:isMobile?'var(--sp-3)':'var(--sp-6) var(--sp-4)'}}>

        {/* ── Subtab bar ── */}
        <div style={{display:'flex',gap:4,padding:4,background:'var(--c-mist)',borderRadius:'var(--r-xl)',border:'1px solid var(--c-line)',marginBottom:'var(--sp-6)',width:'fit-content'}}>
          {[
            {id:'meta',  label:'Meta decks',  icon:'🏆'},
            {id:'mine',  label:'My decks',    icon:'🃏'},
            {id:'worlds',label:'Hall of fame', icon:'⭐'},
          ].map(function(t){
            var active = deckTab===t.id;
            return(
              <button key={t.id} onClick={function(){setDeckTab(t.id);}}
                style={{display:'flex',alignItems:'center',gap:6,padding:isMobile?'8px 12px':'9px 18px',borderRadius:'var(--r-lg)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:700,border:'none',cursor:'pointer',color:active?'var(--c-paper)':'var(--c-fg-2)',background:active?'var(--c-ink)':'transparent',transition:'all var(--dur-1) var(--ease-smooth)',whiteSpace:'nowrap'}}>
                <span>{t.icon}</span>
                {(!isMobile||active)&&<span>{t.label}</span>}
              </button>
            );
          })}
        </div>

        {/* ── STANDARD META ── */}
        {deckTab==='meta'&&<div style={{marginBottom:'var(--sp-8)'}}>
          <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',marginBottom:'var(--sp-5)',flexWrap:'wrap',gap:'var(--sp-3)'}}>
            <div>
              <p className="eyebrow" style={{marginBottom:4,color:'var(--c-electric-ink)'}}>Standard · SVI–ASC · Season 2026</p>
              <h2 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-30)',letterSpacing:'var(--tr-tight)',color:'var(--c-fg-1)'}}>Top 10 meta decks</h2>
            </div>
            <a href="https://limitlesstcg.com/decks" target="_blank" rel="noreferrer"
              style={{fontSize:'var(--fs-12)',color:'var(--c-water-ink)',textDecoration:'none',padding:'4px 10px',borderRadius:'var(--r-sm)',border:'1px solid var(--c-water)',background:'var(--c-water-bg)'}}>
              Data: Limitless TCG →
            </a>
          </div>
          {[
            {rank:1,  name:'Dragapult ex',          type:'Psychic',  tier:'S', share:18.49, pts:1260, regTop8:168, regWins:23, intTop8:37, intWins:3,  trend:'stable',  desc:"Phantom Dive spreads damage counters across the bench while Phantom Force makes it near-unblockable. Dominant in every format since Twilight Masquerade. Multiple variants: LZ Box, Pidgeot, Dusknoir, Iron Thorns.", bestFinish:'Multiple Regional wins 2026', url:'https://limitlesstcg.com/decks/284'},
            {rank:2,  name:"Marnie's Grimmsnarl ex", type:'Darkness', tier:'S', share:13.60, pts:927,  regTop8:110, regWins:15, intTop8:20, intWins:2,  trend:'up',      desc:"New powerhouse from Mega Evolution era. Marnie's Grimmsnarl ex hits for massive damage and provides draw support. Rising fast since ASC release.", bestFinish:'Regional wins post-rotation', url:'https://limitlesstcg.com/decks/329'},
            {rank:3,  name:'Gardevoir ex',           type:'Psychic',  tier:'S', share:11.48, pts:782,  regTop8:95,  regWins:12, intTop8:18, intWins:1,  trend:'stable',  desc:"Psychic Embrace attaches energy from discard. Mega Diancie, Jellicent ex, and Clefairy ex added strong new attackers in 2025. Notoriously difficult to pilot but extremely rewarding.", bestFinish:'1st place Champions League Japan', url:'https://limitlesstcg.com/decks/255'},
            {rank:4,  name:"N's Zoroark ex",         type:'Darkness', tier:'A', share:9.66,  pts:658,  regTop8:78,  regWins:9,  intTop8:12, intWins:1,  trend:'up',      desc:"Coin Bonus and Run Away Draw build a massive hand of Basic Energy. Dudunsparce provides additional draw. Gholdengo ex as a flexible win condition alongside Zoroark.", bestFinish:'Top 8 multiple Regionals', url:'https://limitlesstcg.com/decks/320'},
            {rank:5,  name:'Gholdengo ex',           type:'Steel',    tier:'A', share:9.35,  pts:637,  regTop8:74,  regWins:8,  intTop8:11, intWins:1,  trend:'stable',  desc:"Make It Rain OHKOs anything on the board. Lunatone for draw support, Genesect V for search. Has proven itself as a consistent top contender since 2023.", bestFinish:'Top 4 Regional 2026', url:'https://limitlesstcg.com/decks/267'},
            {rank:6,  name:'Mega Absol Box',         type:'Darkness', tier:'A', share:5.90,  pts:402,  regTop8:48,  regWins:5,  intTop8:8,  intWins:0,  trend:'up',      desc:"New archetype from Mega Evolution. Pairs Mega Absol ex with disruption tools for a strong control-aggro hybrid. Punishes slower setup strategies hard.", bestFinish:'Top 8 Regional 2026', url:'https://limitlesstcg.com/decks/344'},
            {rank:7,  name:'Froslass Munkidori',     type:'Water',    tier:'A', share:5.50,  pts:375,  regTop8:44,  regWins:4,  intTop8:7,  intWins:0,  trend:'stable',  desc:"Spreading damage via Froslass while Munkidori's Adrena Brain relocates damage counters for surprise KOs. Very technical to play, high ceiling.", bestFinish:'Top 8 Regional 2026', url:'https://limitlesstcg.com/decks/288'},
            {rank:8,  name:'Charizard ex',           type:'Fire',     tier:'B', share:4.59,  pts:313,  regTop8:38,  regWins:4,  intTop8:6,  intWins:0,  trend:'down',    desc:"Infernal Reign attaches Fire energy from discard. Pidgeot ex Quick Search finds any card. Tier-zero in 2024, now a solid B-tier pick post-rotation losing key G-mark support.", bestFinish:'Top 8 Regional 2026', url:'https://limitlesstcg.com/decks/264'},
            {rank:9,  name:'Crustle Mysterious Rock', type:'Fighting', tier:'B', share:4.20,  pts:286,  regTop8:34,  regWins:3,  intTop8:5,  intWins:0,  trend:'up',      desc:"Mysterious Rock Inn stadium + Crustle creates a lock that prevents opponents from retreating. Control-oriented strategy that punishes active-reliant decks.", bestFinish:'Top 8 Regional 2026', url:'https://limitlesstcg.com/decks/341'},
            {rank:10, name:'Raging Bolt ex / Joltik Box', type:'Lightning', tier:'B', share:3.98, pts:271, regTop8:32, regWins:3, intTop8:5, intWins:0, trend:'stable', desc:"Tied for 10th. Raging Bolt hits for massive damage with Frenzied Gouging. Joltik Box uses energy acceleration via Joltik's Jolting Charge for a Lightning toolbox of attackers.", bestFinish:'Top 16 Regional 2026', url:'https://limitlesstcg.com/decks/280'},
          ].map(function(deck,i){
            var tierColor = deck.tier==='S'?'var(--c-electric)':deck.tier==='A'?'var(--c-grass)':'var(--c-water)';
            var typeColor2 = typeColor(deck.type);
            var typeBg2 = typeBg(deck.type);
            var trendIcon = deck.trend==='up'?'↑':deck.trend==='down'?'↓':'→';
            var trendColor = deck.trend==='up'?'var(--c-grass-ink)':deck.trend==='down'?'var(--c-fire-ink)':'var(--c-fg-3)';
            var winRate = deck.regTop8>0?Math.round((deck.regWins/deck.regTop8)*100):0;
            return(
              <div key={deck.rank} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)',overflow:'hidden',marginBottom:'var(--sp-3)',transition:'border-color var(--dur-1) var(--ease-smooth)'}}>
                {/* Type color band */}
                <div style={{height:4,background:typeColor2}}/>
                <div style={{padding:isMobile?'var(--sp-3)':'var(--sp-4) var(--sp-5)'}}>
                  <div style={{display:'flex',alignItems:'flex-start',gap:'var(--sp-3)',flexWrap:'wrap'}}>
                    {/* Rank + Tier */}
                    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,flexShrink:0}}>
                      <div style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-24)',color:'var(--c-fg-3)',lineHeight:1,width:36,textAlign:'center'}}>#{deck.rank}</div>
                      <div style={{padding:'2px 8px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-11)',fontWeight:900,background:'color-mix(in srgb, '+tierColor+' 15%, transparent)',color:tierColor,border:'1px solid color-mix(in srgb, '+tierColor+' 40%, transparent)',letterSpacing:'0.05em'}}>
                        {deck.tier}
                      </div>
                    </div>

                    {/* Main info */}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:6}}>
                        <span style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:isMobile?'var(--fs-16)':'var(--fs-20)',color:'var(--c-fg-1)',lineHeight:'var(--lh-snug)'}}>{deck.name}</span>
                        <TypeChip type={deck.type}/>
                        <span style={{fontSize:'var(--fs-13)',color:trendColor,fontWeight:700}}>{trendIcon} {deck.trend}</span>
                      </div>
                      <p style={{fontSize:'var(--fs-13)',color:'var(--c-fg-2)',lineHeight:'var(--lh-normal)',marginBottom:'var(--sp-3)'}}>{deck.desc}</p>
                      <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',fontStyle:'italic'}}>Best finish: {deck.bestFinish}</div>
                    </div>

                    {/* Stats block */}
                    <div style={{flexShrink:0,display:'flex',flexDirection:'column',gap:'var(--sp-2)',minWidth:isMobile?'100%':160}}>
                      {/* Meta share bar */}
                      <div>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:'var(--fs-11)',fontWeight:600,color:'var(--c-fg-3)',marginBottom:4}}>
                          <span>Meta share</span>
                          <span style={{fontFamily:'var(--font-mono)',color:'var(--c-fg-1)'}}>{deck.share}%</span>
                        </div>
                        <div style={{background:'var(--c-mist-2)',borderRadius:'var(--r-full)',height:6,overflow:'hidden'}}>
                          <div style={{height:'100%',borderRadius:'var(--r-full)',background:typeColor2,width:Math.min(100,(deck.share/20)*100)+'%',transition:'width .5s var(--ease-pop)'}}/>
                        </div>
                      </div>
                      {/* Stats grid */}
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                        {[
                          {label:'Pts',    value:deck.pts.toLocaleString(), color:'var(--c-electric-ink)'},
                          {label:'Win%',   value:winRate+'%',               color:winRate>=20?'var(--c-grass-ink)':'var(--c-fg-2)'},
                          {label:'Reg T8', value:deck.regTop8,              color:'var(--c-fg-2)'},
                          {label:'Reg W',  value:deck.regWins,              color:'var(--c-grass-ink)'},
                          {label:'Int T8', value:deck.intTop8,              color:'var(--c-fg-2)'},
                          {label:'Int W',  value:deck.intWins,              color:deck.intWins>0?'var(--c-grass-ink)':'var(--c-fg-3)'},
                        ].map(function(s){return(
                          <div key={s.label} style={{background:'var(--c-mist)',borderRadius:'var(--r-md)',padding:'6px 8px',textAlign:'center'}}>
                            <div style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-16)',color:s.color,lineHeight:1}}>{s.value}</div>
                            <div style={{fontSize:'var(--fs-10)',fontWeight:700,color:'var(--c-fg-3)',letterSpacing:'0.06em',textTransform:'uppercase',marginTop:2}}>{s.label}</div>
                          </div>
                        );})}
                      </div>
                      <a href={deck.url} target="_blank" rel="noreferrer"
                        style={{display:'block',textAlign:'center',padding:'7px',borderRadius:'var(--r-md)',fontSize:'var(--fs-12)',fontWeight:600,border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-2)',textDecoration:'none'}}>
                        Full stats →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <p style={{fontSize:'var(--fs-11)',color:'var(--c-fg-3)',textAlign:'center',marginTop:'var(--sp-3)'}}>
            Data sourced from <a href="https://limitlesstcg.com/decks" target="_blank" rel="noreferrer" style={{color:'var(--c-water-ink)'}}>Limitless TCG</a> · Standard format SVI–ASC · 2025–2026 season · Updated May 2026
          </p>
        </div>}

        {/* My Decks */}
        {deckTab==='mine'&&<div>
        <p className="eyebrow" style={{marginBottom:4}}>Your collection</p>
        <h2 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-30)',letterSpacing:'var(--tr-tight)',color:'var(--c-fg-1)',marginBottom:'var(--sp-5)'}}>My decks</h2>
        {decks.length===0&&(
          <div style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)',marginBottom:'var(--sp-7)'}}>
            <MascotEmpty variant="b" title="No decks yet" body="Build your first deck and start battling."/>
            <div style={{paddingBottom:'var(--sp-5)',textAlign:'center'}}>
              <button onClick={function(){setScreen('deckbuilder');}} style={{padding:'10px 20px',borderRadius:'var(--r-md)',background:'var(--c-water)',color:'#fff',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',fontWeight:600,border:'none',cursor:'pointer'}}>Build your first deck</button>
            </div>
          </div>
        )}
        {decks.map(function(deck,idx){
          var cnt=deckCount(deck),issues=validateDeck(deck,format),canPlay=issues.length===0&&cnt===60;
          var pokemon=deck.cards.filter(function(e){return e.card.supertype==='Pokemon'||e.card.supertype==='Pokémon';});
          var trainers=deck.cards.filter(function(e){return e.card.supertype==='Trainer';});
          var energies=deck.cards.filter(function(e){return e.card.supertype==='Energy';});
          return(
            <div key={idx} style={{
                background: canPlay ? 'color-mix(in srgb, var(--c-grass) 6%, var(--c-bg-elevated))' : 'var(--c-bg-elevated)',
                border: '1px solid ' + (canPlay ? 'var(--c-grass)' : 'var(--c-line)'),
                borderRadius: 'var(--r-xl)', padding: 'var(--sp-5)', marginBottom: 'var(--sp-4)',
                boxShadow: canPlay ? '0 0 0 3px color-mix(in srgb, var(--c-grass) 12%, transparent)' : 'none',
                transition: 'all var(--dur-2) var(--ease-smooth)',
                position: 'relative', overflow: 'hidden',
              }}>
              {/* Animated shimmer strip when complete */}
              {canPlay && <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:'linear-gradient(90deg, var(--c-grass) 0%, var(--c-electric) 50%, var(--c-grass) 100%)',backgroundSize:'200% 100%',animation:'shimmer 2s linear infinite'}}/>}
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12,marginTop:canPlay?4:0}}>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-20)',color:'var(--c-fg-1)'}}>{deck.name}</div>
                    {canPlay && <span style={{fontSize:'var(--fs-10)',fontWeight:800,color:'var(--c-grass-ink)',background:'var(--c-grass-bg)',border:'1px solid var(--c-grass)',borderRadius:'var(--r-full)',padding:'2px 8px',letterSpacing:'0.05em',whiteSpace:'nowrap'}}>✓ LEGAL</span>}
                  </div>
                  <div style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginTop:2}}>
                    {cnt}/60 · {pokemon.reduce(function(s,e){return s+e.qty;},0)} Pokémon · {trainers.reduce(function(s,e){return s+e.qty;},0)} Trainers · {energies.reduce(function(s,e){return s+e.qty;},0)} Energy
                  </div>
                </div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'flex-end'}}>
                  <button onClick={function(){setActiveDeckIdx(idx);setScreen('deckbuilder');}} style={{padding:'7px 14px',borderRadius:'var(--r-md)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer',border:'1px solid var(--c-water)',background:'var(--c-water-bg)',color:'var(--c-water-ink)',fontFamily:'var(--font-ui)'}}>Edit</button>
                  {canPlay&&<button onClick={function(){handleSaveDeck(deck);}} style={{padding:'7px 14px',borderRadius:'var(--r-md)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer',border:'1px solid var(--c-dragon)',background:'var(--c-dragon-bg)',color:'var(--c-dragon-ink)',fontFamily:'var(--font-ui)'}}>{session?'☁️ Save':'☁️ Sign in to save'}</button>}
                  <button onClick={function(){if(!canPlay){showToast('Deck needs 60 cards and at least one Pokémon','warning');return;}setBattleDeck(deck);}}
                    style={{
                      padding: canPlay ? '8px 20px' : '7px 14px',
                      borderRadius: 'var(--r-md)', fontSize: 'var(--fs-13)',
                      fontWeight: canPlay ? 800 : 600,
                      cursor: canPlay ? 'pointer' : 'default',
                      border: '1px solid ' + (canPlay ? 'var(--c-grass)' : 'var(--c-line)'),
                      background: canPlay ? 'var(--c-grass)' : 'transparent',
                      color: canPlay ? '#fff' : 'var(--c-fg-3)',
                      fontFamily: 'var(--font-ui)',
                      boxShadow: canPlay ? '0 4px 16px color-mix(in srgb, var(--c-grass) 35%, transparent)' : 'none',
                      transform: canPlay ? 'scale(1.04)' : 'none',
                      transition: 'all var(--dur-1) var(--ease-pop)',
                    }}>
                    ⚔️ {canPlay ? 'Playtest now!' : 'Playtest'}
                  </button>
                  <button onClick={function(){deleteDeck(idx);}} style={{padding:'7px 12px',borderRadius:'var(--r-md)',fontSize:'var(--fs-13)',cursor:'pointer',border:'1px solid var(--c-fire)',background:'var(--c-fire-bg)',color:'var(--c-fire-ink)',fontFamily:'var(--font-ui)'}}>Delete</button>
                </div>
              </div>
              <div style={{background:'var(--c-mist-2)',borderRadius:'var(--r-full)',height:canPlay?8:6,marginBottom:8,overflow:'hidden',transition:'height var(--dur-2)'}}>
                <div style={{background:canPlay?'var(--c-grass)':'var(--c-water)',width:Math.min(100,(cnt/60)*100)+'%',height:'100%',borderRadius:'var(--r-full)',transition:'width .4s var(--ease-smooth)'}}/>
              </div>
              {canPlay
                ? <div style={{fontSize:'var(--fs-12)',color:'var(--c-grass-ink)',marginBottom:12,fontWeight:700}}>🏆 60/60 — legal deck, ready to battle!</div>
                : <div style={{fontSize:'var(--fs-12)',color:'var(--c-fire-ink)',marginBottom:12}}>{issues[0]||''}</div>
              }
              <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                {deck.cards.map(function(entry){return(
                  <div key={entry.card.id} title={entry.qty+'× '+entry.card.name} style={{position:'relative'}}>
                    {entry.card.images&&entry.card.images.small&&<img src={entry.card.images.small} alt={entry.card.name} style={{height:46,borderRadius:'var(--r-sm)'}}/>}
                    <span style={{position:'absolute',top:0,right:0,background:'rgba(0,0,0,.7)',color:'var(--c-electric)',fontSize:9,fontWeight:800,borderRadius:'var(--r-sm)',padding:'1px 3px'}}>{entry.qty}</span>
                  </div>
                );})}
              </div>
            </div>
          );
        })}

        </div>}
        {/* World Championship Decks */}
        {deckTab==='worlds'&&<div style={{marginTop:0}}>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:6}}>
            <span style={{fontSize:28}}>🏆</span>
            <div>
              <p className="eyebrow" style={{marginBottom:2,color:'var(--c-electric-ink)'}}>Hall of fame</p>
              <h2 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-30)',letterSpacing:'var(--tr-tight)',color:'var(--c-fg-1)'}}>World Championship decks</h2>
            </div>
          </div>
          <p style={{color:'var(--c-fg-3)',fontSize:'var(--fs-14)',marginBottom:'var(--sp-5)'}}>Real finalist and champion decks from every era — instant playtest.</p>

          {/* Era filter */}
          <div style={{display:'flex',gap:'var(--sp-2)',flexWrap:'wrap',marginBottom:'var(--sp-5)'}}>
            {WORLDS_ERAS.map(function(e){
              var active=worldsEra===e.id;
              return(
                <button key={e.id} onClick={function(){setWorldsEra(e.id);}}
                  style={{padding:'5px 12px',borderRadius:'var(--r-full)',fontSize:'var(--fs-12)',fontWeight:600,cursor:'pointer',border:'1px solid '+(active?e.color:'var(--c-line)'),background:active?'color-mix(in srgb, '+e.color+' 15%, transparent)':'var(--c-mist)',color:active?e.color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',transition:'all var(--dur-1) var(--ease-smooth)'}}>
                  {e.label}
                </button>
              );
            })}
          </div>

          <p style={{color:'var(--c-fg-3)',fontSize:'var(--fs-12)',fontFamily:'var(--font-mono)',marginBottom:'var(--sp-4)'}}>{visibleWorlds.length} decks</p>

          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(auto-fill,minmax(340px,1fr))',gap:'var(--sp-4)'}}>
            {visibleWorlds.map(function(wdeck){
              var eraColor=(WORLDS_ERAS.find(function(e){return e.id===wdeck.era;})||{}).color||'var(--c-electric)';
              return(
                <div key={wdeck.id} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)',padding:'var(--sp-5)',position:'relative',overflow:'hidden',transition:'border-color var(--dur-1) var(--ease-smooth)'}}>
                  {/* Top color band */}
                  <div style={{position:'absolute',top:0,left:0,right:0,height:4,background:eraColor,borderRadius:'var(--r-xl) var(--r-xl) 0 0'}}/>

                  <div style={{marginTop:4}}>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:'var(--sp-3)'}}>
                      <span style={{fontSize:'var(--fs-11)',fontWeight:700,color:eraColor,background:'color-mix(in srgb, '+eraColor+' 15%, transparent)',border:'1px solid color-mix(in srgb, '+eraColor+' 40%, transparent)',borderRadius:'var(--r-sm)',padding:'2px 7px',fontFamily:'var(--font-ui)',letterSpacing:'var(--tr-wide)'}}>{(WORLDS_ERAS.find(function(e){return e.id===wdeck.era;})||{}).label} {wdeck.year}</span>
                      <span style={{fontSize:'var(--fs-11)',fontWeight:600,color:'var(--c-electric-ink)',background:'var(--c-electric-bg)',borderRadius:'var(--r-sm)',padding:'2px 7px',fontFamily:'var(--font-ui)'}}>🏆 {wdeck.placement}</span>
                    </div>
                    <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-18)',color:'var(--c-fg-1)',marginBottom:2,lineHeight:'var(--lh-snug)'}}>{wdeck.name}</div>
                    <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginBottom:4,fontFamily:'var(--font-ui)'}}>{wdeck.player}</div>
                    <div style={{fontSize:'var(--fs-13)',color:'var(--c-fg-2)',lineHeight:'var(--lh-normal)',marginBottom:'var(--sp-4)',fontStyle:'italic'}}>{wdeck.description}</div>
                    <div style={{marginBottom:'var(--sp-4)'}}>
                      <p className="eyebrow" style={{marginBottom:6}}>Decklist · {wdeck.cards.reduce(function(s,c){return s+c.qty;},0)} cards</p>
                      <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                        {wdeck.cards.map(function(c,i){return(
                          <span key={i} style={{fontSize:'var(--fs-11)',color:'var(--c-fg-3)',background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-sm)',padding:'1px 6px',fontFamily:'var(--font-mono)'}}>{c.qty}× {c.name}</span>
                        );})}
                      </div>
                    </div>
                    <button onClick={function(){setBattleDeck(worldsDeckToPlayable(wdeck));}}
                      style={{width:'100%',padding:'10px 0',borderRadius:'var(--r-md)',fontSize:'var(--fs-13)',fontWeight:700,cursor:'pointer',border:'1px solid '+eraColor,background:'color-mix(in srgb, '+eraColor+' 12%, transparent)',color:eraColor,fontFamily:'var(--font-ui)',transition:'background var(--dur-1) var(--ease-smooth)'}}
                      onMouseEnter={function(e){e.currentTarget.style.background='color-mix(in srgb, '+eraColor+' 25%, transparent)';}}
                      onMouseLeave={function(e){e.currentTarget.style.background='color-mix(in srgb, '+eraColor+' 12%, transparent)';}}>
                      ⚔️ Playtest this deck
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>}
      </div>
      {/* Cloud save button on completed decks */}
      <Toast data={toast}/>
      <style>{`@keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}`}</style>
    </div>
  );

  // ── CARD BROWSER (set detail) ─────────────────────────────────────────────
  if(screen==='cards'&&activeSet) return(
    <div style={{background:'var(--c-paper)',minHeight:'100vh',fontFamily:'var(--font-ui)'}}>
      {nav}
      <div style={{maxWidth:1100,margin:'0 auto',padding:isMobile?'var(--sp-4) var(--sp-3)':'var(--sp-6) var(--sp-4)'}}>
        <div style={{display:'flex',gap:8,marginBottom:'var(--sp-5)',alignItems:'center'}}>
          <button onClick={popNav}
            style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:'var(--r-full)',background:'var(--c-mist)',border:'1px solid var(--c-line)',color:'var(--c-fg-2)',cursor:'pointer',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600}}>
            ← All sets
          </button>
          <button onClick={goHome}
            style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:'var(--r-full)',background:'var(--c-mist)',border:'1px solid var(--c-line)',color:'var(--c-fg-2)',cursor:'pointer',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600}}>
            🏠 Home
          </button>
        </div>
        <h2 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-30)',letterSpacing:'var(--tr-tight)',color:'var(--c-fg-1)',marginBottom:4}}>{activeSet.name}</h2>
        <p style={{color:'var(--c-fg-3)',fontSize:'var(--fs-13)',fontFamily:'var(--font-mono)',marginBottom:'var(--sp-5)'}}>{activeSet.year} · {total} cards</p>
        {loading&&<div style={{textAlign:'center',padding:80,color:'var(--c-fg-3)'}}>Loading cards…</div>}
        {!loading&&<CardGrid list={cards}/>}
      </div>
      <Toast data={toast}/>
      {GlobalAuthModal}
    </div>
  );

  // ── COLLECTIONS SCREEN ───────────────────────────────────────────────────────
  if(screen==='collections') {
    var openCol = activeCollection ? collections.find(function(c){return c.id===activeCollection;}) : null;

    // ── Handle photo search results ──
    function handlePhotoSearchResults(searchData) {
      setPhotoSearchResults(searchData);
      // If only one result, directly proceed to variant selection
      if(searchData.results && searchData.results.length === 1) {
        showVariantPicker(searchData.results[0]);
      }
    }

    function showVariantPicker(card) {
      // Show a mini-modal for variant selection
      var variantKeys = card.tcgplayer && card.tcgplayer.prices ? Object.keys(card.tcgplayer.prices) : [];
      var variantOptions = variantKeys.length > 0 ? variantKeys : [null];

      // Auto-select if only one variant
      if(variantOptions.length === 1) {
        addCardToCollection(photoSearchModal.collectionId, card, '', variantOptions[0]);
        setPhotoSearchModal(null);
        setPhotoSearchResults(null);
        return;
      }

      // Otherwise show variant picker
      setPhotoSearchResults(Object.assign({}, searchData, {
        selectedCard: card,
        showVariantPicker: true,
        variantOptions: variantOptions
      }));
    }

    // ── Collection create/edit modal ──
    var CollectionModal = (showCollectionModal) ? (
      <div onClick={function(){setShowCollectionModal(false);setEditingCollection(null);}} style={{position:'fixed',inset:0,zIndex:200,background:'rgba(0,0,0,0.75)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
        <div onClick={function(e){e.stopPropagation();}} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)',padding:'var(--sp-5)',width:'100%',maxWidth:440,boxShadow:'var(--sh-3)'}}>
          <h3 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-24)',color:'var(--c-fg-1)',marginBottom:'var(--sp-4)'}}>{editingCollection?'Edit collection':'New collection'}</h3>
          {/* Name */}
          <label style={{display:'block',fontSize:'var(--fs-13)',fontWeight:600,color:'var(--c-fg-2)',marginBottom:6}}>Name *</label>
          <input value={collectionForm.name} onChange={function(e){setCollectionForm(function(f){return Object.assign({},f,{name:e.target.value});});}}
            placeholder="e.g. Charizard cards, Base Set, Shinies…"
            style={{width:'100%',background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-md)',padding:'9px 12px',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',color:'var(--c-fg-1)',outline:'none',marginBottom:'var(--sp-3)',boxSizing:'border-box'}}/>
          {/* Description */}
          <label style={{display:'block',fontSize:'var(--fs-13)',fontWeight:600,color:'var(--c-fg-2)',marginBottom:6}}>Description</label>
          <textarea value={collectionForm.description} onChange={function(e){setCollectionForm(function(f){return Object.assign({},f,{description:e.target.value});});}}
            placeholder="What is this collection for?"
            rows={2}
            style={{width:'100%',background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-md)',padding:'9px 12px',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',color:'var(--c-fg-1)',outline:'none',marginBottom:'var(--sp-3)',resize:'vertical',boxSizing:'border-box'}}/>
          {/* Tags */}
          <label style={{display:'block',fontSize:'var(--fs-13)',fontWeight:600,color:'var(--c-fg-2)',marginBottom:6}}>Tags <span style={{fontWeight:400,color:'var(--c-fg-3)'}}>— comma separated</span></label>
          <input value={collectionForm.tags} onChange={function(e){setCollectionForm(function(f){return Object.assign({},f,{tags:e.target.value});});}}
            placeholder="Competitive, Vintage, Holo Hunt…"
            style={{width:'100%',background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-md)',padding:'9px 12px',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',color:'var(--c-fg-1)',outline:'none',marginBottom:8,boxSizing:'border-box'}}/>
          {/* Quick tag pills */}
          <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:'var(--sp-4)'}}>
            {COLLECTION_TAGS.map(function(t){
              var active=collectionForm.tags.includes(t);
              return(
                <button key={t} onClick={function(){
                  setCollectionForm(function(f){
                    var tags=f.tags?f.tags.split(',').map(function(x){return x.trim();}).filter(Boolean):[];
                    var next=active?tags.filter(function(x){return x!==t;}):tags.concat([t]);
                    return Object.assign({},f,{tags:next.join(', ')});
                  });
                }} style={{padding:'3px 8px',borderRadius:'var(--r-full)',fontSize:'var(--fs-11)',fontWeight:600,cursor:'pointer',border:'1px solid '+(active?'var(--c-water)':'var(--c-line)'),background:active?'var(--c-water-bg)':'transparent',color:active?'var(--c-water-ink)':'var(--c-fg-3)',fontFamily:'var(--font-ui)'}}>
                  {t}
                </button>
              );
            })}
          </div>
          {/* Color picker */}
          <label style={{display:'block',fontSize:'var(--fs-13)',fontWeight:600,color:'var(--c-fg-2)',marginBottom:8}}>Color</label>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:'var(--sp-5)'}}>
            {COLLECTION_COLORS.map(function(c){
              var active=collectionForm.color===c;
              return(
                <button key={c} onClick={function(){setCollectionForm(function(f){return Object.assign({},f,{color:c});});}}
                  style={{width:28,height:28,borderRadius:'50%',background:c,border:active?'3px solid white':'2px solid transparent',cursor:'pointer',outline:active?'2px solid '+c:'none',outlineOffset:2,transition:'all var(--dur-1)'}}/>
              );
            })}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={function(){setShowCollectionModal(false);setEditingCollection(null);setCollectionForm({name:'',description:'',tags:'',color:'var(--c-water)'});}}
              style={{flex:1,padding:'10px',borderRadius:'var(--r-md)',border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',fontWeight:600,cursor:'pointer'}}>
              Cancel
            </button>
            <button onClick={function(){if(!collectionForm.name.trim()){showToast('Name is required','warning');return;}if(editingCollection){updateCollection(editingCollection.id,collectionForm);}else{createCollection(collectionForm);}}}
              disabled={!collectionForm.name.trim()}
              style={{flex:2,padding:'10px',borderRadius:'var(--r-md)',border:'none',background:'var(--c-water)',color:'#fff',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',fontWeight:700,cursor:collectionForm.name.trim()?'pointer':'default',opacity:collectionForm.name.trim()?1:0.5}}>
              {editingCollection?'Save changes':'Create collection'}
            </button>
          </div>
        </div>
      </div>
    ) : null;

    // ── Card note modal ──
    var NoteModal = cardNoteModal ? (
      <div onClick={function(){setCardNoteModal(null);}} style={{position:'fixed',inset:0,zIndex:200,background:'rgba(0,0,0,0.75)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
        <div onClick={function(e){e.stopPropagation();}} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)',padding:'var(--sp-5)',width:'100%',maxWidth:400,boxShadow:'var(--sh-3)'}}>
          <h3 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-20)',color:'var(--c-fg-1)',marginBottom:4}}>Note for {cardNoteModal.cardName}</h3>
          <p style={{fontSize:'var(--fs-13)',color:'var(--c-fg-3)',marginBottom:'var(--sp-3)'}}>Add personal notes — condition, price paid, trade value, memories…</p>
          {/* Condition selector */}
          <div style={{marginBottom:'var(--sp-3)'}}>
            <div style={{fontSize:'var(--fs-13)',fontWeight:600,color:'var(--c-fg-2)',marginBottom:8}}>Condition</div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {[
                {id:'NM', label:'NM', desc:'Near Mint',    color:'var(--c-grass)'},
                {id:'LP', label:'LP', desc:'Lightly Played',color:'var(--c-electric)'},
                {id:'MP', label:'MP', desc:'Moderately',   color:'var(--c-ground)'},
                {id:'HP', label:'HP', desc:'Heavily',      color:'var(--c-fire)'},
                {id:'DMG',label:'DMG',desc:'Damaged',      color:'var(--c-dark)'},
              ].map(function(c){
                var active=(cardNoteModal.condition||'NM')===c.id;
                return(
                  <button key={c.id} onClick={function(){setCardNoteModal(function(m){return Object.assign({},m,{condition:c.id});});}}
                    title={c.desc}
                    style={{padding:'5px 12px',borderRadius:'var(--r-md)',fontSize:'var(--fs-12)',fontWeight:700,cursor:'pointer',border:'1px solid '+(active?c.color:'var(--c-line)'),background:active?'color-mix(in srgb, '+c.color+' 15%, transparent)':'transparent',color:active?c.color:'var(--c-fg-3)',fontFamily:'var(--font-ui)'}}>
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <textarea
            value={cardNoteModal.note}
            onChange={function(e){setCardNoteModal(function(m){return Object.assign({},m,{note:e.target.value});});}}
            placeholder="e.g. bought for $12, PSA 9 candidate, trading…"
            rows={3}
            autoFocus
            style={{width:'100%',background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-md)',padding:'10px 12px',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',color:'var(--c-fg-1)',outline:'none',resize:'vertical',marginBottom:'var(--sp-4)',boxSizing:'border-box'}}/>
          <div style={{display:'flex',gap:8}}>
            <button onClick={function(){setCardNoteModal(null);}} style={{flex:1,padding:'10px',borderRadius:'var(--r-md)',border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',fontWeight:600,cursor:'pointer'}}>Cancel</button>
            <button onClick={function(){updateCardNote(cardNoteModal.collectionId,cardNoteModal.cardId,cardNoteModal.note,cardNoteModal.condition);}} style={{flex:2,padding:'10px',borderRadius:'var(--r-md)',border:'none',background:'var(--c-grass)',color:'#fff',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',fontWeight:700,cursor:'pointer'}}>Save note</button>
          </div>
        </div>
      </div>
    ) : null;

    // ── Photo search modal ──
    var PhotoSearchModal = (photoSearchModal && photoSearchResults) ? (
      <div onClick={function(){setPhotoSearchModal(null);setPhotoSearchResults(null);}} style={{position:'fixed',inset:0,zIndex:300,background:'rgba(0,0,0,0.75)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
        <div onClick={function(e){e.stopPropagation();}} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)',padding:'var(--sp-5)',width:'100%',maxWidth:600,maxHeight:'80vh',overflow:'auto',boxShadow:'var(--sh-3)'}}>
          {photoSearchResults.showVariantPicker ? (
            // ── Variant picker modal ──
            <div>
              <div style={{marginBottom:'var(--sp-4)'}}>
                <img src={photoSearchResults.selectedCard.images && photoSearchResults.selectedCard.images.small ? photoSearchResults.selectedCard.images.small : ''} alt={photoSearchResults.selectedCard.name} style={{maxWidth:150,marginBottom:'var(--sp-3)',borderRadius:'var(--r-lg)'}}/>
                <h3 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-20)',color:'var(--c-fg-1)',marginBottom:4}}>{photoSearchResults.selectedCard.name}</h3>
                <p style={{fontSize:'var(--fs-13)',color:'var(--c-fg-3)'}}>{photoSearchResults.selectedCard.set && photoSearchResults.selectedCard.set.name ? photoSearchResults.selectedCard.set.name : ''} #{photoSearchResults.selectedCard.number || ''}</p>
              </div>
              <div style={{marginBottom:'var(--sp-4)'}}>
                <div style={{fontSize:'var(--fs-13)',fontWeight:600,color:'var(--c-fg-2)',marginBottom:8}}>Select variant</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:8}}>
                  {photoSearchResults.variantOptions.map(function(variant){
                    var label = variant ? (VARIANT_LABELS_COL[variant] || variant) : 'Base';
                    return(
                      <button key={variant || 'base'} onClick={function(){addCardToCollection(photoSearchModal.collectionId,photoSearchResults.selectedCard,'',variant);setPhotoSearchModal(null);setPhotoSearchResults(null);}}
                        style={{padding:'10px',borderRadius:'var(--r-md)',background:'var(--c-water)',color:'#fff',border:'none',cursor:'pointer',fontFamily:'var(--font-ui)',fontSize:'var(--fs-12)',fontWeight:600}}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={function(){setPhotoSearchModal(null);setPhotoSearchResults(null);}} style={{flex:1,padding:'10px',borderRadius:'var(--r-md)',border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',fontWeight:600,cursor:'pointer'}}>Cancel</button>
              </div>
            </div>
          ) : (
            // ── Search results list ──
            <div>
              <h3 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-20)',color:'var(--c-fg-1)',marginBottom:4}}>Search results for "{photoSearchResults.cardName}"</h3>
              <p style={{fontSize:'var(--fs-13)',color:'var(--c-fg-3)',marginBottom:'var(--sp-4)',fontStyle:'italic'}}>Found {photoSearchResults.results.length} results</p>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:'var(--sp-3)',marginBottom:'var(--sp-4)'}}>
                {photoSearchResults.results.map(function(card){
                  return(
                    <div key={card.id} onClick={function(){showVariantPicker(card);}} style={{cursor:'pointer',background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',overflow:'hidden',transition:'border-color var(--dur-1)',background:'var(--c-mist)'}}>
                      {card.images && card.images.small ? (
                        <img src={card.images.small} alt={card.name} style={{width:'100%',display:'block',height:180,objectFit:'cover'}}/>
                      ) : (
                        <div style={{width:'100%',height:180,background:'var(--c-bg-elevated)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:32}}>🃏</div>
                      )}
                      <div style={{padding:'var(--sp-2) var(--sp-3)'}}>
                        <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-12)',color:'var(--c-fg-1)',marginBottom:2,lineHeight:'var(--lh-snug)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{card.name}</div>
                        <div style={{fontSize:'var(--fs-10)',color:'var(--c-fg-3)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{card.set && card.set.name ? card.set.name : ''}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={function(){setPhotoSearchModal(null);setPhotoSearchResults(null);}} style={{flex:1,padding:'10px',borderRadius:'var(--r-md)',border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',fontWeight:600,cursor:'pointer'}}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    ) : null;

    return(
      <div style={{background:'var(--c-paper)',minHeight:'100vh',fontFamily:'var(--font-ui)',position:'relative'}}>
        <DotGrid/>
        <div style={{position:'relative',zIndex:1}}>
          {nav}
          <div style={{maxWidth:1100,margin:'0 auto',padding:isMobile?'var(--sp-3)':'var(--sp-6) var(--sp-5)',display:'grid',gridTemplateColumns:openCol&&!isMobile?'280px 1fr':'1fr',gap:'var(--sp-5)',alignItems:'start'}}>

            {/* ── Left: collection list ── */}
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'var(--sp-4)'}}>
                <div>
                  <p className="eyebrow" style={{marginBottom:2,color:'var(--c-fg-3)'}}>My collections</p>
                  <h2 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-24)',color:'var(--c-fg-1)'}}>Collections</h2>
                </div>
                <button onClick={function(){setCollectionForm({name:'',description:'',tags:'',color:'var(--c-water)'});setEditingCollection(null);setShowCollectionModal(true);}}
                  style={{padding:'8px 16px',borderRadius:'var(--r-md)',background:'var(--c-water)',color:'#fff',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:700,border:'none',cursor:'pointer',flexShrink:0}}>
                  + New
                </button>
              </div>

              {collections.length===0&&(
                <div style={{textAlign:'center',padding:'var(--sp-7)',background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)'}}>
                  <div style={{fontSize:36,marginBottom:'var(--sp-3)'}}>📦</div>
                  <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-18)',color:'var(--c-fg-1)',marginBottom:6}}>No collections yet</div>
                  <div style={{fontSize:'var(--fs-13)',color:'var(--c-fg-3)',marginBottom:'var(--sp-4)'}}>Create collections to organise your cards — by set, type, rarity, or anything you like.</div>
                  <button onClick={function(){setCollectionForm({name:'',description:'',tags:'',color:'var(--c-water)'});setEditingCollection(null);setShowCollectionModal(true);}}
                    style={{padding:'9px 20px',borderRadius:'var(--r-md)',background:'var(--c-water)',color:'#fff',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',fontWeight:700,border:'none',cursor:'pointer'}}>
                    Create first collection
                  </button>
                </div>
              )}

              <div style={{display:'flex',flexDirection:'column',gap:'var(--sp-2)'}}>
                {collections.map(function(col){
                  var isOpen=activeCollection===col.id;
                  return(
                    <div key={col.id}
                      onClick={function(){setActiveCollection(isOpen?null:col.id);}}
                      style={{background:isOpen?'color-mix(in srgb, '+col.color+' 8%, var(--c-bg-elevated))':'var(--c-bg-elevated)',border:'1px solid '+(isOpen?col.color:'var(--c-line)'),borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',cursor:'pointer',transition:'all var(--dur-1) var(--ease-smooth)',position:'relative',overflow:'hidden'}}>
                      {/* Color strip */}
                      <div style={{position:'absolute',left:0,top:0,bottom:0,width:4,background:col.color,borderRadius:'var(--r-lg) 0 0 var(--r-lg)'}}/>
                      <div style={{paddingLeft:'var(--sp-3)'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
                          <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-16)',color:'var(--c-fg-1)'}}>{col.name}</div>
                          <div style={{display:'flex',gap:4}} onClick={function(e){e.stopPropagation();}}>
                            <button onClick={function(){setCollectionForm({name:col.name,description:col.description||'',tags:(col.tags||[]).join(', '),color:col.color});setEditingCollection(col);setShowCollectionModal(true);}}
                              style={{background:'none',border:'none',color:'var(--c-fg-3)',cursor:'pointer',fontSize:14,padding:'2px 4px'}}>✎</button>
                            <button onClick={function(){if(window.confirm('Delete "'+col.name+'"?'))deleteCollection(col.id);}}
                              style={{background:'none',border:'none',color:'var(--c-fire-ink)',cursor:'pointer',fontSize:14,padding:'2px 4px'}}>🗑</button>
                          </div>
                        </div>
                        {col.description&&<div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginBottom:6,lineHeight:'var(--lh-normal)'}}>{col.description}</div>}
                        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                          <span style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-11)',color:'var(--c-fg-3)'}}>{col.cards.length} cards</span>
                          {(col.tags||[]).map(function(t){return(
                            <span key={t} style={{padding:'1px 7px',borderRadius:'var(--r-full)',fontSize:'var(--fs-10)',fontWeight:600,background:'color-mix(in srgb, '+col.color+' 15%, transparent)',color:col.color,border:'1px solid color-mix(in srgb, '+col.color+' 35%, transparent)'}}>{t}</span>
                          );})}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Right: open collection cards ── */}
            {openCol&&(
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'var(--sp-4)',flexWrap:'wrap',gap:'var(--sp-2)'}}>
                  <div>
                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:4}}>
                      <div style={{width:12,height:12,borderRadius:'50%',background:openCol.color}}/>
                      <h2 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-24)',color:'var(--c-fg-1)'}}>{openCol.name}</h2>
                    </div>
                    {openCol.description&&<p style={{fontSize:'var(--fs-13)',color:'var(--c-fg-3)',marginBottom:6}}>{openCol.description}</p>}
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      {(openCol.tags||[]).map(function(t){return(
                        <span key={t} style={{padding:'2px 8px',borderRadius:'var(--r-full)',fontSize:'var(--fs-11)',fontWeight:600,background:'color-mix(in srgb, '+openCol.color+' 12%, transparent)',color:openCol.color,border:'1px solid color-mix(in srgb, '+openCol.color+' 35%, transparent)'}}>{t}</span>
                      );})}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:'8px',flexShrink:0}}>
                    <CardPhotoSearch onSearchResults={handlePhotoSearchResults} searchCards={searchCards}/>
                    <button onClick={function(){setScreen('sets');}}
                      style={{padding:'7px 14px',borderRadius:'var(--r-md)',background:'var(--c-water)',color:'#fff',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:700,border:'none',cursor:'pointer',flexShrink:0}}>
                      + Add cards
                    </button>
                  </div>
                </div>

                {openCol.cards.length===0?(
                  <div style={{textAlign:'center',padding:'var(--sp-7)',background:'var(--c-bg-elevated)',border:'1px dashed var(--c-line)',borderRadius:'var(--r-xl)'}}>
                    <div style={{fontSize:32,marginBottom:'var(--sp-3)'}}>🃏</div>
                    <div style={{fontSize:'var(--fs-14)',color:'var(--c-fg-3)',marginBottom:'var(--sp-4)'}}>No cards yet. Browse cards and add them to this collection.</div>
                    <button onClick={function(){setScreen('sets');}} style={{padding:'8px 18px',borderRadius:'var(--r-md)',background:openCol.color,color:'#fff',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:700,border:'none',cursor:'pointer'}}>Browse cards →</button>
                  </div>
                ):(
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:'var(--sp-3)'}}>
                    {openCol.cards.map(function(entry){
                      return(
                        <div key={entry.id} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',overflow:'hidden',transition:'border-color var(--dur-1)'}}>
                          {entry.image
                            ?<img src={entry.image} alt={entry.name} style={{width:'100%',display:'block'}}/>
                            :<div style={{paddingBottom:'140%',background:'var(--c-mist)',position:'relative'}}><span style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:24}}>🃏</span></div>
                          }
                          <div style={{padding:'var(--sp-2) var(--sp-3)'}}>
                            <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-13)',color:'var(--c-fg-1)',marginBottom:2,lineHeight:'var(--lh-snug)'}}>{entry.name}</div>
                            <div style={{fontSize:'var(--fs-11)',color:'var(--c-fg-3)',marginBottom:'var(--sp-2)'}}>{entry.setName}{entry.number?' #'+entry.number:''}</div>
                            <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:4}}>
                              {entry.condition&&entry.condition!=='NM'&&(
                                <span style={{padding:'1px 6px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-10)',fontWeight:800,background:entry.condition==='LP'?'var(--c-electric-bg)':entry.condition==='MP'?'var(--c-ground-bg)':entry.condition==='HP'?'var(--c-fire-bg)':'var(--c-dark-bg)',color:entry.condition==='LP'?'var(--c-electric-ink)':entry.condition==='MP'?'var(--c-ground-ink)':entry.condition==='HP'?'var(--c-fire-ink)':'var(--c-dark-ink)'}}>
                                  {entry.condition}
                                </span>
                              )}
                              {entry.condition==='NM'&&<span style={{padding:'1px 6px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-10)',fontWeight:800,background:'var(--c-grass-bg)',color:'var(--c-grass-ink)'}}>NM</span>}
                            </div>
                            {entry.note&&(
                              <div style={{background:'var(--c-mist)',borderRadius:'var(--r-sm)',padding:'4px 6px',fontSize:'var(--fs-11)',color:'var(--c-fg-2)',lineHeight:'var(--lh-normal)',marginBottom:'var(--sp-2)',fontStyle:'italic'}}>
                                📝 {entry.note}
                              </div>
                            )}
                            <div style={{display:'flex',gap:4}}>
                              <button onClick={function(){setCardNoteModal({collectionId:openCol.id,cardId:entry.id,note:entry.note||'',cardName:entry.name});}}
                                style={{flex:1,padding:'4px 0',borderRadius:'var(--r-sm)',border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-3)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-11)',fontWeight:600,cursor:'pointer'}}>
                                {entry.note?'Edit note':'+ Note'}
                              </button>
                              <button onClick={function(){removeCardFromCollection(openCol.id,entry.id);}}
                                style={{padding:'4px 6px',borderRadius:'var(--r-sm)',border:'1px solid var(--c-fire)',background:'transparent',color:'var(--c-fire-ink)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-11)',cursor:'pointer'}}>
                                ✕
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {CollectionModal}
        {NoteModal}
        {PhotoSearchModal}
        {GlobalAuthModal}
        <Toast data={toast}/>
      </div>
    );
  }

  // ── WISHLIST SCREEN ──────────────────────────────────────────────────────────
  if(screen==='wishlist') {
    var totalMarket = wishlist.filter(function(w){return !w.purchased&&w.marketPrice;}).reduce(function(s,w){return s+(w.marketPrice||0);},0);
    var purchased   = wishlist.filter(function(w){return w.purchased;});
    var remaining   = wishlist.filter(function(w){return !w.purchased;});

    return(
      <div style={{background:'var(--c-paper)',minHeight:'100vh',fontFamily:'var(--font-ui)',position:'relative'}}>
        <DotGrid/>
        <div style={{position:'relative',zIndex:1}}>
          {nav}
          <div style={{maxWidth:860,margin:'0 auto',padding:isMobile?'var(--sp-4) var(--sp-3)':'var(--sp-6) var(--sp-5)'}}>

            {/* Shared wishlist banner */}
            {sharedWishlist&&(
              <div style={{background:'var(--c-dragon-bg)',border:'1px solid var(--c-dragon)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',marginBottom:'var(--sp-4)',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'var(--sp-3)'}}>
                <div>
                  <div style={{fontWeight:700,color:'var(--c-dragon-ink)',fontSize:'var(--fs-14)'}}>👁 Viewing a shared wishlist</div>
                  <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginTop:2}}>{sharedWishlist.length} cards · read only</div>
                </div>
                <button onClick={function(){setSharedWishlist(null);window.history.replaceState({},'',window.location.pathname);}}
                  style={{padding:'6px 14px',borderRadius:'var(--r-md)',border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer'}}>
                  View my wishlist
                </button>
              </div>
            )}

            {/* Header */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'var(--sp-5)',flexWrap:'wrap',gap:'var(--sp-3)'}}>
              <div>
                <p className="eyebrow" style={{marginBottom:4,color:'var(--c-electric-ink)'}}>
                  {sharedWishlist?'Shared wishlist':'Shopping list'}
                </p>
                <h1 style={{fontFamily:'var(--font-display)',fontWeight:900,fontSize:isMobile?'var(--fs-30)':'var(--fs-38)',letterSpacing:'var(--tr-tight)',color:'var(--c-fg-1)'}}>
                  {sharedWishlist?"Friend's Wishlist":"Wishlist"}
                </h1>
              </div>
              {!sharedWishlist&&wishlist.length>0&&(
                <div style={{display:'flex',gap:'var(--sp-3)',flexWrap:'wrap'}}>
                  {/* Summary stats */}
                  <div style={{background:'var(--c-electric-bg)',border:'1px solid var(--c-electric)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',textAlign:'center'}}>
                    <div style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-24)',color:'var(--c-electric-ink)',lineHeight:1}}>{remaining.length}</div>
                    <div style={{fontSize:'var(--fs-11)',fontWeight:700,color:'var(--c-electric-ink)',opacity:0.75,letterSpacing:'var(--tr-wide)',textTransform:'uppercase',marginTop:2}}>To buy</div>
                  </div>
                  <div style={{background:'var(--c-grass-bg)',border:'1px solid var(--c-grass)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',textAlign:'center'}}>
                    <div style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-24)',color:'var(--c-grass-ink)',lineHeight:1}}>{purchased.length}</div>
                    <div style={{fontSize:'var(--fs-11)',fontWeight:700,color:'var(--c-grass-ink)',opacity:0.75,letterSpacing:'var(--tr-wide)',textTransform:'uppercase',marginTop:2}}>Purchased</div>
                  </div>
                  {totalMarket>0&&(
                    <div style={{background:'var(--c-water-bg)',border:'1px solid var(--c-water)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',textAlign:'center'}}>
                      <div style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-24)',color:'var(--c-water-ink)',lineHeight:1}}>${totalMarket.toFixed(2)}</div>
                      <div style={{fontSize:'var(--fs-11)',fontWeight:700,color:'var(--c-water-ink)',opacity:0.75,letterSpacing:'var(--tr-wide)',textTransform:'uppercase',marginTop:2}}>Est. total</div>
                    </div>
                  )}
                </div>
              )}
              {/* Share button */}
              {!sharedWishlist&&remaining.length>0&&(
                <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:8}}>
                  <button onClick={shareWishlist}
                    style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',borderRadius:'var(--r-md)',border:'1px solid var(--c-ghost)',background:'var(--c-ghost-bg)',color:'var(--c-ghost-ink)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:700,cursor:'pointer',transition:'all var(--dur-1) var(--ease-smooth)'}}>
                    🔗 Share wishlist
                  </button>
                  {shareToast&&(
                    <div style={{fontSize:'var(--fs-12)',color:'var(--c-grass-ink)',fontWeight:600,animation:'toast-in var(--dur-2) var(--ease-pop)'}}>
                      ✓ Link copied to clipboard!
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Empty state */}
            {wishlist.length===0&&(
              <div style={{textAlign:'center',padding:'var(--sp-9) var(--sp-5)',background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)'}}>
                <div style={{fontSize:48,marginBottom:'var(--sp-4)'}}>⭐</div>
                <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-24)',color:'var(--c-fg-1)',marginBottom:'var(--sp-2)'}}>Your wishlist is empty</div>
                <div style={{fontSize:'var(--fs-14)',color:'var(--c-fg-3)',marginBottom:'var(--sp-5)',maxWidth:300,margin:'0 auto var(--sp-5)'}}>Browse cards and tap ☆ to add them here. Then buy them all at once from TCGPlayer.</div>
                <button onClick={function(){setScreen('sets');}} style={{padding:'10px 24px',borderRadius:'var(--r-md)',background:'var(--c-water)',color:'#fff',fontFamily:'var(--font-ui)',fontSize:'var(--fs-14)',fontWeight:700,border:'none',cursor:'pointer'}}>Browse cards</button>
              </div>
            )}

            {/* Shared wishlist view */}
            {sharedWishlist&&sharedWishlist.length>0&&(
              <div style={{marginBottom:'var(--sp-7)'}}>
                <p className="eyebrow" style={{marginBottom:'var(--sp-4)'}}>Cards · {sharedWishlist.length}</p>
                <div style={{display:'flex',flexDirection:'column',gap:'var(--sp-2)'}}>
                  {sharedWishlist.map(function(item){
                    return(
                      <div key={item.id} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',display:'flex',alignItems:'center',gap:'var(--sp-3)'}}>
                        {item.image?<img src={item.image} alt={item.name} style={{width:44,height:62,borderRadius:'var(--r-sm)',objectFit:'cover',flexShrink:0}}/>:<div style={{width:44,height:62,borderRadius:'var(--r-sm)',background:'var(--c-mist)',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>🃏</div>}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-16)',color:'var(--c-fg-1)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.name}</div>
                          {item.variantLabel&&<span style={{display:'inline-block',padding:'1px 6px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-10)',fontWeight:700,background:'var(--c-electric-bg)',color:'var(--c-electric-ink)',border:'1px solid var(--c-electric)',marginBottom:3}}>{item.variantLabel}</span>}
                          <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginTop:2}}>{item.setName}{item.number?' · #'+item.number:''}{item.rarity?' · '+item.rarity:''}</div>
                        </div>
                        {item.marketPrice!=null&&<div style={{textAlign:'right',flexShrink:0}}><div style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-18)',color:'var(--c-electric-ink)'}}>${item.marketPrice.toFixed(2)}</div><div style={{fontSize:'var(--fs-10)',color:'var(--c-fg-3)'}}>market</div></div>}
                        <div style={{display:'flex',flexDirection:'column',gap:6,flexShrink:0}}>
                          <a href={item.tcgUrl} target="_blank" rel="noreferrer" style={{display:'block',padding:'6px 14px',borderRadius:'var(--r-md)',background:'var(--c-electric)',color:'#000',textDecoration:'none',fontFamily:'var(--font-ui)',fontSize:'var(--fs-12)',fontWeight:800,textAlign:'center',whiteSpace:'nowrap'}}>Buy →</a>
                          <button onClick={function(){addToWishlist(item);}} style={{padding:'5px 10px',borderRadius:'var(--r-md)',border:'1px solid var(--c-dragon)',background:'var(--c-dragon-bg)',color:'var(--c-dragon-ink)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-11)',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>+ My list</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* To buy section */}
            {!sharedWishlist&&remaining.length>0&&(
              <div style={{marginBottom:'var(--sp-7)'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'var(--sp-4)'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'var(--sp-3)'}}>
                    <p className="eyebrow">To buy · {remaining.length} cards</p>
                  </div>
                  {/* Buy all button */}
                  <a href={'https://www.tcgplayer.com/search/pokemon/product?q='+encodeURIComponent(remaining.slice(0,3).map(function(w){return w.name;}).join(' '))}
                    target="_blank" rel="noreferrer"
                    style={{padding:'7px 16px',borderRadius:'var(--r-md)',background:'var(--c-electric)',color:'#000',textDecoration:'none',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:800,flexShrink:0}}>
                    Shop all on TCGPlayer →
                  </a>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:'var(--sp-2)'}}>
                  {remaining.map(function(item){
                    return(
                      <div key={item.id} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',display:'flex',alignItems:'center',gap:'var(--sp-3)',transition:'all var(--dur-1) var(--ease-smooth)'}}>
                        {/* Card image */}
                        {item.image
                          ?<img src={item.image} alt={item.name} style={{width:44,height:62,borderRadius:'var(--r-sm)',objectFit:'cover',flexShrink:0}}/>
                          :<div style={{width:44,height:62,borderRadius:'var(--r-sm)',background:'var(--c-mist)',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>🃏</div>
                        }
                        {/* Info */}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-16)',color:'var(--c-fg-1)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.name}</div>
                          <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginTop:2}}>{item.setName}{item.number?' · #'+item.number:''}{item.rarity?' · '+item.rarity:''}</div>
                          {item.types&&item.types.length>0&&<div style={{marginTop:4,display:'flex',gap:4}}>{item.types.slice(0,2).map(function(t){return<TypeChip key={t} type={t}/>;})}</div>}
                        </div>
                        {/* Price */}
                        <div style={{textAlign:'right',flexShrink:0}}>
                          {item.marketPrice!=null&&(
                            <div style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-20)',color:'var(--c-electric-ink)'}}>${item.marketPrice.toFixed(2)}</div>
                          )}
                          <div style={{fontSize:'var(--fs-10)',color:'var(--c-fg-3)',marginTop:2}}>market</div>
                        </div>
                        {/* Actions */}
                        <div style={{display:'flex',flexDirection:'column',gap:6,flexShrink:0}}>
                          <a href={item.tcgUrl} target="_blank" rel="noreferrer"
                            style={{display:'block',padding:'6px 14px',borderRadius:'var(--r-md)',background:'var(--c-electric)',color:'#000',textDecoration:'none',fontFamily:'var(--font-ui)',fontSize:'var(--fs-12)',fontWeight:800,textAlign:'center',whiteSpace:'nowrap'}}>
                            Buy →
                          </a>
                          <button onClick={function(){togglePurchased(item.id);}}
                            style={{padding:'5px 10px',borderRadius:'var(--r-md)',border:'1px solid var(--c-grass)',background:'var(--c-grass-bg)',color:'var(--c-grass-ink)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-11)',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
                            ✓ Got it
                          </button>
                          <button onClick={function(){removeFromWishlist(item.id);}}
                            style={{padding:'5px 10px',borderRadius:'var(--r-md)',border:'1px solid var(--c-fire)',background:'transparent',color:'var(--c-fire-ink)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-11)',fontWeight:600,cursor:'pointer'}}>
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Purchased section */}
            {!sharedWishlist&&purchased.length>0&&(
              <div>
                <p className="eyebrow" style={{marginBottom:'var(--sp-4)',color:'var(--c-grass-ink)'}}>Purchased · {purchased.length} cards</p>
                <div style={{display:'flex',flexDirection:'column',gap:'var(--sp-2)',opacity:0.65}}>
                  {purchased.map(function(item){
                    return(
                      <div key={item.id} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-grass)',borderRadius:'var(--r-lg)',padding:'var(--sp-3) var(--sp-4)',display:'flex',alignItems:'center',gap:'var(--sp-3)'}}>
                        {item.image
                          ?<img src={item.image} alt={item.name} style={{width:44,height:62,borderRadius:'var(--r-sm)',objectFit:'cover',flexShrink:0,filter:'grayscale(30%)'}}/>
                          :<div style={{width:44,height:62,borderRadius:'var(--r-sm)',background:'var(--c-mist)',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>✓</div>
                        }
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:'flex',alignItems:'center',gap:6}}>
                            <span style={{fontSize:14}}>✓</span>
                            <span style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'var(--fs-16)',color:'var(--c-fg-2)',textDecoration:'line-through',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.name}</span>
                          </div>
                          <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginTop:2}}>{item.setName}</div>
                        </div>
                        <div style={{display:'flex',gap:6,flexShrink:0}}>
                          <button onClick={function(){togglePurchased(item.id);}}
                            style={{padding:'5px 10px',borderRadius:'var(--r-md)',border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-3)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-11)',fontWeight:600,cursor:'pointer'}}>
                            Undo
                          </button>
                          <button onClick={function(){removeFromWishlist(item.id);}}
                            style={{padding:'5px 10px',borderRadius:'var(--r-md)',border:'1px solid var(--c-fire)',background:'transparent',color:'var(--c-fire-ink)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-11)',fontWeight:600,cursor:'pointer'}}>
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button onClick={function(){var wl=wishlist.filter(function(w){return !w.purchased;});setWishlist(wl);saveWishlist(wl);showToast('Cleared purchased cards');}}
                  style={{marginTop:'var(--sp-4)',padding:'8px 16px',borderRadius:'var(--r-md)',border:'1px solid var(--c-line)',background:'transparent',color:'var(--c-fg-3)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer'}}>
                  Clear all purchased
                </button>
              </div>
            )}
          </div>
        </div>
        {GlobalAuthModal}
        <Toast data={toast}/>
      </div>
    );
  }

  // ── EVENTS SCREEN ───────────────────────────────────────────────────────────
  if(screen==='events') {
    var EVENTS = [
      // ── MAJOR INTERNATIONAL ──────────────────────────────────────────────
      {
        id:'worlds-2026', type:'worlds', date:'2026-08-28', endDate:'2026-08-30',
        name:'Pokémon World Championships 2026',
        location:'San Francisco, CA', venue:'Moscone Center',
        desc:'The pinnacle of competitive Pokémon — TCG, VGC, Pokémon GO and more. Invite-only for qualified players.',
        url:'https://worlds.pokemon.com',
        sdDistance: null,
      },
      {
        id:'naic-2026', type:'international', date:'2026-06-12', endDate:'2026-06-14',
        name:'North America International Championships (NAIC)',
        location:'Columbus, OH', venue:'Greater Columbus Convention Center',
        desc:'The largest TCG & VGC event in North America. Open registration with Championship Points on the line.',
        url:'https://championships.pokemon.com',
        sdDistance: null,
      },
      // ── REGIONALS ────────────────────────────────────────────────────────
      {
        id:'regionals-la-2026', type:'regional', date:'2026-05-09', endDate:'2026-05-10',
        name:'Los Angeles Regional Championships',
        location:'Los Angeles, CA', venue:'TBD',
        desc:'Major Regional Championship — 2 hours from San Diego. Earns Championship Points.',
        url:'https://rk9.gg/events/pokemon',
        sdDistance: '~120 miles',
      },
      {
        id:'regionals-indy-2026', type:'regional', date:'2026-05-30', endDate:'2026-05-31',
        name:'Indianapolis Regional Championships',
        location:'Indianapolis, IN', venue:'Indiana Convention Center',
        desc:'Major Regional — sold out. Earns Championship Points.',
        url:'https://rk9.gg/events/pokemon',
        sdDistance: null,
      },
      {
        id:'regionals-orlando-2026', type:'regional', date:'2026-04-03', endDate:'2026-04-05',
        name:'Orlando Regional Championships',
        location:'Orlando, FL', venue:'Orange County Convention Center',
        desc:'Completed. 2,745 players. One of the largest Regionals of the season.',
        url:'https://limitlesstcg.com',
        sdDistance: null, past: true,
      },
      {
        id:'regionals-houston-2026', type:'regional', date:'2026-03-20', endDate:'2026-03-22',
        name:'Houston Regional Championships',
        location:'Houston, TX', venue:'George R. Brown Convention Center',
        desc:'Completed. Major Regional Championship.',
        url:'https://rk9.gg/events/pokemon',
        sdDistance: null, past: true,
      },
      // ── PRE-RELEASES ─────────────────────────────────────────────────────
      {
        id:'prerelease-chaos-rising', type:'prerelease', date:'2026-05-16', endDate:'2026-05-17',
        name:'Mega Evolution — Chaos Rising Pre-Release',
        location:'San Diego area stores', venue:'Local game stores',
        desc:'Play with Chaos Rising cards before the official May 22 release. Check your LGS for specific dates and times. Build-and-battle kits.',
        url:'https://play.pokemon.com/en-us/local-tournaments/tcg/',
        sdDistance: 'Local',
      },
      // ── SAN DIEGO LOCAL ──────────────────────────────────────────────────
      {
        id:'tcs-rockets-weekly-tue', type:'local', date:'2026-05-06', recurring:'weekly',
        name:'TCS Rockets — Tuesday Night Pokemon TCG',
        location:'San Diego, CA', venue:'TCS Rockets (5155 Waring Rd, San Diego 92120)',
        desc:'Weekly Tuesday TCG league/tournament. $5 entry. 6:00 PM – 8:30 PM.',
        url:'https://tcsrockets.com/events-calendar/category/pokemon/',
        sdDistance: 'Local · ~10 mi from Chula Vista',
      },
      {
        id:'tcs-rockets-weekly-sat', type:'local', date:'2026-05-09', recurring:'weekly',
        name:'TCS Rockets — Saturday Pokemon TCG',
        location:'San Diego, CA', venue:'TCS Rockets (5155 Waring Rd, San Diego 92120)',
        desc:'Weekly Saturday TCG play. $5 entry. 12:00 PM – 2:00 PM.',
        url:'https://tcsrockets.com/events-calendar/category/pokemon/',
        sdDistance: 'Local · ~10 mi from Chula Vista',
      },
      {
        id:'card-party-sd', type:'convention', date:'2026-09-01',
        name:'Card Party San Diego',
        location:'San Diego, CA', venue:'TBD',
        desc:'Pokémon & TCG card show featuring tournaments, trade nights, creator meet-and-greets, pack battles and exclusive cards.',
        url:'https://www.card.party',
        sdDistance: 'Local',
      },
    ];

    var EVENT_TYPES = {
      worlds:       { label:'Worlds',       color:'var(--c-electric)',  icon:'🌍' },
      international:{ label:'International', color:'var(--c-psychic)',   icon:'🏆' },
      regional:     { label:'Regional',      color:'var(--c-fire)',      icon:'⚔️' },
      prerelease:   { label:'Pre-release',   color:'var(--c-grass)',     icon:'🎁' },
      local:        { label:'Local',         color:'var(--c-water)',     icon:'📍' },
      convention:   { label:'Convention',    color:'var(--c-dragon)',    icon:'🎪' },
    };

    var EVENT_FILTERS = ['all','local','prerelease','regional','international','worlds','convention'];
    var today = new Date().toISOString().slice(0,10);

    var filteredEvts = EVENTS.filter(function(e){
      if(!showPast && e.past) return false;
      if(evtFilter !== 'all' && e.type !== evtFilter) return false;
      return true;
    }).sort(function(a,b){ return a.date.localeCompare(b.date); });

    function formatDate(d, end) {
      var opts = { month:'short', day:'numeric', year:'numeric' };
      var s = new Date(d+'T12:00:00').toLocaleDateString('en-US', opts);
      if(end && end !== d) {
        var e = new Date(end+'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' });
        return s + ' – ' + e;
      }
      return s;
    }

    function daysUntil(d) {
      var diff = Math.ceil((new Date(d+'T12:00:00') - new Date()) / 86400000);
      if(diff < 0) return null;
      if(diff === 0) return 'Today';
      if(diff === 1) return 'Tomorrow';
      if(diff < 7) return 'In '+diff+' days';
      if(diff < 30) return 'In '+Math.ceil(diff/7)+' weeks';
      return 'In '+Math.ceil(diff/30)+' months';
    }

    return (
      <div style={{background:'var(--c-paper)',minHeight:'100vh',fontFamily:'var(--font-ui)',position:'relative'}}>
        <DotGrid/>
        <div style={{position:'relative',zIndex:1}}>
          {nav}
          <div style={{maxWidth:860,margin:'0 auto',padding:isMobile?'var(--sp-4) var(--sp-3)':'var(--sp-6) var(--sp-5)'}}>

            {/* Header */}
            <div style={{marginBottom:'var(--sp-5)'}}>
              <p className="eyebrow" style={{marginBottom:4,color:'var(--c-electric-ink)'}}>Season 2026</p>
              <h1 style={{fontFamily:'var(--font-display)',fontWeight:900,fontSize:isMobile?'var(--fs-30)':'var(--fs-38)',letterSpacing:'var(--tr-tight)',color:'var(--c-fg-1)',marginBottom:'var(--sp-2)'}}>
                Tournament Calendar
              </h1>
              <p style={{color:'var(--c-fg-3)',fontSize:'var(--fs-14)',lineHeight:'var(--lh-normal)'}}>
                Official Championship Series events, pre-releases, and San Diego local tournaments.
                Local event data via <a href="https://tcsrockets.com" target="_blank" rel="noreferrer" style={{color:'var(--c-water-ink)'}}>TCS Rockets</a> · 
                Find more at <a href="https://play.pokemon.com/en-us/local-tournaments/tcg/" target="_blank" rel="noreferrer" style={{color:'var(--c-water-ink)'}}>play.pokemon.com</a>
              </p>
            </div>

            {/* Filters */}
            <div style={{display:'flex',gap:'var(--sp-2)',flexWrap:'wrap',marginBottom:'var(--sp-4)',alignItems:'center'}}>
              {EVENT_FILTERS.map(function(f){
                var active = evtFilter===f;
                var def = EVENT_TYPES[f]||{label:'All',color:'var(--c-electric)',icon:'📅'};
                var label = f==='all'?'All events':def.label;
                var color = f==='all'?'var(--c-electric)':def.color;
                return(
                  <button key={f} onClick={function(){setEvtFilter(f);}}
                    style={{padding:'5px 12px',borderRadius:'var(--r-full)',fontSize:'var(--fs-12)',fontWeight:700,cursor:'pointer',border:'1px solid '+(active?color:'var(--c-line)'),background:active?'color-mix(in srgb, '+color+' 15%, transparent)':'var(--c-mist)',color:active?color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',transition:'all var(--dur-1) var(--ease-smooth)'}}>
                    {f!=='all'&&def.icon+' '}{label}
                  </button>
                );
              })}
              <button onClick={function(){setShowPast(function(p){return !p;});}}
                style={{marginLeft:'auto',padding:'5px 12px',borderRadius:'var(--r-full)',fontSize:'var(--fs-12)',fontWeight:600,cursor:'pointer',border:'1px solid var(--c-line)',background:showPast?'var(--c-mist-2)':'transparent',color:'var(--c-fg-3)',fontFamily:'var(--font-ui)'}}>
                {showPast?'Hide past':'Show past'}
              </button>
            </div>

            {/* Event count */}
            <p style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginBottom:'var(--sp-4)'}}>{filteredEvts.length} events</p>

            {/* Event list */}
            <div style={{display:'flex',flexDirection:'column',gap:'var(--sp-3)'}}>
              {filteredEvts.map(function(evt){
                var def = EVENT_TYPES[evt.type]||{label:evt.type,color:'var(--c-water)',icon:'📅'};
                var until = daysUntil(evt.date);
                var isPast = evt.past || evt.date < today;
                return(
                  <div key={evt.id} style={{
                    background:'var(--c-bg-elevated)',
                    border:'1px solid var(--c-line)',
                    borderRadius:'var(--r-xl)',
                    overflow:'hidden',
                    opacity: isPast ? 0.6 : 1,
                    transition:'border-color var(--dur-1) var(--ease-smooth)',
                  }}>
                    {/* Color band top */}
                    <div style={{height:4,background:def.color}}/>
                    <div style={{padding:'var(--sp-4) var(--sp-5)'}}>
                      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'var(--sp-3)',flexWrap:'wrap',marginBottom:'var(--sp-3)'}}>
                        <div style={{flex:1,minWidth:0}}>
                          {/* Type + distance badges */}
                          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:'var(--sp-2)'}}>
                            <span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-11)',fontWeight:700,background:'color-mix(in srgb, '+def.color+' 15%, transparent)',color:def.color,border:'1px solid color-mix(in srgb, '+def.color+' 35%, transparent)'}}>
                              {def.icon} {def.label}
                            </span>
                            {evt.sdDistance&&(
                              <span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-11)',fontWeight:600,background:'var(--c-water-bg)',color:'var(--c-water-ink)',border:'1px solid var(--c-water)'}}>
                                📍 {evt.sdDistance}
                              </span>
                            )}
                            {evt.recurring&&(
                              <span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-11)',fontWeight:600,background:'var(--c-grass-bg)',color:'var(--c-grass-ink)',border:'1px solid var(--c-grass)'}}>
                                🔁 {evt.recurring}
                              </span>
                            )}
                            {isPast&&<span style={{padding:'2px 8px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-11)',fontWeight:600,background:'var(--c-mist-2)',color:'var(--c-fg-3)'}}>Past</span>}
                          </div>
                          {/* Event name */}
                          <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:isMobile?'var(--fs-16)':'var(--fs-20)',color:'var(--c-fg-1)',lineHeight:'var(--lh-snug)',marginBottom:4}}>
                            {evt.name}
                          </div>
                          {/* Location */}
                          <div style={{fontSize:'var(--fs-13)',color:'var(--c-fg-2)',marginBottom:4}}>
                            📍 {evt.venue&&evt.venue!==evt.location?evt.venue+' · ':''}{evt.location}
                          </div>
                          {/* Description */}
                          <div style={{fontSize:'var(--fs-13)',color:'var(--c-fg-3)',lineHeight:'var(--lh-normal)'}}>{evt.desc}</div>
                        </div>
                        {/* Date block */}
                        <div style={{textAlign:'right',flexShrink:0}}>
                          <div style={{fontFamily:'var(--font-mono)',fontWeight:700,fontSize:'var(--fs-13)',color:'var(--c-fg-1)'}}>{formatDate(evt.date,evt.endDate)}</div>
                          {until&&!isPast&&(
                            <div style={{marginTop:4,padding:'3px 10px',borderRadius:'var(--r-full)',background:'color-mix(in srgb, '+def.color+' 15%, transparent)',color:def.color,fontSize:'var(--fs-11)',fontWeight:700,whiteSpace:'nowrap'}}>
                              {until}
                            </div>
                          )}
                          {evt.url&&(
                            <a href={evt.url} target="_blank" rel="noreferrer"
                              style={{display:'inline-block',marginTop:8,padding:'6px 14px',borderRadius:'var(--r-md)',fontSize:'var(--fs-12)',fontWeight:600,border:'1px solid var(--c-line)',background:'var(--c-mist)',color:'var(--c-fg-2)',textDecoration:'none'}}>
                              Info →
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredEvts.length===0&&(
                <MascotEmpty variant="c" title="No events found" body="Try a different filter or show past events."/>
              )}
            </div>

            {/* ── SD STORE DIRECTORY ── */}
            <div style={{marginTop:'var(--sp-7)',paddingTop:'var(--sp-6)',borderTop:'1px solid var(--c-line)'}}>
              <p className="eyebrow" style={{marginBottom:4,color:'var(--c-water-ink)'}}>San Diego area</p>
              <h3 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-24)',color:'var(--c-fg-1)',marginBottom:'var(--sp-2)',letterSpacing:'var(--tr-tight)'}}>Local stores</h3>
              <p style={{fontSize:'var(--fs-13)',color:'var(--c-fg-3)',marginBottom:'var(--sp-5)'}}>Check each store's social media or website for current event schedules — leagues and weekly tournaments change seasonally.</p>
              <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(auto-fill,minmax(280px,1fr))',gap:'var(--sp-3)'}}>
                {[
                  {
                    name:'TCS Rockets',
                    address:'5155 Waring Rd, San Diego 92120',
                    area:'Normal Heights',
                    distance:'~10 mi from Chula Vista',
                    events:'Weekly Tue 6–8:30 PM · Sat 12–2 PM · $5 entry',
                    type:'league',
                    url:'https://tcsrockets.com/events-calendar/category/pokemon/',
                    ig:'tcsrockets',
                    notes:'Official Play! Pokémon League. Hosts League Challenges and pre-releases.',
                  },
                  {
                    name:'Game Empire',
                    address:'5037 Shawline St, San Diego 92111',
                    area:'Convoy / Kearny Mesa',
                    distance:'~12 mi from Chula Vista',
                    events:'Sat 6:30–9 PM (TCG League)',
                    type:'league',
                    url:'https://www.gameempire.com/',
                    ig:'gameempiresandiego',
                    notes:'Established San Diego institution. Pokémon & VGC league. Board games, RPGs, MTG.',
                  },
                  {
                    name:'Brute Force Games',
                    address:'3755 Murphy Canyon Rd, San Diego 92123',
                    area:'Murphy Canyon',
                    distance:'~11 mi from Chula Vista',
                    events:'Weekly events — check website for Pokémon schedule',
                    type:'store',
                    url:'https://www.bruteforcemtg.com/',
                    ig:'bruteforcegames',
                    notes:'Premier local game store. Large open play area. Carries Pokémon, MTG, board games. Weekly organized play.',
                  },
                  {
                    name:'Tabletop Tavern',
                    address:'Chula Vista, CA',
                    area:'Chula Vista',
                    distance:'Local',
                    events:'Weekly Pokémon TCG events · hosts tournaments & pre-releases',
                    type:'league',
                    url:'https://www.tabletoptavern.co/',
                    ig:'thetcgtavern',
                    notes:"Your closest local LGS. Has co-hosted major SD area Pokemon tournaments including SD's Finest at Del Mar Fairgrounds with Slim Rips.",
                  },
                  {
                    name:"Tubby's TCG",
                    address:'9019 Park Plaza Dr Ste AA, La Mesa 91942',
                    area:'La Mesa',
                    distance:'~8 mi from Chula Vista',
                    events:'Weekly Fri 6–8 PM (TCG League)',
                    type:'league',
                    url:'https://tubbystcg.tcgplayerpro.com/',
                    ig:'tubbystcg',
                    notes:'Family-owned shop specializing in Pokémon & Yu-Gi-Oh. Vintage + modern cards. Weekly league on Fridays.',
                  },
                  {
                    name:'MCollectibles',
                    address:'3702 Del Sol Blvd Ste D, San Diego 92154',
                    area:'Otay Ranch / South SD',
                    distance:'~5 mi from Chula Vista',
                    events:'Pokemon leagues & gaming events — check socials',
                    type:'league',
                    url:'https://www.instagram.com/mcollectiblesshop/',
                    ig:'mcollectiblesshop',
                    notes:"San Diego's Pokémon HQ — specializes in Pokémon, One Piece TCG. Hosts league events and card shows.",
                  },
                  {
                    name:"Papa Joey's Collectibles",
                    address:'Online & pop-up events',
                    area:'San Diego (online + events)',
                    distance:'Varies',
                    events:'Tournaments & live breaks — check website',
                    type:'store',
                    url:'https://papajoeys.com/collections/events',
                    ig:'papajoeys_collectibles',
                    notes:'TCG, Sports cards, Funkos. Hosts tournaments & live pack breaks on TikTok/Whatnot. Military veteran discount. Physical location closed — check socials for pop-up events.',
                  },
                  {
                    name:'Slim Rips',
                    address:'San Diego area (online + events)',
                    area:'San Diego',
                    distance:'Varies',
                    events:'Card shows & tournaments — follow on Instagram',
                    type:'store',
                    url:'https://www.instagram.com/slims_rips/',
                    ig:'slims_rips',
                    notes:"Community staple run by Shane & Tyler. Co-hosted SD's Finest Pokémon Tournament at Del Mar Fairgrounds with Tabletop Tavern. Follow on IG for event announcements.",
                  },
                ].map(function(store){
                  var typeColor3 = store.type==='league'?'var(--c-water)':'var(--c-dragon)';
                  var typeBg3   = store.type==='league'?'var(--c-water-bg)':'var(--c-dragon-bg)';
                  var typeLabel = store.type==='league'?'Official League':'Store';
                  return(
                    <div key={store.name} style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-xl)',overflow:'hidden'}}>
                      <div style={{height:4,background:typeColor3}}/>
                      <div style={{padding:'var(--sp-4)'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'var(--sp-2)'}}>
                          <div>
                            <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-16)',color:'var(--c-fg-1)',lineHeight:'var(--lh-snug)'}}>{store.name}</div>
                            <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',marginTop:2}}>{store.area}</div>
                          </div>
                          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
                            <span style={{padding:'2px 7px',borderRadius:'var(--r-sm)',fontSize:'var(--fs-10)',fontWeight:700,background:typeBg3,color:typeColor3,border:'1px solid '+typeColor3,whiteSpace:'nowrap'}}>{typeLabel}</span>
                            <span style={{fontSize:'var(--fs-10)',fontWeight:600,color:'var(--c-water-ink)',background:'var(--c-water-bg)',padding:'1px 6px',borderRadius:'var(--r-sm)'}}>📍 {store.distance}</span>
                          </div>
                        </div>
                        <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-2)',marginBottom:'var(--sp-2)',display:'flex',alignItems:'center',gap:4}}>
                          <span style={{color:'var(--c-fg-3)'}}>📌</span> {store.address}
                        </div>
                        <div style={{fontSize:'var(--fs-12)',color:'var(--c-grass-ink)',background:'var(--c-grass-bg)',borderRadius:'var(--r-sm)',padding:'4px 8px',marginBottom:'var(--sp-3)',border:'1px solid var(--c-grass)'}}>
                          🗓 {store.events}
                        </div>
                        <div style={{fontSize:'var(--fs-12)',color:'var(--c-fg-3)',lineHeight:'var(--lh-normal)',marginBottom:'var(--sp-3)'}}>{store.notes}</div>
                        <div style={{display:'flex',gap:6}}>
                          <a href={store.url} target="_blank" rel="noreferrer"
                            style={{flex:1,display:'block',textAlign:'center',padding:'6px 0',borderRadius:'var(--r-md)',fontSize:'var(--fs-12)',fontWeight:600,border:'1px solid var(--c-line)',background:'var(--c-mist)',color:'var(--c-fg-2)',textDecoration:'none'}}>
                            Website →
                          </a>
                          <a href={'https://instagram.com/'+store.ig} target="_blank" rel="noreferrer"
                            style={{display:'block',textAlign:'center',padding:'6px 10px',borderRadius:'var(--r-md)',fontSize:'var(--fs-12)',fontWeight:600,border:'1px solid var(--c-psychic)',background:'var(--c-psychic-bg)',color:'var(--c-psychic-ink)',textDecoration:'none'}}>
                            IG
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer note */}
            <div style={{marginTop:'var(--sp-6)',padding:'var(--sp-4)',background:'var(--c-mist)',borderRadius:'var(--r-lg)',fontSize:'var(--fs-13)',color:'var(--c-fg-3)',lineHeight:'var(--lh-loose)'}}>
              <strong style={{color:'var(--c-fg-2)'}}>Find more events near you:</strong> Use the official 
              {' '}<a href="https://play.pokemon.com/en-us/local-tournaments/tcg/" target="_blank" rel="noreferrer" style={{color:'var(--c-water-ink)'}}>Play! Pokémon event locator</a>{' '}
              to find League Challenges, League Cups, and pre-releases at every store.
            </div>
          </div>
        </div>
        <Toast data={toast}/>
      </div>
    );
  }

  // ── SET BROWSER — 3-column pokedex layout ────────────────────────────────
  var previewSet = hoveredSet || activeSet;

  return(
    <div style={{background:'var(--c-paper)',minHeight:'100vh',fontFamily:'var(--font-ui)',position:'relative'}}>
      <DotGrid/>
      <div style={{position:'relative',zIndex:1}}>
        {nav}

        {/* 3-column layout: filter rail / set grid / detail drawer */}
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'240px 1fr 340px',gap:'var(--sp-5)',maxWidth:1440,margin:'var(--sp-5) auto var(--sp-8)',padding:isMobile?'0 var(--sp-3)':'0 var(--sp-4)',alignItems:'start'}}>

          {/* ── Filter Rail ── */}
          {!isMobile&&<aside style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',padding:'var(--sp-4)',position:'sticky',top:78}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:'var(--sp-3)'}}>
              <span className="eyebrow">Filter by era</span>
              {era!=='all'&&<button onClick={function(){setEra('all');setSetSearch('');}} style={{background:'none',border:'none',color:'var(--c-fg-2)',fontSize:'var(--fs-12)',cursor:'pointer',fontFamily:'var(--font-ui)'}}>Clear</button>}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:3,marginBottom:'var(--sp-4)'}}>
              {ERAS.map(function(e){
                var active=era===e.id;
                return(
                  <button key={e.id} onClick={function(){setEra(e.id);}}
                    style={{display:'flex',alignItems:'center',gap:10,padding:'7px 10px',borderRadius:'var(--r-full)',border:'1px solid '+(active?e.color:'transparent'),background:active?'color-mix(in srgb, '+e.color+' 12%, transparent)':'transparent',color:active?e.color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer',textAlign:'left',width:'100%',transition:'all var(--dur-1) var(--ease-smooth)'}}>
                    <span style={{width:8,height:8,borderRadius:'50%',background:e.color,flexShrink:0}}/>
                    {e.label}
                  </button>
                );
              })}
            </div>

            <div style={{height:1,background:'var(--c-line)',margin:'0 calc(-1 * var(--sp-4))',marginBottom:'var(--sp-4)'}}/>

            {/* Set search */}
            <p className="eyebrow" style={{marginBottom:'var(--sp-2)'}}>Search sets</p>
            <div style={{position:'relative'}}>
              <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--c-fg-3)',fontSize:13,pointerEvents:'none'}}>⌕</span>
              <input value={setSearch} onChange={function(e){setSetSearch(e.target.value);}}
                placeholder="Filter…"
                style={{width:'100%',background:'var(--c-mist)',border:'1px solid var(--c-line)',borderRadius:'var(--r-sm)',padding:'7px 10px 7px 30px',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',color:'var(--c-fg-1)',outline:'none'}}/>
            </div>

            <div style={{height:1,background:'var(--c-line)',margin:'var(--sp-4) calc(-1 * var(--sp-4))',marginBottom:'var(--sp-3)'}}/>
            <p className="eyebrow" style={{marginBottom:'var(--sp-2)'}}>Search by photo</p>
            <CardPhotoSearch
              onSearchResults={function(data){setGlobalQ(data.cardName);searchCards(data.cardName);setScreen('search');}}
              searchCards={function(q){return searchCards(q);}}
            />

            <div style={{height:1,background:'var(--c-line)',margin:'var(--sp-3) calc(-1 * var(--sp-4))',marginBottom:'var(--sp-3)'}}/>
            <div style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-11)',color:'var(--c-fg-3)'}}>{visibleSets.length} sets · 26 years</div>

            <div style={{height:1,background:'var(--c-line)',margin:'var(--sp-3) calc(-1 * var(--sp-4))',marginBottom:'var(--sp-3)'}}/>

            {/* Format filter */}
            <p className="eyebrow" style={{marginBottom:'var(--sp-2)',color:'var(--c-fg-3)'}}>Format</p>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              {Object.entries(FORMATS).map(function([id,def]){
                var active=format===id;
                return(
                  <button key={id} onClick={function(){setFormat(id);}}
                    title={def.desc}
                    style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 10px',borderRadius:'var(--r-md)',border:'1px solid '+(active?def.color:'transparent'),background:active?'color-mix(in srgb, '+def.color+' 12%, transparent)':'transparent',color:active?def.color:'var(--c-fg-2)',fontFamily:'var(--font-ui)',fontSize:'var(--fs-13)',fontWeight:600,cursor:'pointer',transition:'all var(--dur-1) var(--ease-smooth)',textAlign:'left',width:'100%'}}>
                    <span>{def.label}</span>
                    {active&&<span style={{fontSize:'var(--fs-10)',fontWeight:800,color:def.color}}>✓</span>}
                  </button>
                );
              })}
            </div>
            <div style={{fontSize:'var(--fs-11)',color:'var(--c-fg-3)',marginTop:6,lineHeight:'var(--lh-normal)'}}>{FORMATS[format].desc}</div>
          </aside>}

          {/* ── Main set grid ── */}
          <main style={{minWidth:0}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:'var(--sp-5)'}}>
              <div>
                <p className="eyebrow" style={{marginBottom:4}}>Card catalog</p>
                <h1 style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-38)',letterSpacing:'var(--tr-tight)',color:'var(--c-fg-1)',lineHeight:'var(--lh-tight)'}}>
                  {era==='all'?'All sets':(ERAS.find(function(e){return e.id===era;})||{}).label||'Sets'}
                </h1>
                {format!=='unlimited'&&(
                  <div style={{display:'inline-flex',alignItems:'center',gap:6,marginTop:6,padding:'4px 12px',borderRadius:'var(--r-full)',background:'color-mix(in srgb, '+FORMATS[format].color+' 12%, transparent)',border:'1px solid color-mix(in srgb, '+FORMATS[format].color+' 40%, transparent)',fontSize:'var(--fs-12)',fontWeight:700,color:FORMATS[format].color}}>
                    {format==='standard'?'⚡':'🌐'} {FORMATS[format].label} sets only
                    <button onClick={function(){setFormat('unlimited');}} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',fontSize:14,padding:0,lineHeight:1,opacity:0.7}}>✕</button>
                  </div>
                )}
              </div>
              <div style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-13)',color:'var(--c-fg-3)',paddingBottom:6}}>
                <span style={{fontVariantNumeric:'tabular-nums'}}>{visibleSets.length}</span> of <span style={{fontVariantNumeric:'tabular-nums'}}>{SETS.length}</span>
              </div>
            </div>

            {visibleSets.length===0&&<MascotEmpty variant="c" title="No sets found" body={'Try a different filter or search term.'}/>}

            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))',gap:'var(--sp-3)'}}>
              {visibleSets.map(function(set){
                var ec=(ERAS.find(function(e){return e.id===set.era;})||{}).color||'var(--c-electric)';
                var isHovered=hoveredSet&&hoveredSet.id===set.id;
                return(
                  <button key={set.id}
                    onClick={function(){openSet(set);}}
                    onMouseEnter={function(){setHoveredSet(set);}}
                    onMouseLeave={function(){setHoveredSet(null);}}
                    style={{background:'var(--c-bg-elevated)',border:'1px solid '+(isHovered?ec:'var(--c-line)'),borderRadius:'var(--r-lg)',padding:0,cursor:'pointer',display:'flex',flexDirection:'column',textAlign:'left',transition:'all var(--dur-1) var(--ease-pop)',fontFamily:'var(--font-ui)',overflow:'hidden',transform:isHovered?'translateY(-2px)':'none'}}>
                    {/* Type-color band on top */}
                    <div style={{height:5,background:ec,width:'100%',flexShrink:0}}/>
                    <div style={{padding:'var(--sp-3) var(--sp-4)'}}>
                      <div style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-11)',color:'var(--c-fg-3)',marginBottom:3}}>{set.year} · {set.total} cards</div>
                      <div style={{fontWeight:700,fontSize:'var(--fs-14)',color:'var(--c-fg-1)',fontFamily:'var(--font-display)',lineHeight:'var(--lh-snug)'}}>{set.name}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </main>

          {/* ── Detail Drawer ── */}
          {!isMobile&&<aside style={{background:'var(--c-bg-elevated)',border:'1px solid var(--c-line)',borderRadius:'var(--r-lg)',overflow:'hidden',position:'sticky',top:78}}>
            {previewSet?(function(){
              var ec=(ERAS.find(function(e){return e.id===previewSet.era;})||{}).color||'var(--c-electric)';
              var eraLabel=(ERAS.find(function(e){return e.id===previewSet.era;})||{}).label||'';
              return(
                <>
                  {/* Hero band with card art background */}
                  <div style={{background:ec,padding:'var(--sp-7) var(--sp-5) var(--sp-5)',position:'relative',minHeight:160,display:'flex',flexDirection:'column',justifyContent:'flex-end',overflow:'hidden'}}>
                    {/* Card art — blurred, low opacity, right-aligned */}
                    {previewCard&&previewCard.imageUrl&&(
                      <img
                        src={previewCard.imageUrl}
                        alt=""
                        style={{
                          position:'absolute',right:-20,top:'50%',transform:'translateY(-50%) rotate(8deg)',
                          height:180,width:'auto',
                          opacity:0.28,
                          filter:'blur(0.5px)',
                          borderRadius:12,
                          pointerEvents:'none',
                          transition:'opacity var(--dur-3) var(--ease-smooth)',
                        }}
                      />
                    )}
                    {/* Dot grid overlay */}
                    <div style={{position:'absolute',inset:0,backgroundImage:'url(/assets/bg-dotgrid.svg)',backgroundRepeat:'repeat',opacity:0.08}}/>
                    {/* Gradient fade so text stays readable */}
                    <div style={{position:'absolute',inset:0,background:'linear-gradient(to right, '+ec+' 45%, transparent 100%)'}}/>
                    <div style={{position:'relative'}}>
                      <div style={{fontFamily:'var(--font-mono)',fontSize:'var(--fs-11)',color:'rgba(255,255,255,0.75)',marginBottom:4,letterSpacing:'var(--tr-mega)',textTransform:'uppercase'}}>{eraLabel} · {previewSet.year}</div>
                      <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:'var(--fs-24)',color:'#fff',lineHeight:'var(--lh-snug)',letterSpacing:'var(--tr-tight)'}}>{previewSet.name}</div>
                    </div>
                  </div>
                  <div style={{padding:'var(--sp-4) var(--sp-4) var(--sp-5)'}}>
                    <div style={{display:'flex',gap:'var(--sp-2)',marginBottom:'var(--sp-4)',flexWrap:'wrap'}}>
                      <div style={{flex:1,background:'var(--c-mist)',borderRadius:'var(--r-md)',padding:'var(--sp-3)',textAlign:'center'}}>
                        <div style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-24)',color:'var(--c-fg-1)',fontVariantNumeric:'tabular-nums'}}>{previewSet.total}</div>
                        <div style={{fontSize:'var(--fs-11)',fontWeight:600,color:'var(--c-fg-3)',letterSpacing:'var(--tr-mega)',textTransform:'uppercase',marginTop:2}}>Cards</div>
                      </div>
                      <div style={{flex:1,background:'var(--c-mist)',borderRadius:'var(--r-md)',padding:'var(--sp-3)',textAlign:'center'}}>
                        <div style={{fontFamily:'var(--font-mono)',fontWeight:900,fontSize:'var(--fs-24)',color:'var(--c-fg-1)'}}>{previewSet.year}</div>
                        <div style={{fontSize:'var(--fs-11)',fontWeight:600,color:'var(--c-fg-3)',letterSpacing:'var(--tr-mega)',textTransform:'uppercase',marginTop:2}}>Year</div>
                      </div>
                    </div>
                    <button onClick={function(){openSet(previewSet);}}
                      style={{width:'100%',padding:'11px 0',borderRadius:'var(--r-md)',fontSize:'var(--fs-14)',fontWeight:700,cursor:'pointer',border:'none',background:ec,color:'#fff',fontFamily:'var(--font-ui)',transition:'filter var(--dur-1)'}}
                      onMouseEnter={function(e){e.currentTarget.style.filter='brightness(0.88)';}}
                      onMouseLeave={function(e){e.currentTarget.style.filter='none';}}>
                      Browse {previewSet.total} cards →
                    </button>
                  </div>
                </>
              );
            })():(
              <MascotEmpty variant="a" title="Hover a set" body="Details appear here when you hover over a set."/>
            )}
          </aside>}
        </div>
      </div>
      <Toast data={toast}/>
      {GlobalAuthModal}
    </div>
  );
}
